/**
 * @fileoverview Arquivo responsável por definir as traduções em inglês da aplicação.
 */

const messages = {
	en: {
		translations: {
			selectLanguage: "Select Language",
			triageNotice: {
				message: "Work order {{number}} arrived for a customer you are attending. The triage \"{{name}}\" is waiting: hand it to the AI whenever you want.",
				open: "Open conversation",
			},
			billingStatusBanner: {
				overdue: "Payment pending: pay by {{date}} to keep sending messages.",
				overdueInternal: "Payment pending: pay by {{date}} to keep the Internal Chat.",
				overdueNoDeadline: "Payment pending: there's an unpaid overdue invoice.",
				suspendedAdmin: "Sending messages and the Internal Chat are suspended due to a pending payment.",
				suspendedMember: "Sending messages and the Internal Chat are suspended due to a pending payment. Talk to your company administrator.",
				suspendedInternalAdmin: "The Internal Chat is suspended due to a pending payment.",
				suspendedInternalMember: "The Internal Chat is suspended due to a pending payment. Talk to your company administrator.",
				pay: "Pay now",
				adequacyDue: "A plan condition has ended: choose what stays by {{date}}, or access will be restricted until you do.",
				choose: "Choose what stays",
				close: "Close until tomorrow",
			},
			messageUsageBanner: {
				commonNear: "{{percent}}% of this month's messages are used ({{used}} of {{limit}}). Renews on {{date}}.",
				commonFull: "This month's messages are used up. Agents and automations can send again on {{date}}, or sooner with a bigger plan.",
				aiNear: "The AI has used {{percent}}% of its messages this month ({{used}} of {{limit}}). Renews on {{date}}.",
				aiFull: "The AI's messages are used up. Until {{date}}, AI conversations go to your agents. A bigger AI pack frees them sooner.",
				close: "Close notice",
			},
			noResponseAlert: {
				messageOne:
					"Attention: 1 conversation waiting for a reply for more than 10 minutes.",
				messageMany:
					"Attention: {{count}} conversations waiting for a reply for more than 10 minutes.",
				action: "View Pending Conversations",
			},
			signup: {
				title: "Sign Up",
				toasts: {
					success: "User created successfully! Please login!!!",
					fail: "Error creating user. Please check the provided information.",
				},
				form: {
					name: "Company Name",
					email: "Email",
					phone: "Phone Number (with Area Code)",
					plan: "Plan",
					password: "Password",
				},
				formErrors: {
					name: {
						required: "Company name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					password: {
						short: "Password is too short",
						long: "Password is too long",
					},
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
				},
				buttons: {
					submit: "Register",
					login: "Already have an account? Login!",
				},
				plan: {
					attendant: "Attendant",
					whatsapp: "WhatsApp",
					queues: "Queues",
				},
			},
			login: {
				title: "Login",
				form: {
					email: "Email",
					password: "Password",
				},
				buttons: {
					submit: "Sign in",
					submitting: "Signing in...",
					register: "Sign up now!",
				},
				errors: {
					emailRequired: "Email is required",
					emailInvalid: "Invalid email",
					passwordRequired: "Password is required",
				},
				capsLockOn: "Caps Lock is on",
			},
			mfa: {
				setup: {
					title: "Set up two-factor authentication",
					scanQr: "Scan the QR code with Google Authenticator, Duo Mobile or a compatible app.",
					manualSecret: "Can't scan? Enter this code manually:",
					next: "Next",
					back: "Back",
					confirmOtp: "Enter the 6-digit code from the app:",
					confirm: "Confirm",
					backupTitle: "Backup codes",
					backupHint: "Store them safely. Each code is single-use and replaces the app if you lose your phone.",
					copyAll: "Copy all",
					download: "Download .txt",
					savedAck: "I have saved my codes securely",
					oneTimeWarning: "These codes will be shown only once.",
					finish: "Done",
					cancel: "Cancel",
					copied: "Codes copied",
				},
				login: {
					title: "Two-factor verification",
					otpLabel: "Authenticator app code",
					otpHint: "Enter the 6-digit code from your app.",
					backupLabel: "Backup code",
					backupHint: "Use one of the recovery codes you saved.",
					verify: "Verify",
					useBackup: "Use backup code",
					useOtp: "Use authenticator app",
					backToPassword: "Back",
				},
				policy: {
					title: "Team two-factor authentication",
					modeOff: "Disabled",
					modeOptional: "Optional",
					modeRequired: "Required",
					gracePeriod: "Setup deadline (days)",
					save: "Save",
					saved: "Policy updated",
				},
				profile: {
					menuItem: "Two-factor authentication",
					statusEnabled: "Active since {{date}}",
					statusDisabled: "Not configured",
					enable: "Enable",
					disable: "Disable",
					disableConfirm: "Confirm with your current code to disable MFA",
					adminReset: "Reset user MFA",
					adminResetConfirm: "Are you sure? The user will need to re-enroll on next login.",
					adminResetDone: "MFA has been reset",
					enabledDesc: "Your account is protected: every login requires a code from your authenticator app.",
					disabledDesc: "Enable two-factor authentication to strengthen your account security. You'll need an app like Google Authenticator or Duo Mobile.",
				},
				banner: {
					title: "Your company requires two-factor authentication.",
					subtitle: "Set it up within {{days}} day(s) to keep accessing the platform.",
					cta: "Set up now",
				},
				errors: {
					invalidOtp: "Invalid or expired code",
					ticketExpired: "Verification session expired. Please log in again.",
					backupUsed: "Backup code already used",
				},
			},
			resetPassword: {
				title: "Reset Password",
				toasts: {
					emailSent: "Email sent successfully!",
					emailNotFound: "Email not found!",
					passwordUpdated: "Password updated successfully!",
				},
				formErrors: {
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
					newPassword: {
						required: "New password is required",
						matches: "Your password must have at least 8 characters, including one uppercase letter, one lowercase letter, and one number.",
					},
					confirmPassword: {
						required: "Password confirmation is required",
						matches: "Passwords do not match",
					},
				},
				form: {
					email: "Email",
					verificationCode: "Verification Code",
					newPassword: "New Password",
					confirmPassword: "Confirm New Password",
				},
				buttons: {
					submitEmail: "Send Email",
					submitPassword: "Reset Password",
					back: "Don't have an account? Sign up!",
				},
			},
			dashboard: {
				scope: {
					label: "Numbers for",
					company: "This account",
					allMine: "All my departments",
					units: "My units",
				},
				toasts: {
					selectFilterError: "Set filter parameters",
					userChartError: "Error getting conversation information",
					dateChartError: "Error getting conversation information",
					messagesPerUserError: "Error loading messages per user",
				},
				filters: {
					initialDate: "Start Date",
					finalDate: "End Date",
					filterType: {
						title: "Filter Type",
						options: {
							perDate: "Filter by Date",
							perPeriod: "Filter by Period",
						},
						helper: "Select desired filter type",
					},
				},
				periodSelect: {
					title: "Period",
					options: {
						none: "None selected",
						last3: "Last 3 days",
						last7: "Last 7 days",
						last15: "Last 15 days",
						last30: "Last 30 days",
						last60: "Last 60 days",
						last90: "Last 90 days",
					},
					helper: "Select desired period",
				},
				counters: {
					inTalk: "In conversation",
					waiting: "Waiting",
					finished: "Finished",
					newContacts: "New contacts",
					averageTalkTime: "Avg. Talk Time",
					averageWaitTime: "Avg. Wait Time",
				},
				buttons: {
					filter: "Filter",
				},
				onlineTable: {
					ratingLabel: "1 - Unsatisfied, 2 - Satisfied, 3 - Very Satisfied",
					name: "Name",
					ratings: "Ratings",
					avgSupportTime: "Avg. Support Time",
					status: "Status (Current)",
				},
				charts: {
					user: {
						label: "Conversations Chart",
						title: "Total Conversations by Users",
						start: "Start",
						end: "End",
						filter: "Filter",
					},
					date: {
						label: "Conversations Chart",
						title: "Total",
						start: "Start",
						end: "End",
						filter: "Filter",
					},
				},
			},
			dashboardGeral: {
				title: "Group Dashboard",
				subtitle: "Consolidated view of group {{groupName}}",
				accountsCount: "{{count}} accounts",
				cards: {
					active: "Active Tickets",
					pending: "Pending",
					noResponse: "No Response +10min",
					noResponseRate: "No response rate",
					avgPerAttendant: "Average/Attendant",
					art: "Group ART",
				},
				table: {
					title: "Performance by Account",
					headers: {
						account: "Account",
						active: "Active",
						pending: "Pending",
						noResponse: "No Resp +10min",
						art: "ART",
						attendants: "Attendants",
					},
				},
				states: {
					loading: "Loading group dashboard...",
					empty: "No accounts found in this group",
					error: "Error loading dashboard. Try again?",
				},
			},
			plans: {
				toasts: {
					errorList: "Could not load records list",
					errorOperation: "Could not complete operation",
					error: "Could not complete operation. Check if a plan with the same name already exists or if fields were filled correctly",
					success: "Operation completed successfully!",
				},
				confirm: {
					title: "Delete Record",
					message: "Do you really want to delete this record?",
				},
				buttons: {
					newCategory: "New Category",
					newPlan: "New Plan",
				},
				modal: {
					editPlan: "Edit Plan",
					newPlan: "New Plan",
					newCategory: "New Category",
					categoryName: "Category Name",
					categoryPlaceholder: "E.g.: Premium, Enterprise, Starter",
					basicInfo: "Basic Information",
					emptyCategory: "This category is empty. Click \"New Plan\" to add.",
				},
				labels: {
					plans: "Plans",
					plan: "plan",
					plansPlural: "plans",
				},
				form: {
					name: "Name",
					users: "Users",
					connections: "Connections",
					queues: "Queues",
					value: "Value",
					internalChat: "Internal Chat",
					externalApi: "External API",
					kanban: "CRM",
					integrations: "Integrations",
					campaigns: "Campaigns",
					schedules: "Schedules",
					leads: "CRM",
					enabled: "Enabled",
					disabled: "Disabled",
					clear: "Cancel",
					delete: "Delete",
					save: "Save",
					yes: "Yes",
					no: "No",
					money: "$",
				},
			},
			boardShare: {
				open: "Share board",
				title: "Share board",
				description: "Let other people view this board through a secure link, without having to sign in to Sttelar Talk.",
				close: "Close",
				back: "Back",
				cancel: "Cancel",
				newLink: "New link",
				listTitle: "Links for this board ({{count}})",
				loadError: "Couldn't load the links.",
				retry: "Try again",
				empty: "No links yet.",
				form: {
					name: "Link name",
					namePlaceholder: "E.g.: Midea client",
					nameHelp: "So you know who you shared it with.",
					nameRequired: "Give the link a name.",
					validity: "Validity",
					permanent: "Permanent",
					permanentHelp: "Works until you revoke it.",
					temporary: "Temporary",
					presets: {
						"1h": "1 hour",
						"24h": "24 hours",
						"7d": "7 days",
						"30d": "30 days",
						custom: "Custom",
					},
					customDate: "Date and time",
					expiresOn: "Stops working on {{date}}.",
					customRequired: "Choose the date and time.",
					customInvalid: "Choose a date between 5 minutes and 1 year from now.",
					protection: "Protection",
					password: "Require a password",
					passwordHint: "Recommended when the board has customer data.",
					passwordLabel: "Password",
					passwordRule: "At least 8 characters.",
					passwordTooShort: "The password must have at least 8 characters.",
					passwordTooLong: "The password is too long.",
					generate: "Generate a strong password",
					show: "Show password",
					hide: "Hide password",
					copyPassword: "Copy password",
					visibility: "What the link shows",
					visibilityBase: "Always: card name, stage, priority, due date, assignee and subtask progress.",
					contactInfo: "Contact details",
					contactInfoHelp: "Phone, email, CPF (partial) and address.",
					cardDetails: "Card details",
					cardDetailsHelp: "Custom fields, service order, description and subtasks.",
					never: "Comments, attachments and history never appear.",
					create: "Create link",
					creating: "Creating...",
				},
				created: {
					newTitle: "Link created",
					renewedTitle: "New link generated",
					newText: "Copy the link and send it to whoever will view the board.",
					copyOnlyTitle: "Share link",
					copyOnlyText: "Couldn't copy automatically. Select the address below and copy it.",
					renewedText: "The previous link stopped working. Send this one instead.",
					link: "Link",
					copyLink: "Copy",
					copiedShort: "Copied",
					copied: "Link copied",
					copyFailed: "Couldn't copy. Select the text and copy it.",
					passwordCopied: "Password copied",
					passwordNote: "Send the password through another channel. It won't be shown again.",
					done: "Done",
					seeLinks: "See links",
				},
				row: {
					active: "Active",
					expired: "Expired",
					revoked: "Revoked",
					permanent: "Permanent",
					until: "Until {{date}}",
					expiredOn: "Expired on {{date}}",
					revokedOn: "Revoked on {{date}}",
					revokedBy: "Revoked on {{date}} by {{name}}",
					withPassword: "With password",
					withoutPassword: "No password",
					showsContact: "Shows contact",
					showsDetails: "Shows details",
					createdBy: "Created on {{date}} by {{name}}",
					createdAt: "Created on {{date}}",
					lastAccess: "Last opened on {{date}}",
					neverAccessed: "Not opened yet",
					copyLink: "Copy link",
					more: "More actions",
					setPassword: "Set password",
					changePassword: "Change password",
					changeValidity: "Change validity",
					renew: "Generate new link",
					revoke: "Revoke",
				},
				password: {
					title: "Link password",
					text: "Anyone who already opened the board will need to type the new password.",
					save: "Save password",
					remove: "Remove password",
					saved: "Password saved",
					removed: "Password removed",
				},
				validity: {
					title: "Link validity",
					current: "It is currently valid until {{date}}.",
					currentPermanent: "It is currently permanent.",
					text: "The new validity counts from now.",
					save: "Save validity",
					saved: "Validity saved",
				},
				renew: {
					title: "Generate a new link?",
					text: "The current link stops working right away. Whoever has it will need the new one.",
					confirm: "Generate new link",
				},
				revoke: {
					title: "Revoke link?",
					text: "Whoever has the link loses access right away, and this can't be undone.",
					confirm: "Revoke",
					done: "Link revoked",
				},
				removePassword: {
					title: "Remove the password?",
					text: "Anyone with the link will be able to view the board without a password.",
					confirm: "Remove password",
				},
			},
			sharedBoard: {
				pageTitle: "Shared board",
				eyebrow: "Shared board",
				readOnly: "View mode — Read only",
				readOnlyShort: "Read only",
				updatedAt: "Updated at {{time}}",
				validUntil: "Link valid until {{date}}",
				refresh: "Refresh",
				themeLight: "Light theme",
				themeDark: "Dark theme",
				search: "Search cards",
				allAssignees: "All assignees",
				unassigned: "Unassigned",
				noColumns: "This board has no stages yet.",
				emptyColumn: "No cards in this stage",
				truncated: "Showing the first {{count}} cards.",
				offline: "No connection right now. The data may be outdated.",
				loading: "Loading the board",
				password: {
					title: "Protected board",
					text: "Type the password you received to view this board.",
					label: "Password",
					submit: "Open board",
					wrong: "Wrong password.",
					tooMany: "Too many attempts. Wait a few minutes and try again.",
					failed: "Couldn't sign in right now. Try again.",
					cookies: "Couldn't keep your access in this browser. Check that cookies are allowed and try again.",
					sessionEnded: "The password changed or your access ended. Type the password again.",
					show: "Show password",
					hide: "Hide password",
				},
				status: {
					invalid: {
						title: "Invalid link",
						text: "This link doesn't exist or was copied incompletely. Check with whoever shared it.",
					},
					expired: {
						title: "Link expired",
						text: "This link is no longer available. Ask whoever shared it for a new one.",
					},
					revoked: {
						title: "Access ended",
						text: "Whoever shared this link ended it. Ask for a new one if you need it.",
					},
					unavailable: {
						title: "Board unavailable",
						text: "This board isn't available right now.",
					},
					rateLimited: {
						title: "Too many requests",
						text: "Wait a minute and try again.",
					},
					error: {
						title: "Couldn't open the board",
						text: "Check your connection and try again.",
					},
					retry: "Try again",
				},
				card: {
					priority: "Priority",
					dueAt: "Due date",
					assignee: "Assignee",
					createdAt: "Created on",
					products: "Product of interest",
					checklist: "Subtasks",
					checklistProgress: "{{done}} of {{total}} done",
					phone: "Phone",
					email: "Email",
					cpf: "CPF",
					address: "Address",
					serviceOrder: "Service order",
					field: "Field",
					description: "Description",
					nobody: "Nobody",
					loadError: "Couldn't open this card.",
					close: "Close",
					priorities: {
						low: "Low",
						normal: "Medium",
						high: "High",
						urgent: "Highest",
					},
				},
			},
			externalLeads: {
				title: "CRM",
				refresh: "Refresh",
				stats: {
					total: "Total cards",
					unviewed: "New",
					pending: "Pending",
					completed: "Completed",
				},
				filters: {
					searchPlaceholder: "Search by name, phone, ID or service order...",
					status: "Status",
					all: "All",
					sortBy: "Sort by",
					newestFirst: "Newest first",
					oldestFirst: "Oldest first",
				},
				card: {
					new: "New",
					notInformed: "Not informed",
					attachments: "attachment(s)",
					serviceOrderPlaceholder: "Service Order",
					saveServiceOrder: "Save S.O.",
					noChanges: "No changes",
					viewDetails: "View details",
					delete: "Delete",
					startConversation: "Start WhatsApp conversation",
				},
				modal: {
					clientInfo: "Client Information",
					name: "Name",
					phone: "Phone",
					email: "E-mail",
					cpf: "ID Number",
					address: "Address",
					serviceOrder: "Service Order",
					attachments: "Attachments",
					customFields: "Custom Fields",
					changeStatus: "Change Status",
					receivedAt: "Received at:",
					viewedAt: "Viewed at:",
					close: "Close",
				},
				confirmDelete: {
					title: "Delete card",
					message: "Are you sure you want to delete this card? This action cannot be undone.",
				},
				empty: {
					title: "No cards found",
					description: "Cards created on the board or received through integrations will appear here",
				},
				loading: "Loading...",
				toasts: {
					newLead: "New card:",
					deleted: "Card deleted successfully",
					statusUpdated: "Status updated!",
					serviceOrderSaved: "Service Order saved!",
				},
				defaultStatus: "Pending",
			},
			kanban: {
				title: "CRM Kanban",
				toasts: {
					removed: "Tag removed from ticket!",
					added: "Tag added successfully!",
					refreshed: "Data updated!",
					errorLoadingTags: "Error loading tags",
					errorLoadingTickets: "Error loading tickets",
					errorMoving: "Error moving card",
				},
				open: "Open",
				seeTicket: "View Ticket",
				tickets: "tickets",
				columns: "columns",
				refresh: "Refresh",
				searchPlaceholder: "Search by name or number...",
				allQueues: "All queues",
				loading: "Loading tickets...",
				noMessage: "No message",
				unknownContact: "Unknown contact",
			},
			companies: {
				title: "Register Company",
				buttons: {
					newGroup: "New Group",
					newCompany: "New Company",
				},
				modal: {
					editCompany: "Edit Company",
					newCompany: "New Company",
					newGroup: "New Group",
					groupName: "Group Name",
					groupPlaceholder: "E.g.: Client XYZ",
				},
				labels: {
					companies: "Companies",
					company: "company",
					companiesPlural: "companies",
				},
				tooltips: {
					addUser: "Add new user",
					editUser: "Edit existing user",
					editCompany: "Edit company",
					deleteCompany: "Delete company",
				},
				form: {
					name: "Company Name",
					plan: "Plan",
					token: "Token",
					submit: "Register",
					success: "Company created successfully!",
				},
			},
			agentManager: {
				button: "Manage Agents",
				title: "AI agents",
				subtitle: "The AI that answers in your flows. What you change in an agent applies to every flow that uses it.",
				close: "Close",
				search: "Search agents",
				assistant: "Create with the assistant",
				blank: "New agent",
				emptyTitle: "No agents yet",
				empty: "Create one with the assistant, which learns from your business website, or start from scratch.",
				noResults: "No agent with that name.",
				usedIn: "Used in {{list}}",
				usedInMore: "Used in {{list}} and {{count}} more",
				unused: "Not used in any flow",
				flowOff: "off",
				noVersion: "no text",
				updatedAt: "updated {{date}}",
				edit: "Edit",
				view: "View",
				delete: "Delete",
				deleteInUse: "Remove the agent from its flows before deleting it.",
				confirmDeleteTitle: "Delete the agent?",
				confirmDelete: "{{name}} goes away with its text, versions and sources. This can't be undone.",
				deleted: "Agent deleted.",
				loadMore: "Load more",
				unsaved: "Unsaved changes",
				discardConfirm: "Your changes to this agent will be lost. Leave anyway?",
				editor: {
					back: "Back to the list",
					newTitle: "New agent",
					name: "Name",
					namePlaceholder: "E.g. Ana",
					tabs: {
						knowledge: "Knowledge",
						sources: "Sources",
						intelligence: "Intelligence",
						test: "Test",
					},
					save: "Save",
					discard: "Discard",
					saved: "Agent saved.",
					created: "Agent created.",
					unsavedHint: "Unsaved changes.",
					readOnly: "You can view this agent, but not change it.",
					nameRequired: "Give the agent a name.",
					promptRequired: "The AI's text can't be empty.",
					caseNeedsAnswer: "Write the answer for each ready answer, or remove the card.",
					loadFailed: "Couldn't open this agent.",
					knowledge: {
						title: "Instructions and knowledge base",
						info: "What the AI knows and how it answers. Each saved text becomes a new version, and you can go back to an older one.",
						placeholder: "E.g. You are Ana, the assistant of Blue Store. Be polite and answer only with the information below.",
						live: "Live: version {{number}}, from {{date}}.",
						liveNone: "No text yet: the AI uses a default text.",
						history: "Versions",
						historyTitle: "Text versions",
						versionName: "Version {{number}}",
						current: "Live",
						useVersion: "Use this one",
						versionUsed: "Version {{number}} is live.",
						by: "by {{name}}",
						closeHistory: "Close versions",
					},
					sources: {
						siteTitle: "Source website",
						siteInfo: "Where the base came from (the assistant reads the website). Just for reference.",
						sitePlaceholder: "https://yourwebsite.com",
						filesTitle: "Files",
						filesInfo: "The AI reads text files and can send any file from here to the customer when it makes sense.",
						filesAdd: "Upload file",
						filesEmpty: "No files.",
						uploading: "Uploading...",
						remove: "Remove",
						casesTitle: "Ready answers",
						casesInfo: "When someone asks something similar, the AI answers with what you wrote.",
						casesAdd: "New answer",
						casesEmpty: "No ready answers.",
						caseWhen: "When they ask",
						caseWhenPlaceholder: "E.g. How long is the warranty?",
						caseAnswer: "Answer",
						caseAnswerPlaceholder: "E.g. The warranty lasts 90 days from delivery.",
						webTitle: "Web search",
						webInfo: "The AI only searches the websites you list.",
						webToggle: "Search the web",
						webDomains: "Allowed websites",
						webDomainsPlaceholder: "yourwebsite.com, another.com",
						wooTitle: "Store catalog",
						wooInfo: "The AI checks products, prices and stock in the selected WooCommerce stores.",
						wooEmpty: "No active WooCommerce store. Connect one in Integrations.",
					},
					intelligence: {
						model: "Model",
						modelInfo: "Bigger models understand more and cost more per message.",
						reasoning: "Reasoning",
						reasoningInfo: "More reasoning makes answers more thoughtful and more expensive. Only GPT-5 models use it.",
						reasoningOff: "This model doesn't use reasoning.",
						style: "Answer length",
						styles: {
							concise: "Short",
							balanced: "Medium",
							detailed: "Detailed",
						},
						behavior: "How it talks",
						split: "Split the answer into several messages, like a person",
						images: "Read images the customer sends",
						audio: "Listen to audio (transcribes, has a cost)",
						video: "Watch videos",
						wait: "Wait before answering (seconds)",
						waitInfo: "Time for the customer to finish typing: messages in a row become a single answer.",
						aiUser: "Who shows up as the attendant",
						aiUserInfo: "The AI user that holds the conversation while it answers. Without one, only the agent's name shows.",
						aiUserNone: "Only the agent's name",
						photo: "Photo",
						photoChange: "Change photo",
						photoSaveFirst: "Save the agent to add a photo.",
					},
					test: {
						title: "Chat like a customer",
						info: "The AI answers with what is saved. Nothing really happens: transfer, tags and CRM are only simulated.",
						saveFirst: "There are unsaved changes: the test uses the saved version.",
						newFirst: "Save the agent to test it.",
						placeholder: "Write like a customer",
						send: "Send",
						clear: "New chat",
						error: "Couldn't test right now. Try again.",
						empty: "Send a message to see how it answers.",
						files: "Would send: {{files}}",
					},
				},
			},
			invitePage: {
				loading: "Opening the invite...",
				title: "{{name}} invited you to the team",
				titleNoInviter: "You got an invite to the team",
				company: "Company",
				account: "Account",
				accounts: "Accounts",
				profile: "Profile",
				email: "Email",
				expires: "Valid until",
				profiles: {
					user: "Agent",
					admin: "Administrator",
				},
				createSubtitle: "Create your password to sign in.",
				name: "Your name",
				password: "Create a password",
				passwordHint: "At least 8 characters.",
				create: "Create account and sign in",
				hasAccount: "This email already has a Sttelar Talk account. Sign in with it to accept.",
				login: "Sign in to accept",
				acceptSubtitle: "Accept to join the team.",
				accept: "Accept invite",
				otherEmail: "You are signed in as {{current}}. The invite is for {{email}}.",
				switchUser: "Sign out and use the invite's email",
				joinedTitle: "Invite accepted",
				joined: "You are now part of the {{account}} account. To switch accounts later, use the selector in the menu.",
				goToAccount: "Go to the {{account}} account",
				stay: "Stay in the current account",
				notNow: "Not now",
				invalidTitle: "This invite is no longer valid",
				invalid: "It may have expired, been used or deleted. Ask the person who invited you for a new one.",
				failedTitle: "Could not open the invite",
				retry: "Try again",
			},
			setupChecklist: {
				title: "Get the account ready to serve customers",
				progress: "{{done}} of {{total}} ready",
				dismiss: "Dismiss",
				dismissHint: "Hidden only for you",
				done: "ready",
				items: {
					whatsapp: {
						title: "Connect WhatsApp",
						text: "Conversations arrive here.",
						action: "Connect",
					},
					team: {
						title: "Bring the team",
						text: "Invite the people who will answer.",
						action: "Invite",
					},
					ai: {
						title: "Turn on the AI",
						text: "It answers and hands over to the team.",
						action: "Turn on the AI",
					},
					hours: {
						title: "Set the hours",
						text: "The days and hours the company serves customers.",
						action: "Set",
					},
				},
			},
			accountSwitcher: {
				label: "Active account",
				reload: "The account was switched. Reload the page to continue in it.",
			},
			billingProfile: {
				title: "Billing details",
				subtitle: "Who pays the Sttelar Talk invoices. Shown on the invoice and used for the card.",
				personType: "Type",
				company: "Company",
				individual: "Individual",
				nameCompany: "Legal name",
				nameIndividual: "Full name",
				email: "Billing email",
				phone: "Phone",
				zipCode: "ZIP code",
				street: "Address",
				number: "Number",
				complement: "Complement",
				optional: "(optional)",
				required: "Fill in this field.",
				zipLoading: "Looking up the address by ZIP code",
				neighborhood: "Neighborhood",
				city: "City",
				state: "State",
				save: "Save",
				saved: "Billing details saved.",
			},
			billingOverview: {
				legacy: {
					title: "Billing arranged with Sttelar",
					text: "Your company's plan and invoices are arranged directly with Sttelar.",
					talk: "Talk to Sttelar",
					message: "Hi! I'd like to talk about the plan for {{name}}.",
				},
				conditions: {
					title: "Agreed terms",
					list: "List price: {{amount}}",
					price: "Agreed price for {{item}}: {{amount}}",
					discount: "{{value}}% off {{item}}",
					courtesy: "Complimentary: {{item}}",
					courtesyLicense: "Complimentary: one license",
					until: " until {{date}}",
					from: " from {{date}}",
					itemsOf: {
						plan: "the plan license",
						internal_chat: "the Internal Chat license",
						ai_pack: "the AI pack",
					},
					itemsOn: {
						all: "the monthly fee",
						plan: "the plan license",
						internal_chat: "the Internal Chat license",
						ai_pack: "the AI pack",
					},
					items: {
						all: "the monthly fee",
						plan: "the plan license",
						internal_chat: "the Internal Chat license",
						ai_pack: "the AI pack",
					},
				},
				loadError: "Billing could not be loaded.",
				disputes: {
					title: "Card dispute",
					text: "The {{amount}} charge was disputed on the card on {{date}}. While the card issuer reviews it, the company keeps working. If the dispute is accepted (the amount goes back to the card), the charge must be paid again within 7 days.",
				},
				overdue: {
					title: "Payment pending",
					titleSuspended: "Sending suspended",
					titleSuspendedInternal: "Internal Chat suspended",
					deadline: "Pay by {{date}}",
					text: "The {{amount}} invoice due on {{due}} hasn't been paid. Pay by {{date}} to keep {{what}}.",
					whatSending: "sending messages",
					whatInternal: "the Internal Chat",
					textSuspended: "The {{amount}} invoice due on {{due}} wasn't paid, so sending messages (agents, automations and AI) and the Internal Chat are stopped. Nothing was deleted, and customer messages keep arriving. Once paid, everything comes back right away.",
					textSuspendedInternal: "The {{amount}} invoice due on {{due}} wasn't paid, so the Internal Chat is stopped. Nothing was deleted. Once paid, it comes back right away.",
					textNoDeadline: "The {{amount}} invoice due on {{due}} hasn't been paid. In Billing, you can pay with Pix or try the card again.",
					creatingPix: "Creating the Pix...",
					disputeNote: "Includes a charge disputed on the card: the dispute was accepted, and the amount went back to the card.",
					invoiceDispute: "Dispute accepted on {{date}}",
					invoice: "Invoice due on {{date}}",
					payPix: "Pay with Pix",
					retryCard: "Try again on the {{brand}} card ending in {{last4}}",
					retrySavedCard: "Try again on the saved card",
					retrying: "Trying the card...",
					retryPaid: "Payment confirmed.",
					refresh: "Refresh",
					pixExpiredHint: "Create another one with \"Pay with Pix\".",
					boletoSettling: "Already paid the boleto? Confirmation takes up to 3 business days.",
				},
				status: {
					free: "Free",
					active: "Active",
					courtesy: "Courtesy",
					failed: "Payment pending",
					canceling: "Canceled",
					resumed: "Resumed",
				},
				free: "Free",
				perMonth: "per month",
				starterSummary: "Up to {{users}} people, {{accounts}} account, {{whatsapps}} WhatsApp number and {{boards}} CRM board. {{common}} messages and {{ai}} AI messages per month.",
				summary: {
					seats: "{{count}} license",
					seats_plural: "{{count}} licenses",
					internal: "{{count}} for Internal Chat",
					pack: "the {{messages}} AI messages pack",
					starterInternal: "Internal Chat for {{count}} person",
					starterInternal_plural: "Internal Chat for {{count}} people",
					and: "and",
				},
				renewsCard: "Renews on {{date}}, on the {{brand}} card ending in {{last4}}.",
				renewsBoleto: "Renews on {{date}}, by boleto.",
				renewsSavedCard: "Renews on {{date}}, on the saved card.",
				courtesyNote: "This Company is on courtesy and is not charged.",
				disabledNote: "Subscribing is unavailable right now.",
				changeNote: "To change the plan or the licenses, contact Sttelar support.",
				starterChangeNote: "To move to Basic or Pro, contact Sttelar support.",
				paymentPendingNote: "Settle the pending monthly fee before changing the plan. If you need help, contact Sttelar support.",
				reductionScheduledNote: "To increase now, undo the scheduled reduction first.",
				nothingToReduceNote: "There is nothing to reduce: the plan is already at the minimum for what the company uses.",
				cancellationScheduledNote: "With the cancellation scheduled, the plan does not change here. To stay on it, undo the cancellation.",
				resumeNote: "The resumed subscription starts at the end of the paid cycle; until then, the plan does not change here.",
				paidUntil: "Paid until {{date}}. Then, Starter.",
				resumeStarts: "The resumed subscription starts on {{date}}, with no charge until then.",
				actions: {
					upgrade: "Subscribe to Basic or Pro",
					addInternal: "Add Internal Chat",
					changeInternal: "Change Internal Chat",
					reduce: "Reduce at renewal",
					change: "Change plan",
					cancel: "Cancel plan",
					cancelInternal: "Cancel Internal Chat",
				},
				pending: {
					titleBoleto: "Boleto awaiting payment",
					titlePix: "Pix awaiting payment",
					pixExpiredHint: "Give up on it to generate another one.",
					textChange: "{{what}}. Due now: {{amount}}, the difference until the renewal. The plan changes when the payment is confirmed.",
					verifyingChange: "The payment was not confirmed in time. We are checking whether the request was created; do not change again before that.",
					titleCard: "Confirming the payment",
					what: "{{plan}} with {{details}}",
					verifying: "Pagar.me did not confirm in time. We are checking whether the subscription was created; do not subscribe again before that.",
					keepBoleto: "Keep boleto",
					keep: "Keep request",
					text: "{{what}}, {{amount}} per month. The plan changes once the payment is confirmed.",
					due: "Due on {{date}}.",
					line: "Boleto number",
					copy: "Copy",
					copied: "Boleto number copied.",
					open: "Open boleto",
					cancel: "Give up",
					cancelConfirm: "Confirm",
					cancelHint: "After you give up, do not pay this boleto.",
					canceled: "Request canceled.",
					refresh: "Refresh",
				},
				reduction: {
					title: "Reduction scheduled for {{date}}",
					text: "From that date, the company will have {{what}}, for {{amount}} per month. Nothing changes until then.",
					undo: "Undo reduction",
					undoConfirm: "Confirm: undo",
					keep: "Keep reduction",
					undone: "Reduction undone.",
					locked: "It can no longer be undone: the reduction applies at the renewal. After it, to increase, use Change plan.",
				},
				cancellation: {
					title: "Plan canceled: valid until {{date}}",
					titleInternal: "Internal Chat canceled: valid until {{date}}",
					text: "The subscription stopped at Pagar.me and nothing else is charged. Until that date, the company stays on {{plan}}; then it moves to Starter, for free.",
					textInternal: "The subscription stopped at Pagar.me and nothing else is charged. Internal Chat stays until that date.",
					undo: "Undo cancellation",
					undoHint: "Subscribes to {{plan}} again from {{date}}, {{paidWith}}, with no charge now.",
					byCard: "on the {{brand}} card ending in {{last4}}",
					bySavedCard: "on the saved card",
					byBoleto: "by boleto",
					undoConfirm: "Confirm: stay on the plan",
					keep: "Keep cancellation",
					undone: "Cancellation undone: the plan continues after the paid cycle.",
					locked: "It can no longer be undone: the plan ends now. After that, subscribe again in Billing.",
				},
				adequacy: {
					needed: "The new plan does not fit everything the company uses. Choose what stays by {{date}}; without a choice, access is restricted to Billing at the renewal.",
					chosen: "The choice of what stays is made and applies at the renewal.",
					choose: "Choose what stays",
					review: "View or change the choice",
					notNeeded: "There is nothing to choose: everything fits the plan.",
					saved: "Choice saved. It applies at the renewal.",
					doneRestricted: "Done: access is back to normal.",
					doneDue: "Done: the company fits the plan.",
				},
				adequacyDue: {
					title: "Choose what stays by {{date}}",
					text: "A plan condition has ended, and the company is over the {{plan}} limits. Creating more of what is over is blocked. By {{date}}, choose what stays: what leaves is archived and kept for 12 months. Without a choice, access is restricted until you make one. Nothing is deleted.",
					action: "Choose what stays",
				},
				restricted: {
					title: "Choose what stays to continue",
					text: "The company is over the limits of {{plan}}. Until the choice of what stays, only this screen works for everyone in the company. What leaves is archived and kept for 12 months.",
					action: "Choose what stays",
				},
				archived: {
					title: "Archived items ({{count}})",
					info: "Items removed when fitting a smaller plan. They are kept for 12 months and come back with Restore, if the plan allows.",
					show: "View archived",
					hide: "Hide",
					loading: "Loading...",
					empty: "No archived items.",
					restore: "Restore",
					restoring: "Restoring...",
					restored: "{{name}} restored.",
					account: "Account {{name}}",
					accountArchived: "archived",
					dates: "archived on {{archived}}, kept until {{until}}",
					types: {
						account: "Account",
						whatsapp: "Number",
						crm_board: "Board",
						user: "Person",
					},
				},
				usage: {
					title: "Usage this month",
					period: "From {{start}} to {{end}}",
					info: "Messages sent through the platform count; those sent from the phone do not. The month starts on the day the plan started.",
					users: "People",
					internalChat: "Internal Chat",
					common: "Messages",
					ai: "AI messages",
					accounts: "Accounts",
					whatsapps: "WhatsApp numbers",
					crmBoards: "CRM boards",
					of: "{{used}} of {{limit}}",
					unlimited: "{{used}}, no limit",
					notContracted: "not subscribed",
				},
				invoices: {
					title: "Invoices",
					empty: "No invoices yet.",
					date: "Date",
					amount: "Amount",
					method: "Method",
					status: "Status",
					credit_card: "Card",
					pix: "Pix",
					boleto: "Boleto",
					openBoleto: "Open boleto",
					abandoned: "Canceled: do not pay",
					replaced: "Replaced by Pix",
					refund: "Request refund",
					partlyRefunded: "Paid, {{amount}} refunded",
					statuses: {
						paid: "Paid",
						overpaid: "Paid",
						underpaid: "Underpaid",
						pending: "Pending",
						processing: "Processing",
						failed: "Not approved",
						canceled: "Canceled",
						refunded: "Refunded",
						chargedback: "Disputed",
					},
				},
				refunds: {
					title: "Refund requests",
					item: "Refund of {{amount}}, requested on {{date}}",
					refusedReason: "Reason for refusal: {{reason}}",
					canceledReason: "Reason: {{reason}}",
					statuses: {
						requested: "Under review",
						refused: "Refused",
						approved: "Approved: refund in progress",
						refunding: "Approved: refund in progress",
						refunded: "Refunded",
						canceled: "Not refunded",
					},
				},
			},
			refundDialog: {
				title: "Request refund",
				subtitle: "Sttelar reviews the request and replies here, in Billing.",
				close: "Close",
				summary: "Refund of {{refundable}}, from the {{amount}} charge on {{date}}.",
				reason: "Reason",
				reasonPlaceholder: "E.g.: the monthly fee was charged twice this month",
				hint: "At least 10 characters. Once approved, the amount goes back through the same payment method; for boleto, Sttelar arranges the refund.",
				cancel: "Cancel",
				send: "Request refund",
				sent: "Refund request sent.",
			},
			plansDialog: {
				stepsLabel: "Subscription steps",
				stepCustomize: "Customize subscription",
				stepPayment: "Review and pay",
				choosePlan: "Choose the plan",
				perSeatMonth: "per license, per month",
				perks: {
					accounts: "{{count}} account",
					accounts_plural: "{{count}} accounts",
					accountsUnlimited: "Unlimited accounts",
					whatsapps: "{{count}} WhatsApp number",
					whatsapps_plural: "{{count}} WhatsApp numbers",
					whatsappsUnlimited: "Unlimited WhatsApp numbers",
					boards: "{{count}} CRM board",
					boards_plural: "{{count}} CRM boards",
					boardsUnlimited: "Unlimited CRM boards",
				},
				bothPlans: "On both plans: unlimited messages and {{ai}} AI messages per month.",
				seatsHint: "One per team member. Minimum of {{min}}.",
				addons: "Add-ons",
				optional: "Optional",
				internalHint: "Team conversations inside Sttelar Talk.",
				internalPrice: "{{price}} per license, per month",
				howManyInternal: "How many people will use it",
				aiPackHint: "The plan already includes {{ai}} AI messages per month.",
				aiPackNoneShort: "No pack",
				aiPackOptionShort: "{{messages}} messages for {{price}}",
				lineSeats: "{{count}} license at {{price}}",
				lineSeats_plural: "{{count}} licenses at {{price}}",
				linePack: "{{messages}} AI messages per month",
				continue: "Continue to payment",
				continueFree: "Continue",
				continueNote: "You review everything before paying.",
				back: "Back to the subscription",
				billingIntro: "Who pays the invoices. The card also needs the billing address.",
				billingSaveFailed: "Couldn't save right now. Check your connection and try again.",
				billingLoadFailed: "Couldn't load the details right now.",
				freeChangeTitle: "No charge now",
				freeChangeText: "The change takes effect right away, and the new amount comes on the next invoice.",
				billingEdit: "Change",
				billingCancel: "Cancel",
				billingLoading: "Loading the details…",
				billingAddress: "{{street}}, {{number}}, {{city}}, {{state}}",
				saveBilling: "Save and continue",
				cvvHelp: "The 3 digits on the back of the card. On American Express, 4 digits on the front.",
				cardErrors: {
					number: "Check the card number.",
					holder: "Type the name as it appears on the card.",
					expiry: "Invalid or expired date.",
					cvv: "Type the 3 or 4 digits.",
				},
				boletoDue: "The boleto is due in 3 days.",
				quoteTodayLabel: "Today, per month",
				doneNext: "The next charge is on {{date}}.",
				confirmEmail: {
					title: "Confirm your email to subscribe",
					cta: "Confirm your email to continue",
					text: "We sent a link to {{email}}. After clicking it, come back here: your choices stay saved.",
				},
				trust: {
					recurringCard: "Charged every month on the card. The next charge is in one month.",
					recurringBoleto: "A new boleto every month. The next one is in one month.",
					activationCard: "The plan is unlocked as soon as the payment is confirmed, usually within seconds.",
					activationInternal: "Internal Chat is unlocked as soon as the payment is confirmed, usually within seconds.",
					activationBoleto: "The plan is unlocked once the payment is confirmed, up to 3 business days after paying.",
					cancel: "Cancel in Billing, with payments up to date. It stays valid until the end of the paid month.",
					changeCard: "The change takes effect as soon as the payment is confirmed, usually within seconds.",
					changePix: "The change takes effect as soon as the Pix is paid.",
					changeBoleto: "The change takes effect once the payment is confirmed, up to 3 business days after paying.",
				},
				title: "Subscribe to a plan",
				titleInternal: "Add Internal Chat",
				titleChange: "Change plan",
				subtitleChange: "You pay now only the difference until the renewal.",
				savedCard: "Card ending in {{last4}}",
				quoteNowHint_plural: "For the {{count}} days until the renewal",
				savedCardBrand: "{{brand}} ending in {{last4}}",
				savedCardShort: "ending in {{last4}}",
				submitDisabled: "Pay",
				failedBoletoChange: "Could not generate the boleto right now. Try again in a few minutes or pay another way.",
				pixVerifyingText: "We are checking whether the Pix was created. Do not change again before that.",
				pixVerifyingSlow: "This is taking a while. You can close this: the Pix shows up in Billing when it is ready.",
				pixExpiredAt: "This Pix expired at {{time}}.",
				quoteNowInfo: "The difference in the monthly fee for the days left. The renewal date does not change.",
				summaryTitleChange: "What changes",
				quoteFreeValue: "No charge",
				quoteRetry: "Try again",
				quoteFailed: "Could not calculate the amount right now.",
				quoting: "Calculating…",
				quoteChanged: "The amount changed since it was calculated. Check the new amount and pay again.",
				chargeCard: "Charged now on the card {{card}}.",
				chargePix: "Pay now by Pix.",
				chargeBoleto: "Pay by boleto, due in 3 days.",
				boletoMinChange: "Boleto only for amounts from {{min}}.",
				boletoRenewal: "Boleto only with 10 days or more until the renewal.",
				planCurrent: "Current",
				packCurrent: "(current)",
				planChangeInfo: "Here you can only increase. To decrease or go to Basic, use Reduce at renewal.",
				seatsHelpChange: "One license per team member (the AI does not use a license). At least the {{min}} licenses the company already has.",
				pixValidUntil: "Valid until {{time}}",
				pixGenerating: "Generating the Pix code",
				pixCode: "Pix code",
				pixCopyManual: "Could not copy. Select the code and copy it.",
				pixCloseHint: "You can close this: the Pix stays in Billing until it expires.",
				pixExpiredTitle: "The Pix expired",
				pixExpiredText: "The code was not paid within 30 minutes. Generate another one to pay.",
				pixAgain: "Generate another Pix",
				failedPixTitle: "Could not generate the Pix",
				failedSavedCard: "The card {{card}} was not accepted. Pay by Pix or boleto.",
				payOtherWay: "Pay another way",
				reviewTitle: "Payment under review",
				reviewText: "We received the payment. The Sttelar team will review the request and contact you.",
				doneChangeTitle: "Change done",
				verifyingChange: "The payment was not confirmed in time. We are checking whether the request was created; do not change again before that.",
				pix: "Pix",
				savedCardNote: "Charged on the subscription card.",
				pixNote: "The Pix code is valid for 30 minutes.",
				quoteMonthly: "Per month, from {{date}}",
				quoteNothing: "Choose what to increase",
				quoteNow: "Due now",
				quoteNowHint: "For the day left until the renewal",
				quoteFree: "The new amount comes on the next invoice.",
				submitFree: "Change now",
				submitPix: "Generate Pix for {{amount}}",
				pixTitle: "Pay with Pix",
				pixText: "Open your bank app and scan the QR code, or copy the code and paste it in the app.",
				pixQrAlt: "Pix QR Code",
				pixCopy: "Copy Pix code",
				pixCopied: "Pix code copied.",
				pixWaiting: "Waiting for the payment…",
				doneChange: "The company now has {{what}}.",
				failedPix: "Try again in a moment or pay another way.",
				plan: "Plan",
				quoteToday: "Today: {{amount}} per month",
				perSeat: "{{price}} per license",
				seats: "Licenses",
				seatsHelp: "One license per team member (the AI does not take a license). At least {{min}}: the plan charges for at least {{billed}}, and you cannot buy fewer than the people the Company already has.",
				internal: "Internal Chat",
				internalHelp: "Team conversations inside Sttelar Talk, for whoever has a license for it. Up to the number of plan licenses.",
				internalStarterHelp: "Team conversations inside Sttelar Talk. Starter allows up to {{max}} people.",
				aiPack: "AI pack",
				aiPackHelp: "The pack replaces the plan's AI messages: the 10k pack gives 10k per month.",
				aiPackNone: "No pack ({{ai}} AI messages per month)",
				aiPackOption: "{{messages}} messages per month, {{price}}",
				decrease: "Decrease",
				increase: "Increase",
				summaryTitle: "Subscription summary",
				items: {
					plan: "{{plan}}, {{count}} license",
					plan_plural: "{{plan}}, {{count}} licenses",
					internal: "Internal Chat, {{count}} license",
					internal_plural: "Internal Chat, {{count}} licenses",
					pack: "AI pack, {{messages}} messages",
				},
				total: "Total per month",
				payment: "Payment method",
				card: "Credit card",
				boleto: "Boleto",
				boletoMin: "Boleto only from {{min}} per month.",
				cardNumber: "Card number",
				cardHolder: "Name on card",
				cardExpiry: "Expiry",
				cardCvv: "CVV",
				cardSecure: "Card details go straight to Pagar.me. Sttelar Talk neither receives nor stores them.",
				cardInvalid: "Check the card details.",
				cardServiceDown: "Could not reach the card processor. Nothing was charged; try again in a moment.",
				cardExpiryPlaceholder: "MM/YY",
				verifying: "Checking with Pagar.me",
				verifyingText: "Pagar.me did not confirm in time. We are checking whether the subscription was created; do not subscribe again before that.",
				failedBoletoTitle: "The boleto could not be generated",
				boletoGenerating: "Generating the boleto",
				boletoTextNoDate: "The plan changes once the payment is confirmed.",
				submitCard: "Pay {{amount}}",
				submitBoleto: "Generate boleto for {{amount}}",
				cancel: "Cancel",
				close: "Close",
				paying: "Confirming the payment",
				payingText: "This usually takes a few seconds.",
				payingSlow: "The confirmation is taking a while. You can close this: the plan changes as soon as the payment is confirmed.",
				doneTitle: "Payment confirmed",
				donePlan: "The Company is now on {{plan}}.",
				doneInternal: "Internal Chat is enabled for {{count}} person.",
				doneInternal_plural: "Internal Chat is enabled for {{count}} people.",
				failedTitle: "Payment declined",
				failedCard: "The card was not accepted. Check the details or use another card.",
				failedBoleto: "The boleto could not be generated now. Try again in a few minutes or pay by card.",
				retry: "Try again",
				boletoTitle: "Boleto generated",
				boletoText: "Pay by {{date}}. The plan changes once the payment is confirmed.",
				openRequest: "There is already a request awaiting payment. It is shown in Billing.",
			},
			reductionDialog: {
				title: "Reduce at renewal",
				subtitle: "It applies from the renewal. Until then, the company keeps what it has today.",
				planInfo: "On Basic, accounts, WhatsApp numbers and CRM boards have limits.",
				seatsHelp: "At least {{min}}: the people the company already has, and never below the plan's minimum.",
				internalHelp: "At least {{min}}: who already uses Internal Chat.",
				internalHelpStarter: "On Starter, Internal Chat keeps at least 1 license: to remove the last one, use Cancel Internal Chat.",
				from: "Per month, from {{date}}",
				nothing: "Choose what to reduce",
				note: "Nothing is charged now, and nothing from the paid month is refunded.",
				submit: "Schedule reduction",
				blockersHint: "After confirming, you choose what stays, until the renewal. The rest is archived, nothing is deleted.",
				doneTitle: "Reduction scheduled",
				doneText: "From {{date}}, the company will have {{what}}, for {{amount}} per month. Nothing changes until then.",
				blockers: {
					users: "People: the company has {{used}}, and the smaller plan allows {{limit}}.",
					internalChatUsers: "Internal Chat: {{used}} people use it, and the smaller plan allows {{limit}}.",
					accounts: "Accounts: the company has {{used}}, and the smaller plan allows {{limit}}.",
					whatsapps: "WhatsApp numbers: the company has {{used}}, and the smaller plan allows {{limit}}.",
					crmBoards: "CRM boards: the company has {{used}}, and the smaller plan allows {{limit}}.",
				},
				blockersTitle: "The new plan does not fit everything the company uses:",
				chooseNow: "Choose what stays by {{date}}. Without a choice, access is restricted to Billing at the renewal.",
				chooseAction: "Choose what stays",
				chooseLater: "Choose later",
				cancel: {
					title: "Cancel {{plan}}",
					titleInternal: "Cancel Internal Chat",
					subtitle: "The subscription stops now at Pagar.me. Nothing is refunded or charged again.",
					whatHappens: "What happens",
					text: "{{plan}} stays until the end of the cycle already paid. Then the company moves to Starter, for free, and the AI pack ends.",
					textInternal: "Internal Chat stays until the end of the cycle already paid and then ends.",
					starterLimits: "On Starter: up to {{users}} people, {{accounts}} account, {{whatsapps}} WhatsApp number, {{boards}} CRM board, {{common}} messages and {{ai}} AI messages per month.",
					keepInternal: "Keep Internal Chat on Starter",
					keepInternalHint: "{{price}} per person, per month. The subscription continues with it only.",
					keepHelp: "Up to {{max}} people, the Starter maximum.",
					until: "Per month, after {{date}}",
					note: "Undoing means subscribing again: the same plan, from the day after the cycle ends, with no charge now.",
					submit: "Confirm cancellation",
					doneTitle: "Plan canceled",
					doneText: "The subscription stopped at Pagar.me. {{plan}} stays until {{date}}; then the company moves to Starter.",
					doneTitleInternal: "Internal Chat canceled",
					doneTextInternal: "The subscription stopped at Pagar.me. Internal Chat stays until {{date}} and then ends.",
				},
			},
			restrictedAccess: {
				title: "Restricted access",
				textAdmin: "The company is over the plan limits. To use Sttelar Talk again, choose in Billing what stays on the plan. Nothing is deleted: what leaves is kept for 12 months.",
				textMember: "The company is over the plan limits, and the administrator must choose what stays, in Billing. Until then, Sttelar Talk is paused for the team. Contact your company administrator.",
				action: "Go to Billing",
			},
			adequacyDialog: {
				title: "Choose what stays on {{plan}}",
				subtitle: "By {{date}}. What leaves is archived at the renewal and kept for 12 months.",
				subtitleRestricted: "To use Sttelar Talk again, choose what stays on the plan. What leaves is archived now and kept for 12 months.",
				subtitleDue: "A plan condition has ended. Choose what stays by {{date}}: what leaves is archived now and kept for 12 months.",
				close: "Close",
				accounts: "Accounts",
				whatsapps: "WhatsApp numbers",
				crmBoards: "CRM boards",
				users: "People",
				internalChat: "Who keeps Internal Chat",
				count: "{{used}} of {{limit}}",
				countFree: "{{used}}, no limit",
				accountGoesWith: "Its numbers and boards leave with it.",
				goesWithAccount: "Leaves with the account",
				noWhatsapps: "No WhatsApp numbers.",
				noBoards: "No board: Main is created automatically.",
				defaultBoard: "Default",
				boardsNote: "An account left without a board gets Main automatically, and it counts toward the limit.",
				you: "You",
				admin: "Administrator",
				userWithoutAccount: "No account that stays: leaves too",
				needAdmin: "At least one administrator must stay.",
				internalOnly: "Only uses Internal Chat: to remove it, the person leaves.",
				sharedPerson: "Also in another company: their Internal Chat does not change here.",
				summaryTitle: "On {{plan}}",
				limits: {
					users: "People",
					internalChatUsers: "Internal Chat",
					accounts: "Accounts",
					whatsapps: "Numbers",
					crmBoards: "Boards",
				},
				leavingTitle: "Leaves at the renewal",
				leavingNow: "Leaves now",
				leaving: {
					accounts: "{{count}} account",
					accounts_plural: "{{count}} accounts",
					whatsapps: "{{count}} number",
					whatsapps_plural: "{{count}} numbers",
					crmBoards: "{{count}} board",
					crmBoards_plural: "{{count}} boards",
					users: "{{count}} person",
					users_plural: "{{count}} people",
					internalChatOff: "{{count}} without Internal Chat",
					nothing: "Nothing",
				},
				keepNote: "Nothing is deleted: what leaves is kept for 12 months and comes back with Restore, if the plan allows.",
				overHint: "Uncheck until everything fits the plan limits.",
				save: "Save choice",
				saveRestricted: "Finish and restore access",
				saveDue: "Finish the choice",
			},
			planLimit: {
				title: "The plan has reached its limit",
				used: {
					users: "The Company uses {{used}} of the plan's {{limit}} people.",
					internalChat: "The Company uses {{used}} of {{limit}} Internal Chat licenses.",
					accounts: "The Company uses {{used}} of the plan's {{limit}} accounts.",
					whatsapps: "The Company uses {{used}} of the plan's {{limit}} WhatsApp numbers.",
					crmBoards: "The Company uses {{used}} of the plan's {{limit}} CRM boards.",
				},
				generic: "The Company has reached the plan limit.",
				starterOffer: "On Basic or Pro you choose how many licenses you want and get more accounts, numbers and boards.",
				internalOffer: "Internal Chat costs {{price}} per person, per month.",
				otherPlan: "To get more, contact Sttelar support.",
				askAdmin: "Ask the Company administrator to change the plan.",
				changeOffer: "Add licenses: you pay now only the difference until the renewal.",
				changeOfferPro: "On Pro, accounts, numbers and boards have no limit. You pay now only the difference until the renewal.",
				paymentPending: "There is a pending monthly fee. Settle it in Billing (or contact support) before changing the plan.",
				reductionScheduled: "There is a reduction scheduled for the renewal. To increase now, undo it in Billing.",
				cancellationScheduled: "A cancellation is scheduled: until the end of the paid cycle, creating follows the Starter limits. To stay on the plan, undo the cancellation in Billing.",
				seeOptions: "See options",
				pendingRequest: "There is a request awaiting payment. It is in Billing.",
				openBilling: "Open Billing",
				seePlans: "See plans",
				addInternal: "Add Internal Chat",
				notNow: "Not now",
				ok: "Got it",
			},
			companySetup: {
				steps: {
					company: "Company",
					account: "Account",
					ai: "AI",
					whatsapp: "WhatsApp",
					team: "Team",
				},
				progressLabel: "Setup steps",
				leave: "Leave",
				company: {
					title: "What is your company called?",
					subtitle: "It's the name your team will see in Sttelar Talk.",
					label: "Company name",
					placeholder: "E.g.: Blue Phone Store",
					submit: "Continue",
				},
				account: {
					title: "And the name of the first account?",
					subtitle: "An account holds the WhatsApp numbers, the team and the conversations of one store or branch. If you have just one, you can use its name.",
					label: "Account name",
					placeholder: "E.g.: Headquarters",
					info: "Later you can create other accounts, one for each store or branch.",
					submit: "Create account",
					back: "Change the company name",
				},
				whatsapp: {
					title: "Connect the company's WhatsApp",
					subtitle: "Once the number is connected, conversations arrive here and the AI can already answer.",
					connect: "Connect now",
					connectAnother: "Connect another number",
					later: "Do it later",
					next: "Continue",
					showQr: "Show the QR code",
					info: "On the company phone, open WhatsApp, go to Linked devices and scan the QR code.",
					noPermission: "Whoever manages the connections will connect the number later.",
					verifyFirst: "Confirm your email to connect WhatsApp. The link went to {{email}}.",
					resend: "Resend the email",
					alreadyConfirmed: "I've confirmed",
					status: {
						CONNECTED: "Connected",
						qrcode: "QR code not scanned yet",
						OPENING: "Connecting…",
						PAIRING: "Connecting…",
						TIMEOUT: "Disconnected",
						DISCONNECTED: "Disconnected",
					},
				},
				team: {
					pending: "Invite sent",
					title: "Invite the people who will answer with you",
					subtitle: "They get an email, create a password and join your account.",
					email: "Email",
					emailPlaceholder: "name@yourcompany.com",
					profile: "Role",
					profileInfo: "An administrator also sets up the account: team, connections and AI.",
					profiles: {
						user: "Agent",
						admin: "Administrator",
					},
					send: "Invite",
					sent: "Invite sent to {{email}}.",
					invited: "Invites sent",
					verifyFirst: "Confirm your email to send invites. The link went to {{email}}.",
					resend: "Resend the email",
					resent: "Sent again. Check your inbox.",
					emailInvalid: "Type a valid email.",
					later: "Do it later",
					next: "Continue",
					noPermission: "Whoever manages the team will send the invites later.",
				},
				done: {
					teamJoined: "Your team has joined",
					title: "All set",
					subtitle: "Your company is now on Sttelar Talk. What you left for later shows up on the dashboard.",
					aiOn: "On",
					aiOff: "Not on yet",
					whatsappOn: "Connected",
					whatsappOff: "Not connected yet",
					teamNone: "No one invited yet",
					teamOne: "1 invite sent",
					teamMany: "{{count}} invites sent",
					submit: "Go to the dashboard",
					submitContract: "Subscribe to {{plan}}",
				},
			},
			aiSetup: {
				confirmEmail: {
					title: "First, confirm your email",
					text: "We sent a link to {{email}}. The AI is unlocked as soon as you click it.",
					check: "I've confirmed",
					notYet: "We haven't received the confirmation yet. Click the link in the email and try again.",
					resend: "Resend the email",
					resent: "Sent again. Check your inbox and spam folder.",
				},
				step: "Step {{current}} of {{total}}",
				reviewNote: "You review everything before turning the AI on.",
				skip: "Skip for now",
				close: "Close",
				source: {
					title: "Teach your AI about your business",
					subtitle: "Paste your company website and the AI builds the knowledge base on its own.",
					subtitleDescription: "Tell us how the business works and the AI organizes the knowledge base.",
					siteTitle: "Company website",
					siteText: "The AI reads the site pages and keeps what matters for customer service.",
					siteLabel: "Website address",
					siteHint: "It only reads pages from this site.",
					siteRequired: "Enter the website address.",
					siteSubmit: "Build from the website",
					or: "or",
					noSite: "I don't have a website, I'll describe the business",
					haveSite: "I have a website",
					descriptionTitle: "Tell us about the business",
					descriptionText: "Write it the way you would explain the company to a new agent.",
					descriptionLabel: "About the business",
					descriptionPlaceholder: "What you sell or do, where you serve, opening hours, payment, delivery, returns...",
					descriptionCountShort: "{{count}} of at least {{min}} characters",
					descriptionCount: "{{count}} characters",
					descriptionSubmit: "Build from the description",
					learnTitle: "What the AI will learn",
				},
				learn: {
					about: { name: "About the company", text: "What you do and how you serve." },
					offerings: { name: "Products and services", text: "What customers can buy or request." },
					hours: { name: "Opening hours", text: "When the company is available." },
					faq: { name: "Frequently asked questions", text: "The most common questions, answered." },
					contact: { name: "Contact and addresses", text: "Phones, emails and stores." },
					policies: { name: "Policies", text: "Returns, warranty, delivery and payment." },
				},
				preparing: {
					title: "Preparing your AI",
					failedTitle: "Couldn't build the knowledge base",
					subtitle: {
						site: "Reading the website and organizing everything so it can answer your customers.",
						description: "Organizing what you told us so it can answer your customers.",
					},
					stages: {
						readSite: "Reading the website",
						readDescription: "Reading the description",
						offerings: "Sorting products and services",
						faq: "Writing the frequently asked questions",
						review: "Reviewing the knowledge base",
					},
					status: { done: "Done", active: "In progress", queued: "Queued" },
					eta: { site: "Usually takes less than a minute.", description: "Usually takes a few seconds." },
					retry: "Try again",
					describeInstead: "Describe the business",
				},
				learned: {
					cardTitle: "What it learned",
					viewBase: "View and edit the knowledge base",
					nameLabel: "AI name",
					defaultName: "Assistant",
					stepDone: "Learning complete",
					heroTitle: "Done! Your AI just learned about {{name}} 🎉",
					heroText: "See what it found out and chat with it before it starts serving your customers.",
					yourBusiness: "your business",
					findings: { about: "About the company", services: "Services and products", brands: "Brands served", highlights: "Useful information" },
					nothingFound: "It couldn't find a summary of the business. View and complete the knowledge base.",
					missing: { site: "The website didn't include {{items}}. Complete the knowledge base so it can answer better.", description: "The description didn't include {{items}}. Complete the knowledge base so it can answer better." },
					missingItems: { hours: "the opening hours", contact: "the contacts and addresses", policies: "the policies (returns, warranty and payment)" },
					and: "and",
					complete: "Complete the knowledge base",
				},
				meet: {
					title: "Your assistant wants to introduce itself!",
					hello: "Hi! This is {{name}}, the virtual assistant of {{business}}.",
					helloDefault: "Hi! I'm the virtual assistant of {{business}}.",
					helpFallback: "I can answer your customers' questions about what I learned.",
					ask: "Ask as if you were a customer:",
					free: "Write my own question",
					preparing: "Preparing your assistant...",
				},
				activate: {
					title: "Ready for {{name}} to start serving?",
					titleDefault: "Ready for your assistant to start serving?",
					what: "Once on, it instantly answers new WhatsApp conversations and calls your team when needed.",
					whenConnected: "WhatsApp isn't connected yet: once on, it starts answering as soon as you connect.",
					undo: "It lives in Flows, under \"Atendimento com IA\", where you can turn it off anytime.",
					cta: "Turn on my AI",
					skip: "Continue without turning on",
					saved: "Everything is saved. You can turn it on later.",
					savedAskEditor: "Everything is saved. Someone who can edit Flows can turn on the AI.",
				},
				knowledge: {
					title: "Knowledge base",
					subtitle: "This is what the AI uses to answer. Edit anything you want.",
					count: "{{count}} characters",
					pages: "Pages read",
					cancel: "Cancel",
					save: "Save",
				},
				test: {
					title: "Chat with {{name}}",
					subtitle: "Write as if you were a customer. Nothing here is sent to anyone.",
					emptyTitle: "Ask a question",
					emptyText: "Start with one of these or write your own.",
					suggestions: {
						howItWorks: "How does your service work?",
						services: "What services do you offer?",
						hours: "What are your opening hours?",
					},
					placeholder: "Write a message",
					send: "Send",
					thinking: "{{name}} is typing...",
					titleDefault: "Chat with your assistant",
					thinkingDefault: "Your assistant is typing...",
					restart: "Start over",
					error: "Couldn't answer right now. Try again.",
					praise: "This is how your AI can serve your customers!",
					wrong: "Saw something wrong in the answer?",
					fix: "Fix the knowledge base",
				},
				errors: { generic: "Couldn't finish right now. Try again." },
			},
			auth: {
				language: "Language",
				showPassword: "Show password",
				hidePassword: "Hide password",
				backToLogin: "Back to sign in",
				version: "Version {{version}}",
				checkEmailTitle: "Check your email",
				invalidLinkTitle: "This link is no longer valid",
				notArrived: "Didn't get it? Check your spam folder or ask again.",
				resend: "Resend email",
				resent: "Sent again. Check your inbox.",
				passwordHint: "Use at least 8 characters.",
				fields: {
					name: "Full name",
					email: "Email",
					password: "Password"
				},
				errors: {
					generic: "That didn't work. Try again in a moment."
				},
				showcase: {
					label: "About Sttelar Talk",
					headline: "Turn your WhatsApp into a smart customer service hub.",
					lead: "Bring together your numbers, your team and an AI that answers your customers automatically. All in one place.",
					preview: {
						customer: "Hi! My air conditioner stopped cooling. Can you fix it?",
						aiName: "Assistant",
						aiTag: "AI",
						ai: "We sure can! 😊 Is your unit still under warranty, or is it out of warranty?",
						customer2: "It's out of warranty. Can I schedule a visit?",
						handoff: "The AI handed off to Ana, from Support",
						humanName: "Ana",
						human: "Hi, Carla! 😊 I can check the available times to schedule your visit.",
						statusAi: "AI on it",
						statusHuman: "With Ana",
						otherLast1: "Thanks, it worked!",
						otherLast2: "Can you confirm the visit time?"
					},
					benefits: {
						together: { title: "Every conversation. One screen.", text: "Organize your WhatsApp numbers, agents and customers in one place." },
						ai: { title: "Your AI and your team working together.", text: "The AI answers automatically and hands conversations to your agents when needed." },
						free: { title: "Start free. Grow at your pace.", text: "Try Sttelar Talk on the Starter plan and upgrade as your business grows." }
					}
				},
				login: {
					title: "Sign in to your account",
					subtitle: "Use the email and password you registered with.",
					remember: "Keep me signed in",
					forgot: "Forgot your password?",
					noAccount: "New to Sttelar Talk?",
					createAccount: "Start free",
					plans: "See the plans"
				},
				signup: {
					title: "Create your account",
					subtitle: "Start free on Starter, no card required.",
					planChosen: "You chose {{plan}}. The subscription opens after you sign up.",
					planLater: "You chose {{plan}}. Your account starts free on Starter, and Sttelar handles the {{plan}} subscription.",
					planMessage: "Hi! I want to subscribe to {{plan}} on Sttelar Talk.",
					talkToSales: "Talk to Sttelar",
					closedMessage: "Hi! I want to start using Sttelar Talk.",
					submit: "Create account",
					haveAccount: "Already have an account?",
					login: "Sign in",
					sent: "We sent a link to {{email}}. Open it to confirm your email.",
					closedTitle: "Sign-up closed",
					closedText: "New account sign-up is closed right now. Contact Sttelar to get started.",
					limited: "Too many attempts from here right now. Try again in {{minutes}} min.",
					fixEmail: "Fix email",
					legalPrefix: "I have read and accept the",
					legalTerms: "Terms of Use",
					legalAnd: "and the",
					legalPrivacy: "Privacy Policy",
					errors: {
						name: "Enter your full name."
					}
				},
				forgot: {
					title: "Forgot your password?",
					subtitle: "Enter your account email. We'll send a link to create a new password.",
					submit: "Send link",
					sent: "If there is an account for {{email}}, the link will arrive shortly. It is valid for 1 hour."
				},
				reset: {
					title: "Create a new password",
					password: "New password",
					confirm: "Repeat the new password",
					submit: "Save new password",
					mismatch: "The two passwords don't match.",
					invalid: "It was already used or has expired. Ask for a new one to create your password.",
					newLink: "Ask for a new link",
					doneTitle: "Password changed",
					done: "Done. Use the new password to sign in. For security, your other sessions were ended."
				},
				verify: {
					failedTitle: "Couldn't confirm right now",
					retry: "Try again",
					checking: "Confirming your email...",
					okTitle: "Email confirmed",
					ok: "Your email is confirmed. Enter your password to sign in. Don't remember it or didn't create this account? Use \"Forgot your password?\".",
					otherAccount: "The email {{email}} is confirmed. You are still signed in as {{current}}.",
					goToApp: "Go to the app",
					invalid: "It was already used, has expired or was replaced by a newer one. Ask for another below.",
					resend: "Send a new link",
					resent: "If the email still needs confirming, we sent a new link."
				},
				banner: {
					text: "Confirm your email to connect WhatsApp, turn on the AI, invite people, subscribe and create links. We sent the link to {{email}}.",
					sent: "We sent a new link to {{email}}.",
					close: "Close notice"
				},
				toasts: {
					success: "Login successful!",
				},
				token: "Token",
			},
			connections: {
				status: {
					connected: "Connected",
					disconnected: "Disconnected",
					opening: "Connecting",
					starting: "Starting…",
					qrcode: "QR Code",
					scanQr: "Scan the QR Code",
					timeout: "Timed out",
					failed: "Connection failed",
					stopped: "Connection stopped",
					pairing: "Pairing",
					pending: "Pending",
				},
				adminTools: {
					tooltip: "Super Admin tools",
					eyebrow: "Super Admin · Connection tools",
					close: "Close",
					provider: {
						waha: "WhatsApp Web",
						"meta-api": "WhatsApp Business API",
						livechat: "LiveChat",
					},
					nav: {
						tickets: "Delete conversations",
						contacts: "Delete contacts",
						resolve: "Resolve conversations",
						history: "History",
						note: "Deletions can't be undone. Everything is recorded in the history under your user.",
					},
					status: {
						open: "In service",
						pending: "Waiting",
						closed: "Resolved",
					},
					period: {
						today: "Today",
						"7d": "Last 7 days",
						"30d": "Last 30 days",
						custom: "Custom",
						all: "All time",
						from: "From",
						to: "To",
						customRange: "{{from}} to {{to}}",
					},
					summary: {
						withGroups: "with groups",
						withoutGroups: "without groups",
					},
					cleanup: {
						tickets: {
							intro: "Deletes this connection's conversations along with their messages and files. Contacts are kept.",
							statusTitle: "Conversation status",
							periodTitle: "Conversation start",
							periodHint: "Uses the date the conversation started.",
							includeGroups: "Include group conversations",
							pickStatus: "select at least one status",
							note: "Runs in the background, in batches. Conversations that change after confirmation (new message, reopened, transferred) are kept. This cannot be undone.",
							submit: "Delete {{n}} conversation",
							submit_plural: "Delete {{n}} conversations",
						},
						contacts: {
							intro: "Deletes the contacts that have conversations on this connection, with all their conversations, messages and files — including conversations on other connections.",
							periodTitle: "Contact creation",
							periodHint: "Uses the date the contact was created.",
							includeGroups: "Include group contacts",
							note: "Runs in the background, in batches. Contacts with a new or changed conversation after confirmation are kept. This cannot be undone.",
							submit: "Delete {{n}} contact",
							submit_plural: "Delete {{n}} contacts",
						},
						resolve: {
							intro: "Resolves this connection's conversations that are in progress or waiting, with no closing message or rating request: the customer is not notified.",
							periodTitle: "Conversation start",
							periodHint: "Uses the date the conversation started.",
							includeGroups: "Include group conversations",
							note: "Runs in the background, in batches. Conversations that change after confirmation (new message, accepted, transferred) are kept. If the customer writes again, the conversation comes back as usual.",
							submit: "Resolve {{n}} conversation",
							submit_plural: "Resolve {{n}} conversations",
						},
						groupsFound: "{{n}} match these filters",
						previewTitle: "Preview",
						calculating: "Calculating…",
						computedAt: "Calculated at {{time}}",
						refresh: "Recalculate",
						range: "Period considered: {{from}} to {{to}}",
						rangeAll: "Period considered: all history",
						invalidDates: "Pick the start and end dates (the end date can't be after today).",
						recalculated: "The preview was recalculated — check the figures and confirm again.",
					},
					stats: {
						tickets: "Conversations",
						messages: "Messages",
						media: "Media files",
						contacts: "Contacts",
						contactsSplit: "only on this connection: {{exclusive}} · shared: {{shared}}",
						ticketsHere: "on this connection: {{n}}",
						messagesInGroups: "sent by them in groups: {{n}}",
					},
					warnings: {
						liveTickets: "Conversations in service or waiting disappear from agents' screens as soon as the deletion reaches them.",
						sharedContacts: "Contacts that also talk through other connections: {{shared}}. Their conversations there will be deleted too ({{tickets}} in total):",
						otherConnection: "{{name}} — conversations: {{tickets}}, in service or waiting: {{openOrPending}}",
						noConnection: "No connection",
						liveTicketsHere: "Conversations in service or waiting on this connection that will be deleted: {{n}}.",
					},
					confirm: {
						eyebrow: "Irreversible action",
						createTitle: "Confirm deletion",
						resumeTitle: "Resume operation",
						intro: "This will permanently delete:",
						resumeIntro: "The operation continues where it stopped, with the same filters:",
						password: "Your password",
						phraseBefore: "Type",
						phraseAfter: "to confirm",
						cancel: "Cancel",
						submit: "Delete permanently",
						resumeSubmit: "Resume",
						resolve: {
							eyebrow: "Bulk action",
							createTitle: "Confirm resolution",
							intro: "This will resolve, without notifying customers:",
							submit: "Resolve conversations",
						},
						summary: {
							tickets: "Conversations: {{n}} ({{filters}})",
							contacts: "Contacts: {{n}}, {{shared}} shared with other connections ({{filters}})",
							contactTickets: "Conversations: {{n}}, {{other}} of them on other connections",
							messages: "Messages: {{messages}} · media files: {{media}}",
							resumeProgress: "Already processed: {{processed}} of {{total}}",
							previewAt: "Figures from the preview at {{time}}",
						},
					},
					progress: {
						heading: {
							delete_tickets: "Conversation deletion",
							delete_contacts: "Contact deletion",
							resolve_tickets: "Conversation resolution",
						},
						count: "{{done}} of {{total}}",
						barLabel: "Deletion progress",
						deletedTickets: "Conversations deleted",
						deletedContacts: "Contacts deleted",
						skippedTickets: "Conversations kept",
						skippedContacts: "Contacts kept",
						messages: "Messages deleted",
						media: "Media files",
						queued: "Queued — starts as soon as the system's previous operation finishes.",
						canceling: "Finishing the current batch. What has been deleted won't come back.",
						skippedNoteTickets: "Conversations kept: they changed after confirmation or no longer matched the filters.",
						skippedNoteContacts: "Contacts kept: they had a new or changed conversation after confirmation.",
						failedHint: "The operation stopped because of an error. You can resume it from where it stopped.",
						startedBy: "Started by {{user}} on {{date}}",
						finishedAt: "finished on {{date}}",
						resumedBy: "resumed by {{user}} on {{date}}",
						cancelRequestedBy: "cancellation requested by {{user}} on {{date}}",
						cancel: "Cancel operation",
						cancelTitle: "Cancel the deletion?",
						cancelMessage: "The batch in progress finishes and the operation stops. What has been deleted won't come back.",
						resume: "Resume",
						newCleanup: "New deletion",
						resolve: {
							barLabel: "Resolution progress",
							processed: "Conversations resolved",
							canceling: "Finishing the current batch. Conversations already resolved stay resolved.",
							cancelTitle: "Cancel the resolution?",
							cancelMessage: "The batch in progress finishes and the operation stops. Conversations already resolved stay resolved.",
							newCleanup: "New resolution",
						},
						keepRunning: "Back",
					},
					statusLabel: {
						pending: "Queued",
						running: "Running",
						cancel_requested: "Canceling",
						canceled: "Canceled",
						completed: "Completed",
						failed: "Failed",
						denied: "Denied",
					},
					history: {
						intro: "Everything done on this connection with the Super Admin tools, including denied attempts.",
						empty: "No operations on this connection yet.",
						loadMore: "Load more",
						by: "{{user}} · {{date}}",
						countsTickets: "Conversations: {{processed}} of {{total}}",
						countsContacts: "Contacts: {{processed}} of {{total}}",
						detailsTickets: "kept: {{skipped}} · messages: {{messages}}",
						detailsContacts: "kept: {{skipped}} · messages: {{messages}}",
						detailsResolve: "kept: {{skipped}}",
						denied: {
							ERR_INVALID_PASSWORD: "Wrong password",
							ERR_INVALID_CONFIRMATION_PHRASE: "Wrong phrase",
						},
						operation: {
							delete_tickets: "Delete conversations",
							delete_contacts: "Delete contacts",
							resolve_tickets: "Resolve conversations",
						},
					},
					errors: {
						generic: "Couldn't complete. Please try again.",
					},
				},
				passkey: {
					pill: "Passkey",
					pillConfirm: "Confirm passkey",
					finish: "Finish passkey",
				},
				livechat: {
				  tooltip: "LiveChat",
				  active: "Active",
				  inactive: "Inactive",
				  copyCode: "Copy code",
				  preview: "Preview",
				  enable: "Enable widget",
				  disable: "Disable widget",
				  codeCopied: "Installation code copied!",
				  enabled: "Widget enabled",
				  disabled: "Widget disabled",
				},
				title: "Connections",
				toasts: {
					deleted: "WhatsApp connection deleted successfully!",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone.",
					disconnectTitle: "Disconnect",
					disconnectMessage: "Are you sure? You'll need to scan the QR Code again.",
				},
				buttons: {
					add: "Add Connection",
					disconnect: "disconnect",
					tryAgain: "Try again",
					qrcode: "QR CODE",
					newQr: "New QR CODE",
					connecting: "Connecting",
				},
				toolTips: {
					disconnected: {
						title: "Failed to start WhatsApp session",
						content: "Make sure your phone is connected to the internet and try again, or request a new QR Code",
					},
					qrcode: {
						title: "Waiting for QR Code scan",
						content: "Click the 'QR CODE' button and scan the QR Code with your phone to start the session",
					},
					connected: {
						title: "Connection established!",
					},
					timeout: {
						title: "Connection to phone was lost",
						content: "Make sure your phone is connected to the internet and WhatsApp is open, or click the 'Disconnect' button to get a new QR Code",
					},
				},
				table: {
					name: "Name",
					status: "Status",
					lastUpdate: "Last update",
					default: "Default",
					actions: "Actions",
					session: "Session",
				},
			},
			whatsappModal: {
				schedules: {
					title: "Business hours",
					description: "When on, this connection uses its own hours instead of the company's.",
					custom: "Custom hours",
					save: "Save hours",
					saveFirst: "Save the connection before setting custom hours.",
					saved: "Hours updated.",
					restored: "Company hours restored.",
				},
				title: {
					add: "Add WhatsApp",
					edit: "Edit WhatsApp",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					expiresTicket: {
						number: "Enter a valid number",
						integer: "Use integer values only",
						min: "Minimum is 0 (disabled)",
						max: "Maximum is 43200 minutes (30 days)",
					},
					expiresInactiveMessage: {
						max: "Message too long (max 1000 characters)",
					},
					greetingAcceptedMessage: {
						max: "Message too long (max 1000 characters)",
					},
					livechatTitle: {
					  required: "Widget title is required",
					  max: "Title too long (max 40 characters)",
					},
					livechatColor: {
					  invalid: "Invalid color (use #RRGGBB format)",
					},
					livechatDefaultCountryCode: {
					  invalid: "Use the +1 format",
					},
					livechatAllowedDomains: {
					  required: "Enter at least one allowed domain",
					},
				},
				livechat: {
				  sectionAppearance: "Widget appearance",
				  sectionBehavior: "Behavior",
				  installTitle: "Installation code",
				  installHelp: "Paste this code before the closing body tag of your website.",
				  preview: "Preview",
				  createdCopyCode: "Connection created! Copy the installation code below.",
				},
				tabs: {
					general: "General",
					messages: "Messages",
					assessments: "Assessments",
					integrations: "Integrations",
					schedules: "Business Hours",
				},
				form: {
					livechatTitle: "Widget title",
					livechatSubtitle: "Subtitle",
					livechatButtonText: "Button text",
					livechatColor: "Primary color",
					livechatWelcomeMessage: "Welcome message",
					livechatPhoneRequired: "Require phone",
					livechatDefaultCountryCode: "Default country code",
					livechatDefaultCountryCodeHelp: "E.g.: +55",
					livechatDefaultQueue: "Default queue",
					livechatNoQueue: "No queue",
					livechatAllowedDomains: "Allowed domains",
					livechatAllowedDomainsHelp: "One domain per line (e.g. www.yoursite.com). The widget only works on listed domains.",
					livechatEnabled: "Widget enabled",
					name: "Name",
					default: "Default",
					sendIdQueue: "Queue",
					timeSendQueue: "Redirect to queue in X minutes",
					queueRedirection: "Queue Redirection",
					outOfHoursMessage: "Out of office message",
					queueRedirectionDesc: "Select a queue for contacts without a queue to be redirected to",
					prompt: "Prompt",
					queue: "Transfer Queue",
					timeToTransfer: "Transfer after x (minutes)",
					metaInfo: "Provide the credentials from your Meta app to use the official WhatsApp Cloud API.",
					metaAccessToken: "Access Token",
					metaPhoneNumberId: "Phone Number ID",
					metaBusinessAccountId: "Business Account ID (optional)",
					metaAppId: "App ID (optional)",
					metaAppSecret: "App Secret",
					metaAppSecretHelp:
						"Facebook Developers → App settings → Basic → App Secret. Validates webhook signatures. If blank, webhooks are accepted without verification.",
					metaWebhookToken: "Webhook Verify Token",
					metaApiVersion: "Graph API Version",
					autoCloseTitle: "Auto close",
					autoCloseHelp: "Closes the chat when the client does not reply to the agent after the configured time. The message below is sent before closing.",
					expiresTicket: "Inactivity time",
					expiresTicketUnit: "minutes",
					expiresInactiveMessage: "Closure message",
					greetingMessage: "Greeting message",
					complationMessage: "Completion message",
					greetingAcceptedTitle: "Greeting on ticket accept",
					greetingAcceptedMessage: "Message on ticket accept",
					greetingAcceptedMessagePlaceholder: "e.g. {{ms}} *{{name}}*, my name is *{{userName}}* and I'll take it from here!",
					greetingAcceptedMessageHelp: "Sent when the agent accepts a pending ticket. Leave empty to use the default.",
					sendTransferQueueMessageTitle: "Message on queue transfer",
					sendTransferUserMessageTitle: "Message on agent transfer",
					sendTransferQueueAndUserMessageTitle: "Message on queue + agent transfer",
					integration: "Integration"
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "WhatsApp saved successfully.",
			},
			passkeyModal: {
				title: "Connect with Passkey",
				subtitle:
					"WhatsApp asked for a passkey to link this number. Signing happens in your browser, on the WhatsApp Web page — no password or credential passes through Sttelar.",
				extChecking: "Looking for the extension…",
				extPresent: "Extension detected",
				extAbsent:
					"To sign the passkey you need the official WAHA browser extension. It opens WhatsApp Web, you confirm with biometrics/PIN, and you're done.",
				installChrome: "Install on Chrome",
				installFirefox: "Install on Firefox",
				recheck: "I installed it, check again",
				signButton: "Sign with passkey",
				signing: "Opening WhatsApp Web…",
				submitting: "Submitting signature…",
				signHintTab:
					"A WhatsApp Web tab was opened. Click the button and confirm with your biometrics/PIN to sign.",
				fallbackToggle: "Can't install the extension? Use the console (DevTools)",
				fallbackTitle: "Sign via the browser console",
				fallbackStep1: "Open web.whatsapp.com (logged in) and press F12 → Console tab.",
				fallbackStep2: "Paste the script below, hit Enter and confirm with your biometrics/PIN.",
				fallbackStep3: "The signature is copied automatically. Paste it in the field below and submit.",
				copyScript: "Copy script",
				copied: "Copied!",
				pasteLabel: "Paste the signature here",
				pastePlaceholder: '{"id":"...","rawId":"...","type":"public-key","response":{...}}',
				submitAssertion: "Submit signature",
				confirmTitle: "Confirm the code on your phone",
				confirmSubtitle:
					"WhatsApp shows a code on the primary device. Check that it matches the number below and confirm.",
				confirmCodeLabel: "Code",
				confirmLoadingCode: "Fetching the code…",
				confirmButton: "I confirmed on my phone",
				confirming: "Confirming…",
				doneTitle: "Connected!",
				doneSubtitle: "The connection was linked successfully via passkey.",
				close: "Close",
				errors: {
					sign: "Could not sign the passkey. Please try again.",
					confirm: "Could not confirm. Please try again.",
					failed: "WhatsApp rejected the pairing. Generate a new QR and try again.",
					assertionParse: "The pasted signature is invalid. Copy the full JSON from the console.",
				},
			},
			qrCodeModal: {
				title: "Use your WhatsApp:",
				steps: {
					one: "1 - Open WhatsApp on your phone",
					two: {
						partOne: "2 - Tap More options on Android",
						partTwo: "or Settings",
						partThree: "on iPhone",
					},
					three: "3 - Tap Linked Devices and then Link a Device",
					four: "4 - Point your phone at this screen to capture the QR Code",
				},
				waiting: "Waiting for QR Code scan",
				tabs: {
					qr: "QR Code",
					pairing: "Pairing code",
				},
				pairing: {
					title: "Link with phone number",
					phoneLabel: "Phone number with country and area code",
					phonePlaceholder: "e.g. 15551234567",
					generate: "Generate code",
					generating: "Generating...",
					instructions: "On your phone's WhatsApp: Linked Devices → Link with phone number → enter the code below.",
					copy: "Copy",
					copied: "Copied!",
					invalidPhone: "Enter a valid phone number with country code.",
				},
			},
			qrCode: {
				message: "Scan the QR Code to start the session",
			},
			contacts: {
				title: "Contacts",
				toasts: {
					deleted: "Contact deleted successfully!",
					deletedAll: "All contacts deleted successfully!",
				},
				searchPlaceholder: "Search...",
				confirmationModal: {
					deleteTitle: "Delete ",
					deleteAllTitle: "Delete All",
					importTitle: "Import contacts",
					deleteMessage: "Are you sure you want to delete this contact? All related tickets will be lost.",
					deleteAllMessage: "Are you sure you want to delete all contacts? All related tickets will be lost.",
					importMessage: "Do you want to import all phone contacts?",
				},
					buttons: {
						import: "Import Contacts",
						add: "Add Contact",
						export: "Export Contacts",
						delete: "Delete All Contacts",
						startConversation: "Start WhatsApp conversation",
						edit: "Edit contact",
						deleteSingle: "Delete contact",
					},
				table: {
					name: "Name",
					whatsapp: "WhatsApp",
					email: "Email",
					actions: "Actions",
				},
			},
			contactImportModal: {
				title: "Contact Spreadsheet",
				labels: {
					import: "Import contacts",
					result: "results",
					added: "Added",
					savedContact: "Contact saved",
					errors: "Errors",
				},
				buttons: {
					download: "Download template spreadsheet",
					import: "Import contacts",
					background: "Continue in background",
					close: "Close",
				},
				progress: {
					importing: "Importing {{processed}} of {{total}} contacts...",
					success: "{{count}} imported successfully",
					errors: "{{count}} with errors",
					complete: "Import completed",
				},
			},
			queueIntegrationModal: {
				title: {
					add: "Add project",
					edit: "Edit project",
				},
				form: {
					id: "ID",
					type: "Type",
					name: "Name",
					projectName: "Project Name",
					language: "Language",
					jsonContent: "JsonContent",
					urlN8N: "URL",
					typebotSlug: "Typebot - Slug",
					typebotExpires: "Time in minutes to expire a conversation",
					typebotKeywordFinish: "Keyword to finish ticket",
					typebotKeywordRestart: "Keyword to restart flow",
					typebotRestartMessage: "Message when restarting conversation",
					typebotUnknownMessage: "Invalid option message",
					typebotDelayMessage: "Interval (ms) between messages",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
					test: "Test Bot",
				},
				messages: {
					testSuccess: "Integration tested successfully!",
					addSuccess: "Integration added successfully.",
					editSuccess: "Integration edited successfully.",
				},
			},
			sideMenu: {
				name: "Initial Side Menu",
				note: "If enabled, the side menu will start closed",
				options: {
					enabled: "Open",
					disabled: "Closed",
				},
			},
			promptModal: {
				form: {
					name: "Name",
					prompt: "Prompt",
					model: "Model",
					max_tokens: "Maximum Tokens in response",
					temperature: "Temperature",
					apikey: "API Key",
					max_messages: "Maximum messages in History",
				},
				formErrors: {
					name: {
						short: "Name is too short",
						long: "Name is too long",
						required: "Name is required",
					},
					prompt: {
						short: "Prompt is too short",
						required: "Describe the training for Artificial Intelligence",
					},
					modal: {
						required: "Enter the desired model for the Prompt",
					},
					maxTokens: {
						required: "Enter the maximum number of tokens in the response",
					},
					temperature: {
						required: "Enter the temperature",
					},
					apikey: {
						required: "Enter the API Key",
					},
					queueId: {
						required: "Enter the queue",
					},
					maxMessages: {
						required: "Enter the maximum number of messages in history",
					},
				},
				success: "Prompt saved successfully!",
				setor: "Enter the sector",
				title: {
					add: "Add Prompt",
					edit: "Edit Prompt",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
			},
			prompts: {
				title: "Prompts",
				table: {
					name: "Name",
					queue: "Sector/Queue",
					max_tokens: "Maximum Response Tokens",
					actions: "Actions",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone!",
				},
				buttons: {
					add: "Add Prompt",
				},
			},
			contactModal: {
				title: {
					add: "Add Contact",
					edit: "Edit Contact",
				},
				form: {
					mainInfo: "Contact Information",
					basicInfo: "Basic Information",
					complementaryInfo: "Additional Details",
					extraInfo: "Additional Information",
					name: "Name",
					number: "WhatsApp Number",
					email: "Email",
					extraName: "Field Name",
					extraValue: "Value",
					whatsapp: "Source Connection: ",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					phone: {
						short: "Number is too short",
						long: "Number is too long",
					},
					email: {
						invalid: "Invalid email",
					},
				},
				buttons: {
					addExtraInfo: "Add Information",
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Contact saved successfully.",
			},
			queueModal: {
				title: {
					add: "Add Queue",
					edit: "Edit Queue",
				},
				form: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					color: "Color",
					colorShort: "Short color",
					colorLong: "Long color",
					greetingMessage: "Greeting message",
					complationMessage: "Completion message",
					outOfHoursMessage: "Out of office message",
					ratingMessage: "Rating message",
					token: "Token",
					orderQueue: "Queue order (Bot)",
					integrationId: "Integration",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				toasts: {
					success: "Queue saved successfully.",
					info: "Click save to register changes",
				},
				tabs: {
					queueData: "Queue Data",
					attendanceTime: "Service Hours",
				},
			},
			userModal: {
				loading: "Loading user data...",
				title: {
					add: "Add User",
					edit: "Edit User",
				},
				tabs: {
					personal: "Personal info",
					access: "Access",
					service: "Service",
				},
				form: {
					name: "Name",
					email: "Email",
					cpf: "CPF",
					password: "Password",
					optionalSuffix: "· optional",
					passwordKeepHint: "Leave blank to keep the current password",
					allTicketSwitchTitle: "Alerts for conversations without a queue",
					allTicketSwitchDescription:
						"Also receives the alerts for conversations that arrive without a queue. Who sees each conversation is set in Settings → Conversations.",
					typingSwitchTitle: "Show “typing…” to the customer",
					typingSwitchDescription: "While you write, the customer sees “typing…” on WhatsApp.",
					addQueue: "Add queue",
					addAllQueues: "Add all queues",
					noQueuesSelected: "No queues selected",
					noQueueAvailable: "No queues available",
					noConnectionAvailable: "No connections available",
					maxCompanies: "Max. companies",
					maxCompaniesHelper: "How many companies this user can serve",
					groupCompanies: "Group companies",
					changePhoto: "Change photo",
					profile: "Profile",
					jobRole: "Job role",
					noJobRole: "No role",
					operationalType: "Operational type",
					customizePermissions: "Customize permissions",
					countsInIndicators: "Counts in service metrics",
					countsInIndicatorsHelp: "When off, the person is left out of the service numbers.",
					attendant: "Attendant",
					supervisor: "Supervisor",
					whatsapp: "Default Connection",
					noWhatsapp: "No connection",
					queues: "User Queues",
					profileImage: "Change Photo",
					uploadPhoto: "Upload Photo",
					allTicket: "Queueless Ticket [Invisible]",
					allTicketEnabled: "Enabled",
					allTicketDesabled: "Disabled",
					accessScope: "Access Scope *",
					accessScopeFull: "Full (Support + Internal)",
					accessScopeAttendance: "Support Only (Tickets, WhatsApp)",
					accessScopeInternal: "Internal Only (Chat, CRM)",
					internalLockedNotice: "Internal Chat is not included in your plan. Upgrade in Settings → Billing to unlock it.",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
						long: "Name is too long",
					},
					password: {
						short: "Password is too short",
						long: "Password is too long",
					},
					email: {
						required: "Email is required",
						invalid: "Invalid email",
					},
				},
				labels: {
					liberations: "Releases",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					saveChanges: "Save changes",
					cancel: "Cancel",
				},
				success: "User saved successfully.",
			},
			scheduleModal: {
				title: {
					add: "New Schedule",
					edit: "Edit Schedule",
				},
				form: {
					body: "Message",
					contact: "Contact",
					sendAt: "Schedule Date",
					sentAt: "Send Date",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Schedule saved successfully.",
			},
			tagModal: {
				title: {
					add: "New Tag",
					edit: "Edit Tag",
				},
				form: {
					name: "Name",
					color: "Color",
				},
				buttons: {
					okAdd: "Add",
					okEdit: "Save",
					cancel: "Cancel",
				},
				success: "Tag saved successfully.",
			},
			chat: {
				toasts: {
					fillTitle: "Please fill in the conversation title.",
					fillUser: "Please select at least one user.",
				},
				modal: {
					title: "Conversation",
					titleField: "Title",
				},
				confirm: {
					title: "Delete Conversation",
					message: "This action cannot be undone, confirm?",
				},
				chats: "Chats",
				messages: "Messages",
				noTicketMessage: "Select a ticket to start chatting.",
				buttons: {
					close: "Close",
					save: "Save",
					new: "New",
					newChat: "New",
				},
			},
			uploads: {
				titles: {
					titleUploadMsgDragDrop: "DRAG AND DROP FILES IN THE FIELD BELOW",
					titleFileList: "File List",
				},
			},
			ticketsManager: {
				buttons: {
					newTicket: "New",
				},
			},
			ticketsQueueSelect: {
				noQueue: "No queue",
			},
			tickets: {
				splitter: {
					label: "Resize conversation list",
					hint: "Drag to adjust the width. Use the arrow keys or double-click to reset.",
				},
				toolbar: {
					menu: "Conversation options",
					unread: "Unread",
					filters: "Filters",
					search: "Search tickets",
				},
				handledByAi: "Handled by AI: {{name}}",
				toasts: {
					deleted: "The service you were in has been deleted.",
					unauthorized: "Access not allowed",
				},
				filters: {
					user: "Filter by users",
					tags: "Filter by tags",
					connection: "Filter by connection",
					queues: "Filter by queues",
					emptyQueueWarning: "Select at least one queue (or 'No queue') to apply the filter.",
					tagsPlaceholder: "Filter by tags",
					tagsPopoverTitle: "Tags",
					tagsSearchPlaceholder: "Search",
					noTagsOption: "No tags",
					noTagsFound: "No tag found",
					singleSelected: "selected",
					multipleSelected: "selected",
					showingAll: "Showing all items",
					selectAll: "Apply all",
					queuesPopoverTitle: "Queues",
					queuesSearchPlaceholder: "Search",
					noQueuesFound: "No queue found",
					noQueueSelected: "No queue selected — pick at least one",
					clear: "Clear",
					loadingTags: "Loading tags...",
				},
				notification: {
					message: "Message from",
				},
				tabs: {
					open: { title: "Open" },
					groups: { title: "Groups" },
					closed: { title: "Resolved" },
					search: { title: "Search" },
				},
				search: {
					placeholder: "Search tickets and messages",
				},
				buttons: {
					showAll: "All",
				},
			},
			transferTicketModal: {
				title: "Transfer Ticket",
				fieldLabel: "Type to search users",
				fieldUserLabel: "Agent",
				fieldQueueLabel: "Transfer to queue",
				fieldQueuePlaceholder: "Select a queue",
				userPlaceholder: "Select an agent",
				userPopoverTitle: "Agents",
				userSearchPlaceholder: "Search agents",
				clearUserOption: "Clear selection",
				noUsersFound: "No agent found",
				showingAllUsers: "Showing all agents",
				noOptions: "No user found with that name",
				noSelection: "Select an agent or a queue to transfer.",
				queuePopoverTitle: "Queues",
				queueSearchPlaceholder: "Search queues",
				noQueueOption: "No queue",
				noQueuesFound: "No queue found",
				singleSelected: "item selected",
				showingAll: "Showing all queues",
				buttons: {
					ok: "Transfer",
					cancel: "Cancel",
				},
			},
			ticketsList: {
				empty: {
					openTitle: "All quiet here!",
					openDescription: "You have no conversations in progress at the moment.",
					openAllDescription: "There are no conversations in progress at the moment.",
					pendingTitle: "Queue cleared. Nice!",
					pendingDescription: "No conversations are waiting for service. Everything is up to date!",
					groupsTitle: "No group conversations",
					groupsDescription: "There are no group conversations in progress at the moment.",
					pendingCount: "{{count}} conversation waiting for service",
					pendingCount_plural: "{{count}} conversations waiting for service",
					pendingCountMore: "{{count}} or more conversations waiting for service",
					viewPending: "View pending",
					searchTitle: "No conversations found",
					searchDescription: "We found no conversations for “{{term}}”. Try another name, number or term.",
					searchFilteredDescription: "We found no conversations for “{{term}}” with the current filters. Try another term or adjust the filters.",
					filteredTitle: "No conversations found",
					filteredDescription: "Try changing the filters to see more conversations.",
					adjustFilters: "Adjust filters",
				},
				pendingHeader: "Waiting",
				pendingHeaderTab: "Pending",
				assignedHeader: "In Progress",
				noTicketsTitle: "Nothing here!",
				noTicketsMessage: "No service found with this status or search term",
				buttons: {
					accept: "Accept",
					closed: "Finish",
					reopen: "Reopen",
				},
			},
			ticketsListItem: {
				tooltip: {
					chatbot: "Chatbot",
					peek: "Peek Conversation",
					unreplied: "No reply for over 10 minutes",
				},
				noQueue: "NO QUEUE",
				viewLocation: "View location on map",
			},
			ticketAdvanced: {
				selectTicket: "Select Ticket",
				ticketNav: "Ticket",
				attendanceNav: "Services",
			},
			newTicketModal: {
				title: "Choose a contact",
				searchPlaceholder: "Search by name or phone",
				tagsPlaceholder: "Tags",
				tagsPopoverTitle: "Tags",
				tagsSearchPlaceholder: "Search",
				showingAll: "Showing all items",
				singleSelected: "item selected",
				multipleSelected: "items selected",
				clear: "Clear",
				noTagsOption: "No tags",
				noTagsFound: "No tags found",
				ticketTitle: "Choose a ticket",
				createTicket: "Create ticket",
				existingContactPlaceholder: "Search for an existing contact",
				contactSearchHint: "Type at least 3 characters to search for a contact",
				queuePlaceholder: "Select a queue",
				allQueuesOption: "All queues",
				queuePopoverTitle: "Queues",
				queueSearchPlaceholder: "Search queues",
				noQueueOption: "Create without queue",
				noQueuesFound: "No queue found",
				connectionPlaceholder: "Select a connection",
				allConnectionsOption: "All connections",
				connectionPopoverTitle: "Connections",
				loadingConnections: "Loading connections...",
				noConnectionsAvailable: "No connection available",
				newContact: "New contact",
				emptyState: "No contact found",
				emptyTicketsState: "No ticket found",
				loadingTags: "Loading tags...",
				buttons: {
					ok: "Save",
					cancel: "Cancel",
				},
			},
			locationPreview: {
				button: "Preview",
				openInMaps: "Open in Maps",
				showMap: "Show on map",
				hideMap: "Hide map",
			},
			mainDrawer: {
				listItems: {
					dashboard: "Dashboard",
					dashboardGeral: "Group Dashboard",
					connections: "Connections",
					tickets: "Services",
					quickMessages: "Quick Responses",
					tasks: "Tasks",
					contacts: "Contacts",
					queues: "Queues & Chatbot",
					tags: "Tags",
					academy: "Training",
					manageAcademy: "Manage Training",
					administration: "Administration",
					users: "Users",
					settings: "Settings",
					helps: "Help",
					messagesAPI: "API",
					schedules: "Schedules",
					campaigns: "Campaigns",
					flows: "Flows",
					annoucements: "Announcements",
					chats: "Internal Chat",
					financeiro: "Financial",
					files: "File List",
					prompts: "IA",
					queueIntegration: "Integrations",
					metaTemplates: "Meta Templates",
					smartApproaches: "Smart follow-ups",
					integrations: "Integrations",
					workOrders: "Work orders",
				},
				appBar: {
					refresh: "Reload page",
					notRegister: "No notifications",
					language: "Language",
					jobRole: "Job role",
					noJobRole: "No role",
					appearance: "Appearance",
					notificationVolume: "Notification Sound",
					greeting: {
						hello: "Hello",
						welcome: "Welcome to",
						active: "Active until",
					},
					user: {
						profile: "Profile",
						security: "Account security",
						logout: "Logout",
					},
				},
			},
			queueIntegration: {
				title: "Integrations",
				table: {
					id: "ID",
					type: "Type",
					name: "Name",
					projectName: "Project Name",
					language: "Language",
					lastUpdate: "Last update",
					actions: "Actions",
				},
				buttons: {
					add: "Add Project",
				},
				searchPlaceholder: "Search...",
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone! It will be removed from linked queues and connections",
				},
			},
			files: {
				title: "File List",
				table: {
					name: "Name",
					contacts: "Contacts",
					actions: "Action",
				},
				toasts: {
					deleted: "List deleted successfully!",
					deletedAll: "All lists deleted successfully!",
				},
				buttons: {
					add: "Add",
					deleteAll: "Delete All",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteAllTitle: "Delete All",
					deleteMessage: "Are you sure you want to delete this list?",
					deleteAllMessage: "Are you sure you want to delete all lists?",
				},
			},
			messagesAPI: {
				title: "API",
				labels: {
					doc: "Documentation for sending messages",
					method: "Sending Methods",
					textMessage: "Text Message",
					mediaMessage: "Media Message",
					instructions: "Instructions",
					observations: "Important notes",
					before1: "Before sending messages, you need to register the token linked to the connection that will send the messages.",
					before2: "To register, access the 'Connections' menu, click the edit button of the connection and enter the token in the appropriate field.",
					numberDescription: "The sending number should not have masks or special characters and must be composed of:",
					countryCode: "Country Code",
					number: "Number",
					textMessage2: "1. Text Messages",
					textMessageInstructions: "Below is the list of information needed to send text messages:",
					method2: "Method",
					e: "and",
					tests: "Send Test",
					mediaMessage2: "2. Media Messages",
				},
				textMessage: {
					number: "Number",
					body: "Message",
					token: "Registered Token",
				},
				mediaMessage: {
					number: "Number",
					body: "File name",
					media: "File",
					token: "Registered Token",
				},
				toasts: {
					unauthorized: "This company doesn't have permission to access this page! We are redirecting you.",
					success: "Message sent successfully!",
				},
				buttons: {
					send: "Send",
				},
			},
			notifications: {
				noTickets: "No notifications.",
			},
			quickMessages: {
				title: "Quick Responses",
				searchPlaceholder: "Search...",
				noAttachment: "No attachment",
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action is irreversible! Do you want to proceed?",
				},
				buttons: {
					add: "Add",
					attach: "Attach File",
					cancel: "Cancel",
					edit: "Edit",
				},
					toasts: {
						success: "Shortcut added successfully!",
						deleted: "Shortcut removed successfully!",
					},
					validation: {
						shortcodeRequired: "Please enter a shortcut.",
						messageRequired: "Please enter a quick response message.",
					},
					dialog: {
						title: "Quick Message",
						shortcode: "Shortcut",
					message: "Response",
					save: "Save",
					cancel: "Cancel",
					geral: "Allow edit",
					add: "Add",
					edit: "Edit",
					visao: "Allow view",
				},
				table: {
					shortcode: "Shortcut",
					message: "Message",
					actions: "Actions",
					mediaName: "File Name",
					status: "Status",
				},
			},
			leadSettings: {
				tabs: {
					apiTokens: "API Tokens",
					leadStatuses: "Card statuses",
				},
				tokens: {
					newToken: "New Token",
					webhookTitle: "Webhook URL for N8N",
					webhookHint: "Send the header:",
					copyUrl: "Copy URL",
					urlCopied: "Webhook URL copied!",
					emptyTitle: "No tokens created",
					emptyDescription: "Create an API token to integrate with N8N or other platforms",
					createdAt: "Created at:",
					deleteTitle: "Delete API Token",
					deleteMessage: "Are you sure you want to delete the token \"{name}\"? This action cannot be undone and all integrations using this token will stop working.",
					deleted: "Token deleted successfully",
				},
				statuses: {
					newStatus: "New Status",
					emptyTitle: "No statuses created",
					emptyDescription: "Create custom statuses to organize your cards",
					default: "Default",
					pending: "Pending",
					completed: "Completed",
					order: "Order",
					deleteTitle: "Delete Status",
					deleteMessage: "Are you sure you want to delete the status \"{name}\"? Cards with this status will be moved to the default status.",
					deleted: "Status deleted successfully",
				},
			},
			messageVariablesPicker: {
				label: "Available Variables",
				vars: {
					contactFirstName: "First Name",
					contactName: "Name",
					greeting: "Greeting",
					protocolNumber: "Protocol",
					date: "Date",
					hour: "Hour",
				},
			},
			contactLists: {
				title: "Contact Lists",
				table: {
					name: "Name",
					contacts: "Contacts",
					actions: "Actions",
				},
				buttons: {
					add: "New List",
				},
				dialog: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					company: "Company",
					okEdit: "Edit",
					okAdd: "Add",
					add: "Add",
					edit: "Edit",
					cancel: "Cancel",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					deleted: "Record deleted",
					success: "Operation completed successfully",
				},
			},
			contactListItems: {
				title: "Contacts",
				searchPlaceholder: "Search",
				buttons: {
					add: "New",
					lists: "Lists",
					import: "Import",
				},
				download: "Click here to download example spreadsheet.",
				dialog: {
					name: "Name",
					nameShort: "Short name",
					nameLong: "Long name",
					nameRequired: "Name is required",
					number: "Number",
					numberShort: "Short number",
					numberLong: "Long number",
					whatsapp: "WhatsApp",
					email: "Email",
					emailInvalid: "Invalid email",
					okEdit: "Edit",
					okAdd: "Add",
					add: "Add",
					edit: "Edit",
					cancel: "Cancel",
				},
				table: {
					name: "Name",
					number: "Number",
					whatsapp: "WhatsApp",
					email: "Email",
					actions: "Actions",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
					importMessage: "Do you want to import contacts from this spreadsheet? ",
					importTitle: "Import",
				},
				toasts: {
					deleted: "Record deleted",
				},
			},
			campaigns: {
				title: "Campaigns",
				searchPlaceholder: "Search",
				report: {
					title: "Report of",
					title2: "Campaign",
					of: "of",
					validContacts: "Valid contacts",
					delivered: "Delivered",
					connection: "Connection",
					contactList: "Contact List",
					schedule: "Schedule",
					conclusion: "Conclusion",
				},
				config: {
					interval: "Intervals",
					randomInterval: "Random Send Interval",
					biggerInterval: "Larger Interval After",
					greaterInterval: "Greater Send Interval",
					noInterval: "No Interval",
					second: "second",
					seconds: "seconds",
					notDefined: "Not defined",
					addVariable: "Add Variable",
					save: "Save Settings",
					shortcut: "Shortcut",
					content: "Content",
					close: "Close",
					add: "Add",
				},
				buttons: {
					add: "New Campaign",
					contactLists: "Contact Lists",
				},
				status: {
					inactive: "Inactive",
					programmed: "Scheduled",
					inProgress: "In progress",
					canceled: "Canceled",
					finished: "Finished",
				},
				table: {
					name: "Name",
					whatsapp: "Connection",
					contactList: "Contact List",
					status: "Status",
					scheduledAt: "Schedule",
					completedAt: "Completed",
					confirmation: "Confirmation",
					actions: "Actions",
					notDefined: "Not defined",
					notDefined2: "Not defined",
					notScheduled: "Not scheduled",
					notConcluded: "Not concluded",
					stopCampaign: "Stop Campaign",
				},
				dialog: {
					new: "New Campaign",
					update: "Edit Campaign",
					readonly: "View Only",
					form: {
						name: "Name",
						nameShort: "Short name",
						nameLong: "Long name",
						helper: "Use variables like {name}, {number}, {email} or define custom variables.",
						nameRequired: "Name is required",
						message1: "Message 1",
						message2: "Message 2",
						message3: "Message 3",
						message4: "Message 4",
						message5: "Message 5",
						messagePlaceholder: "Message content",
						whatsapp: "Connection",
						status: "Status",
						scheduledAt: "Schedule",
						confirmation: "Confirmation",
						contactList: "Contact List",
						tagList: "Tag List",
						fileList: "File List",
					},
					buttons: {
						add: "Add",
						edit: "Update",
						okadd: "Ok",
						cancel: "Cancel Sends",
						restart: "Restart Sends",
						close: "Close",
						attach: "Attach File",
					},
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					configSaved: "Settings saved",
					success: "Operation completed successfully",
					cancel: "Campaign canceled",
					restart: "Campaign restarted",
					deleted: "Record deleted",
				},
			},
			announcements: {
				active: "Active",
				inactive: "Inactive",
				title: "Announcements",
				searchPlaceholder: "Search",
				high: "High",
				medium: "Medium",
				low: "Low",
				buttons: {
					add: "New Announcement",
					contactLists: "Announcement Lists",
				},
				table: {
					priority: "Priority",
					title: "Title",
					text: "Text",
					mediaName: "File",
					status: "Status",
					actions: "Actions",
				},
				dialog: {
					edit: "Edit Announcement",
					add: "New Announcement",
					update: "Edit Announcement",
					readonly: "View Only",
					form: {
						priority: "Priority",
						required: "Required field",
						title: "Title",
						text: "Text",
						mediaPath: "File",
						status: "Status",
					},
					buttons: {
						add: "Add",
						edit: "Update",
						okadd: "Ok",
						cancel: "Cancel",
						close: "Close",
						attach: "Attach File",
					},
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "This action cannot be undone.",
				},
				toasts: {
					success: "Operation completed successfully",
					deleted: "Record deleted",
					info: "This company doesn't have permission to access this page! We are redirecting you.",
				},
			},
			campaignsConfig: {
				title: "Campaign Settings",
			},
			queues: {
				title: "Queues & Chatbot",
				table: {
					id: "ID",
					name: "Name",
					color: "Color",
					greeting: "Greeting message",
					actions: "Actions",
					orderQueue: "Queue order (bot)",
				},
				buttons: {
					add: "Add queue",
				},
				emptyState: "No queues found",
				toasts: {
					success: "Queue deleted successfully.",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "Are you sure? This action cannot be undone! Services in this queue will continue to exist but won't have any queue assigned.",
				},
			},
			queueSelect: {
				inputLabel: "Queues",
			},
			users: {
				title: "Users",
				table: {
					id: "ID",
					name: "Name",
					email: "Email",
					profile: "Profile",
					actions: "Actions",
					admin: "Admin",
					user: "User",
				},
				buttons: {
					add: "Add user",
				},
				invites: {
					button: "Invite by email",
					title: "Invite by email",
					subtitle: "The person gets a link to create a password and join this account. The invite is valid for 7 days.",
					email: "Email",
					emailPlaceholder: "name@company.com",
					emailInvalid: "Enter a valid email.",
					profile: "Profile",
					license: "License",
					profiles: {
						user: "Agent",
						admin: "Administrator",
					},
					send: "Send invite",
					cancel: "Cancel",
					sent: "Invite sent to {{email}}.",
					pendingTitle: "{{count}} pending invite",
					pendingTitle_plural: "{{count}} pending invites",
					expiresIn: "expires in {{count}} day",
					expiresIn_plural: "expires in {{count}} days",
					expired: "expired",
					invitedBy: "sent by {{name}}",
					resend: "Resend",
					resent: "Invite resent to {{email}}.",
					revoke: "Delete",
					revokeTitle: "Delete the invite?",
					revokeText: "The link sent to {{email}} will stop working.",
					revokeConfirm: "Delete invite",
					revoked: "Invite deleted.",
				},
				emptyState: "No users found",
				toasts: {
					created: "User created successfully.",
					deleted: "User deleted successfully.",
					updated: "User updated successfully.",
				},
				confirmationModal: {
					deleteTitle: "Delete",
					deleteMessage: "All user data will be lost. Open services for this user will be moved to the queue.",
				},
			},
			todolist: {
				input: "New task",
				buttons: {
					add: "Add",
					save: "Save",
				},
			},
			helps: {
				title: "Help Center",
			},
			schedules: {
				title: "Schedules",
				confirmationModal: {
					deleteTitle: "Are you sure you want to delete this Schedule?",
					deleteMessage: "This action cannot be undone.",
				},
				table: {
					contact: "Contact",
					body: "Message",
					sendAt: "Schedule Date",
					sentAt: "Send Date",
					status: "Status",
					actions: "Actions",
				},
				messages: {
					date: "Date",
					time: "Time",
					event: "Event",
					allDay: "All Day",
					week: "Week",
					work_week: "Schedules",
					day: "Day",
					month: "Month",
					previous: "Previous",
					next: "Next",
					yesterday: "Yesterday",
					tomorrow: "Tomorrow",
					today: "Today",
					agenda: "Agenda",
					noEventsInRange: "No schedules in this period.",
					showMore: "more",
				},
				buttons: {
					add: "New Schedule",
				},
				toasts: {
					deleted: "Schedule deleted successfully.",
				},
			},
			tags: {
				title: "Tags",
				confirmationModal: {
					deleteTitle: "Are you sure you want to delete this Tag?",
					deleteMessage: "This action cannot be undone.",
					deleteAllMessage: "Are you sure you want to delete all Tags?",
					deleteAllTitle: "Delete All",
				},
				table: {
					name: "Name",
					color: "Color",
					tickets: "Tagged Records",
					actions: "Actions",
				},
				buttons: {
					add: "New Tag",
					deleteAll: "Delete All",
				},
				toasts: {
					deletedAll: "All Tags deleted successfully!",
					deleted: "Tag deleted successfully.",
				},
			},
			accessControl: {
				tabs: {
					people: "People",
					roles: "Access roles",
					history: "History",
				},
				tabsLabel: "Team and permissions sections",
				roles: {
					agent: "Agent",
					supervisor: "Supervisor",
					admin: "Administrator",
				},
				roleHelp: {
					agent: "Handles conversations and takes care of contacts.",
					supervisor: "Handles conversations and follows the team: reports, flows and mass sends, view only.",
					admin: "Everything, including the team, the company and permissions.",
				},
				licenses: {
					full: "Full",
					attendance: "Support only",
					internal: "Internal only",
				},
				scopes: {
					none: "None",
					own: "Only their own",
					queues: "From their queues",
					company: "Whole company",
				},
				groups: {
					tickets: "Customer service",
					contacts: "Contacts and service tools",
					reports: "Reports",
					settings: "Settings",
					team: "Team and access",
					company: "Company",
					marketing: "Marketing",
					automation: "Automation and AI",
					crm: "CRM and internal chat",
				},
				origin: {
					default: "Role default",
					granted: "Granted",
					denied: "Denied",
				},
				locks: {
					plan: "Not included in the plan",
					module: "Module not enabled",
					setting: "Turned off by Sttelar",
					license: "{{license}} license",
					admin_only: "Administrators only",
					actor: "You don't have this permission",
				},
				readOnly: {
					own: "You can't change your own access.",
					impersonating: "You can't change access while using \"Access as\" a user.",
					other_companies: "This person also belongs to another company that you don't manage.",
					above: "This person has more access than you.",
				},
				people: {
					search: "Search by name or email",
					allRoles: "All roles",
					allLicenses: "All licenses",
					customized: "With exceptions",
					columns: {
						person: "Person",
						role: "Role",
						customizations: "Exceptions",
						license: "License",
						queues: "Queues",
						lastAccess: "Last access",
					},
					grantedOne: "1 granted",
					grantedMany: "{{count}} granted",
					deniedOne: "1 denied",
					deniedMany: "{{count}} denied",
					noQueues: "No queues",
					never: "Never signed in",
					empty: "No one found.",
					open: "Open {{name}}'s access",
					pageRange: "{{from}}–{{to}} of {{total}}",
					previousPage: "Previous page",
					nextPage: "Next page",
				},
				panel: {
					back: "People",
					role: "Access role",
					restore: "Restore role default",
					restoreTitle: "Restore the role default?",
					restoreBody: "{{name}}'s exceptions are removed, and access goes back to the {{role}} default.",
					restoreConfirm: "Restore",
					restoreDirty: "Save or discard your changes first.",
					restored: "{{name}}'s access is back to the role default.",
					searchPermissions: "Search permission",
					onlyCustomized: "Only exceptions",
					onlySensitive: "Only sensitive",
					summary: "{{on}} of {{total}}",
					sensitive: "Sensitive",
					backToDefault: "Back to default",
					dependsOn: "Depends on: {{names}}",
					nothingFound: "No permission found.",
					discard: "Discard",
					save: "Save",
					confirmTitle: "Save {{name}}'s access?",
					confirmRole: "Role: {{from}} → {{to}}",
					confirmScope: "Conversations: {{from}} → {{to}}",
					confirmGains: "Will have",
					confirmLoses: "Will no longer have",
					confirmNothing: "Access stays the same: only the exceptions change.",
					saved: "{{name}}'s access saved.",
					reloaded: "Someone else changed this access. Showing the current one.",
					loadError: "We couldn't load this person's access.",
					previewError: "We couldn't check the changes right now. Please try again.",
				},
				rolesTab: {
					intro: "What each role has by default. Each person's exceptions are in the People tab.",
					permission: "Permission",
					included: "Included",
					notIncluded: "Not included",
				},
				history: {
					person: "Person",
					allPeople: "Everyone",
					loadingPeople: "Loading…",
					noPeople: "No one with that name.",
					morePeople: "Showing {{shown}} of {{total}}. Type to find someone else.",
					from: "From",
					to: "To",
					actions: {
						conversion: "Role set when permissions were introduced",
						user: {
							create: "Account created",
							update: "Role or license changed",
						},
						access: {
							overrides: "Exceptions changed",
							reset: "Role default restored",
						},
					},
					by: "by {{name}}",
					byAs: "by {{name}}, as {{as}}",
					support: "Sttelar support",
					system: "System",
					removed: "Deleted person",
					role: "Role: {{from}} → {{to}}",
					license: "License: {{from}} → {{to}}",
					granted: "Granted: {{name}}",
					denied: "Denied: {{name}}",
					cleared: "Back to default: {{name}}",
					empty: "No changes in this period.",
					loadMore: "Load more",
				},
				permissions: {
					tickets: {
						view: {
							title: "View and handle conversations",
							help: "List, open, reply, accept and reopen. The level sets which conversations.",
						},
						view_no_queue: {
							title: "See conversations without a queue",
							help: "Those not yet in any queue.",
						},
						view_groups: {
							title: "See group conversations",
							help: "WhatsApp groups.",
						},
						create: {
							title: "Start a conversation",
							help: "With a contact or a new number.",
						},
						take_over: {
							title: "Take over a colleague's conversation",
							help: "Take the conversation from another agent.",
						},
						transfer: {
							title: "Transfer conversations",
							help: "To a queue, agent or AI, or back to the queue.",
						},
						close: {
							title: "Close conversations",
							help: "Mark the conversation as resolved.",
						},
						manage_connection: {
							title: "Change a conversation's connection",
							help: "And sync the history.",
						},
						spy: {
							title: "Peek at conversations",
							help: "See without taking over.",
						},
						bulk_actions: {
							title: "Bulk actions",
							help: "Close and reassign many conversations at once.",
						},
						delete: {
							title: "Delete conversations",
							help: "Deletes the conversation and its messages.",
						},
					},
					messages: {
						manage_others: {
							title: "Delete or edit a colleague's message",
							help: "Messages sent by someone else.",
						},
					},
					notes: {
						manage_others: {
							title: "Manage colleagues' notes",
							help: "Edit someone else's and delete any note.",
						},
					},
					contacts: {
						edit: {
							title: "Create and edit contacts",
							help: "Name, number and contact details.",
						},
						import: {
							title: "Import contacts",
							help: "From a spreadsheet or from WhatsApp.",
						},
						wallet_manage: {
							title: "Set a contact's owner",
							help: "The customer portfolio.",
						},
						delete: {
							title: "Delete contacts",
							help: "The contact's conversations are deleted too.",
						},
					},
					tags: {
						manage: {
							title: "Manage tags",
							help: "Create, edit and delete.",
						},
					},
					quick_replies: {
						manage_company: {
							title: "Manage company quick replies",
							help: "The ones the whole team uses.",
						},
					},
					templates: {
						use: {
							title: "Send templates",
							help: "Templates approved by Meta.",
						},
						manage: {
							title: "Manage templates",
							help: "Create, edit and delete at Meta.",
						},
					},
					ai: {
						assist: {
							title: "Rewrite with AI",
							help: "Improve the text before sending.",
						},
					},
					reports: {
						view: {
							title: "Dashboard and metrics",
							help: "The company's numbers.",
						},
						view_group: {
							title: "Group metrics",
							help: "All units in the group.",
						},
					},
					departments: {
						manage: {
							title: "Departments",
							help: "Create them and choose who sees each one's numbers.",
						},
					},
					queues: {
						manage: {
							title: "Queues and chatbot",
							help: "Create and configure.",
						},
					},
					connections: {
						manage: {
							title: "Connections",
							help: "Configure, scan the QR code and pair.",
						},
					},
					files: {
						manage: {
							title: "File lists",
							help: "The files the chatbot sends.",
						},
					},
					schedules: {
						manage: {
							title: "Hours and holidays",
							help: "When the company is open.",
						},
					},
					settings: {
						manage: {
							title: "Service options",
							help: "And the company's modules.",
						},
					},
					team: {
						view: {
							title: "See the team",
							help: "With email, role and two-step verification.",
						},
						manage: {
							title: "Manage the team",
							help: "Add, edit and remove people; queues, connection and job title.",
						},
						manage_access: {
							title: "Manage permissions",
							help: "Each person's access role, license and exceptions.",
						},
						reset_credentials: {
							title: "Change someone's password and email",
							help: "And reset two-step verification.",
						},
						manage_companies: {
							title: "Link to other group companies",
							help: "Give access to other units.",
						},
					},
					company: {
						manage: {
							title: "Company details",
							help: "Name, language and setup guide.",
						},
						security: {
							manage: {
								title: "Company security",
								help: "Require two-step verification.",
							},
						},
					},
					billing: {
						manage: {
							title: "Plan and payment",
							help: "Billing and payment method.",
						},
					},
					api_tokens: {
						manage: {
							title: "API tokens",
							help: "Access for outside systems.",
						},
					},
					integrations: {
						manage: {
							title: "Integrations",
							help: "OpenAI, WooCommerce, Contele and SMS.",
						},
					},
					audiences: {
						view: {
							title: "See contact bases",
							help: "Bases, segments and lists.",
						},
						manage: {
							title: "Manage contact bases",
							help: "Upload bases and create segments and lists.",
						},
					},
					mass_send: {
						view: {
							title: "Follow mass sends",
							help: "Bulk messages.",
						},
						manage: {
							title: "Manage mass sends",
							help: "Create, pause and cancel; unsubscribes.",
						},
					},
					smart_approaches: {
						view: {
							title: "See smart follow-ups",
							help: "And their results.",
						},
						manage: {
							title: "Manage smart follow-ups",
							help: "Create and activate.",
						},
					},
					flows: {
						view: {
							title: "See flows",
							help: "And their results.",
						},
						edit: {
							title: "Edit flows",
							help: "Create, edit and upload files.",
						},
					},
					ai_agents: {
						view: {
							title: "See AI agents",
							help: "The list and each agent's details.",
						},
						manage: {
							title: "Configure AI agents",
							help: "Files, versions and testing.",
						},
					},
					work_orders: {
						view: {
							title: "See work orders",
							help: "And their sources.",
						},
						start_triage: {
							title: "Start a work order triage",
							help: "Sends a message to the customer.",
						},
					},
					triage: {
						configure: {
							title: "Configure triage",
							help: "Triage questions and rules.",
						},
						monitor: {
							title: "Follow triages",
							help: "The ones in progress.",
						},
					},
					crm: {
						use: {
							title: "Use the CRM",
							help: "Leads and columns.",
						},
						boards: {
							manage: {
								title: "Manage CRM boards",
								help: "Create and delete boards.",
							},
							share: {
								title: "Share boards by link",
								help: "Create links so people without a login can view a board, without changing anything.",
							},
						},
					},
					internal_chat: {
						use: {
							title: "Use the internal chat",
							help: "Talk with the team.",
						},
						moderate: {
							title: "Moderate the internal chat",
							help: "Create channels; pin or delete others' messages.",
						},
						invite: {
							title: "Invite to the internal chat",
							help: "The invite link creates a new account.",
						},
					},
				},
			},
			settings: {
				schedulesUpdated: "Schedules updated successfully.",
				success: "Settings saved successfully.",
				title: "Settings",
				groups: {
					account: "Your account",
					service: "Service",
					system: "System",
					company: "Company",
				},
				area: {
					autosave: "Changes are saved automatically.",
					saving: "Saving",
					saved: "Saved",
					saveError: "Couldn't save. Try again.",
					loadError: "Couldn't load the settings.",
					retry: "Try again",
					collapseMenu: "Collapse menu",
					expandMenu: "Show menu",
				},
				search: {
					label: "Search settings",
					placeholder: "Search settings",
					clear: "Clear search",
					open: "Search",
					empty: "Nothing found for “{{query}}”.",
					emptyHint: "Try another word, like “holiday” or “SMS”.",
					count: "{{count}} result(s)",
				},
				sections: {
					security: {
						keywords: "2fa, two-factor, authentication, verification, code, password, login, access, authenticator",
						title: "Security",
						description: "Protect access to your account with two-factor authentication.",
					},
					schedules: {
						keywords: "hours, business hours, schedule, opening, closing, out of hours, service",
						title: "Business hours",
						description: "When the company is open and the days it is closed.",
						fields: {
							weeklyHours: {
								keywords: "week, days, time, open, close, monday, saturday, sunday",
								title: "Weekly hours",
								description: "Days and times when the company is open. Connections with their own hours use theirs.",
							},
							exceptions: {
								keywords: "holiday, national holidays, special date, christmas, new year, carnival, day off, exception",
								title: "Holidays and special dates",
								description: "Replace the weekly hours on the chosen date, on every connection.",
							},
						},
					},
					conversations: {
						keywords: "who sees, visibility, privacy, colleagues' conversations, queue, sector",
						title: "Conversations",
						description: "Who sees the agents' conversations.",
						fields: {
							ticketVisibility: {
								keywords: "only their own, queues, sectors, whole company, see conversations",
								title: "Who sees the conversations",
								description:
									"Only their own: the agent's and those waiting in their queues. Their queues: also their colleagues'. Admins see all of them.",
								options: {
									own: "Only their own",
									queues: "Their queues",
									company: "The whole company",
								},
							},
							supervisorTicketVisibility: {
								keywords: "supervisor, supervisors, conversations, queues, whole company",
								title: "What Supervisors see",
								description: "Never less than agents.",
								options: {
									queues: "From their queues",
									company: "Whole company",
								},
							},
						},
					},
					"quick-replies": {
						keywords: "shortcut, canned responses, replies, share",
						title: "Quick replies",
						description: "Who sees the quick replies. They are created in the Quick replies menu.",
						fields: {
							quickMessagesScope: {
								keywords: "who sees, share, per agent, per company",
								title: "Who sees the replies",
								description: "Per agent: each one sees only their own. Company: everyone sees all of them.",
								options: {
									user: "Each agent their own",
									company: "The whole company",
								},
							},
						},
					},
					indicators: {
						keywords: "unanswered, waiting, response time, colors, alert, sla, indicator, art",
						title: "Indicators",
						description: "Alerts for unanswered conversations and response time colors.",
						groups: {
							noResponse: {
								title: "Unanswered conversations",
								description: "Conversations where the customer has been waiting for a reply for more than 10 minutes.",
							},
							responseTime: {
								title: "Response time",
								description: "Colors of the average response time on the dashboard and in reports.",
							},
							shortReplies: {
								title: "Short customer replies",
								description: "Messages like \"Ok\" and \"👍\" at the end of the conversation don't count in response time or as unanswered.",
							},
						},
						fields: {
							noResponseIndicator: {
								keywords: "alert, menu, counter, unanswered",
								title: "Menu alert",
								description: "Shows in the side menu how many conversations are unanswered.",
							},
							unrepliedClockIcon: {
								keywords: "clock, list, red, waiting",
								title: "Clock on the conversation list",
								description: "Shows a red clock next to the contact waiting for a reply.",
							},
							noResponseBusinessHours: {
								keywords: "business hours, out of hours",
								title: "Count only during business hours",
								description: "Outside business hours, waiting time doesn't count in the chart, list, menu alert or alerts.",
							},
							artThresholds: {
								keywords: "art, green, yellow, red, minutes, average time, limit",
								title: "Color limits",
								description: "In minutes. Above the yellow limit it turns red.",
								greenUpTo: "Green up to",
								yellowUpTo: "Yellow up to",
								errors: {
									integer: "Use whole numbers.",
									range: "Use values from 1 to 1440 minutes.",
									order: "The green limit must be lower than the yellow one.",
								},
							},
							shortReplyFilter: {
								keywords: "ok, emoji, thumbs up, thanks, short reply, closing, commission",
								title: "Ignore short replies in indicators",
								description: "A 1-character message, only emojis or the words below. Applies to messages that arrive after saving.",
							},
							shortReplyWords: {
								keywords: "words, list, ok, thanks",
								title: "Words that count as a short reply",
								description: "One per line. They count alone or together, with emojis and punctuation, regardless of case and accents. Ending in \"?\", they become a question and need a reply.",
								restore: "Restore default",
								errors: {
									tooMany: "Use up to 100 words.",
									tooLong: "Each word can have up to 30 characters.",
								},
							},
							shortReplyStickers: {
								keywords: "sticker",
								title: "A sticker alone counts as a short reply",
								description: "The thank-you sticker at the end of the conversation.",
							},
							shortReplyAutoClose: {
								keywords: "auto close, inactivity, close ticket",
								title: "A short reply doesn't keep the ticket open",
								description: "When the connection closes tickets for inactivity, the customer's \"Ok\" doesn't count as their reply.",
							},
						},
					},
					calls: {
						keywords: "call, calls, phone, voice, decline, reject",
						title: "Calls",
						description: "What to do when a customer calls on WhatsApp.",
						fields: {
							callRejection: {
								keywords: "decline, reject, automatic message, call",
								title: "Decline calls",
								description: "Declines the call right away and sends an automatic message to the customer.",
							},
							callRejectionMessage: {
								title: "Message to the customer",
								description: "Use {{variable}} to include the customer's name.",
								placeholder: "Hi {{variable}}! We don't take calls here at the moment. Please send your question as a message and we'll reply soon.",
							},
						},
					},
					notifications: {
						keywords: "alert, notification, browser, push, sms",
						title: "Notifications",
						description: "Browser alerts and SMS alerts for the team.",
						groups: {
							browser: {
								title: "In the browser",
								description: "Shown on the screen of whoever has the system open.",
							},
							sms: {
								title: "SMS alert",
								description: "Sends an SMS when too many conversations go unanswered.",
							},
						},
						fields: {
							webNotifications: {
								keywords: "browser, push, new message, desktop",
								title: "New messages",
								description: "Shows a browser alert when a message arrives in a conversation.",
							},
							internalChatNotifications: {
								keywords: "internal chat, mention, direct message",
								title: "Internal chat",
								description: "Shows a browser alert for direct messages and mentions in the internal chat.",
							},
							smsAlert: {
								searchTitle: "SMS alert",
								keywords: "sms, phone, mobile, alert, limit, frequency, test",
								title: "Turn on the alert",
								description: "Sends an SMS when the share of unanswered conversations goes over the limit.",
							},
							smsThreshold: {
								title: "Limit (%)",
								description: "The alert goes out when the share of unanswered conversations goes over this value.",
							},
							smsInterval: {
								title: "Time between alerts",
								description: "Minimum time between one SMS and the next.",
								unit: "Unit",
								units: {
									minutes: "Minutes",
									hours: "Hours",
									days: "Days",
								},
							},
							smsPhones: {
								title: "Phones that get the alert",
								description: "One number per line, with area code.",
							},
							smsTest: {
								title: "Test the alert",
								description: "Sends a test SMS to the first number on the list.",
								button: "Send test SMS",
								sending: "Sending...",
								sent: "Test SMS sent to {{phone}}.",
								failed: "Couldn't send: {{error}}",
							},
						},
						toasts: {
							unsupported: "This browser doesn't show notifications.",
							permissionDenied: "The browser didn't allow notifications.",
						},
					},
					modules: {
						keywords: "modules, features, turn on, turn off",
						title: "Modules",
						description: "Features that can be turned on or off for the company.",
						groups: {
							features: {
								title: "Features",
							},
							sttelar: {
								title: "Enabled by Sttelar",
								description: "Only super administrators see these options.",
							},
						},
						fields: {
							enableAcademy: {
								keywords: "academy, training, courses, lessons",
								title: "Training",
								description: "Shows the Training area to the team.",
							},
							triageEnabled: {
								keywords: "triage, ai, artificial intelligence, bot",
								title: "AI triage",
								description: "Enables triage setup and the triage panel in this company's conversations.",
							},
							triageRetention: {
								keywords: "triage, retention, 90 days, delete, cleanup, personal data",
								title: "90-day retention: actually delete",
								description: "Off, the daily cleanup only simulates: it counts what it would delete from triages ended more than 90 days ago. On, it deletes their documents and data; only the history remains, without personal data.",
								never: "Has not run yet (runs every day at 03:30).",
								lastSimulation: "Last simulation on {{date}}: {{sessions}} triages, {{files}} files, {{notes}} summary notes and {{workOrders}} work orders would be deleted.",
								lastDelete: "Last run on {{date}}: {{sessions}} triages cleaned ({{files}} files, {{notes}} summary notes, {{workOrders}} work orders); {{remaining}} left for the next run.",
								confirmTitle: "Actually delete?",
								confirmBody: "From the next cleanup (every day at 03:30), this company's triages ended more than 90 days ago permanently lose the photos, invoices, videos and audios sent in the triage, the data read, the summary note and the work order data. This cannot be undone.",
							},
							ticketCloseButton: {
								keywords: "resolve, close, finish, end, button, agent, conversation, ticket",
								title: "Agents can resolve conversations",
								description: "When off, only admins close conversations: the Resolve button disappears for agents. Automatic closing and the AI keep closing them.",
							},
						},
					},
					company: {
						keywords: "tax id, cnpj, legal name, trade name, phone, email, company details, account",
						title: "Company details",
						description: "Each account's tax details and contact.",
					},
					"job-roles": {
						keywords: "job title, role, team",
						title: "Job titles",
						description: "Job titles shown on team profiles and in the internal chat.",
					},
					departments: {
						keywords: "department, departments, accounts, units, dashboard, reports, who sees",
						title: "Departments",
						description: "Groups of accounts and who in this account sees their numbers on the Dashboard.",
					},
					"team-access": {
						keywords: "permissions, access role, supervisor, agent, administrator, exceptions, history, who can",
						title: "Team and permissions",
						description: "Each person's access role and the exceptions to it.",
					},
					"company-security": {
						keywords: "2fa, two-factor, authentication, required, policy, deadline, team",
						title: "Company security",
						description: "Choose whether the team must use two-factor authentication.",
					},
					billing: {
						keywords: "invoice, invoices, plan, payment, card, billing, subscription, upgrade, cancel",
						title: "Billing",
						description: "Plan, payment method and invoices.",
					},
				},
				companyData: {
					accountLabel: "Account",
					razaoSocial: "Legal Name",
					nomeFantasia: "Trade Name",
					cnpj: "Tax ID (CNPJ)",
					phone: "Phone",
					email: "Email",
					phRazaoSocial: "e.g. Acme Trading LLC",
					phNomeFantasia: "e.g. Acme Store",
					language: "Language of automatic messages to customers",
					phEmail: "contact@company.com",
					save: "Save",
					saved: "Data updated successfully.",
					billingHint: "Invoices and tax receipts use the Billing details, in Billing.",
					invalidCnpj: "Invalid CNPJ. Check the digits.",
				},
				jobRoles: {
					title: "Job Roles",
					emptyState: "No job roles created for this company yet.",
					modal: {
						add: "New job role",
						edit: "Edit job role",
					},
					form: {
						name: "Job role name",
					},
					buttons: {
						add: "Add job role",
						cancel: "Cancel",
						save: "Save",
					},
					toasts: {
						created: "Job role created successfully.",
						updated: "Job role updated successfully.",
						deleted: "Job role deleted successfully.",
					},
					confirmationModal: {
						deleteTitle: "Delete job role",
						deleteMessage: "Users assigned to this role will automatically move to No role.",
					},
				},
				departments: {
					emptyState: "No departments yet.",
					columns: {
						name: "Department",
						companies: "Accounts",
						viewers: "Who sees",
					},
					nobody: "Administrators only",
					buttons: {
						add: "New department",
						cancel: "Cancel",
						save: "Save",
						edit: "Edit",
						delete: "Delete",
					},
					modal: {
						add: "New department",
						edit: "Edit department",
					},
					form: {
						name: "Name",
						companies: "Accounts",
						companiesHelp: "Each account belongs to one department only.",
						viewers: "Who sees the numbers",
						viewersHelp: "People in this account. Administrators already see all of them.",
						takenBy: "In {{name}}",
						noOptions: "Nothing found",
					},
					toasts: {
						created: "Department created.",
						updated: "Department updated.",
						deleted: "Department deleted.",
					},
					confirmationModal: {
						deleteTitle: "Delete department",
						deleteMessage: "The accounts stay the same; they are just no longer grouped in this department.",
					},
				},
				helps: {
					toasts: {
						errorList: "Could not load records list",
						errorOperation: "Could not complete operation",
						error: "Could not complete operation. Check if help with the same name exists or if fields were filled correctly",
						success: "Operation completed successfully!",
					},
					buttons: {
						clean: "Clear",
						delete: "Delete",
						save: "Save",
					},
					grid: {
						title: "Title",
						description: "Description",
						video: "Video",
					},
					confirmModal: {
						title: "Delete Record",
						confirm: "Do you really want to delete this record?",
					},
				},
				company: {
					toasts: {
						errorList: "Could not load records list",
						errorOperation: "Could not complete operation",
						error: "Could not complete operation. Check if company with same name exists or if fields were filled correctly",
						success: "Operation completed successfully!",
					},
					confirmModal: {
						title: "Delete Record",
						confirm: "Do you really want to delete this record?",
					},
					form: {
						name: "Name",
						email: "Email",
						phone: "Phone",
						plan: "Plan",
						status: "Status",
						yes: "Yes",
						no: "No",
						campanhas: "Campaigns",
						enabled: "Enabled",
						disabled: "Disabled",
						createdAt: "Created On",
					},
					buttons: {
						clear: "Clear",
						delete: "Delete",
						user: "User",
						save: "Save",
					},
				},
				schedules: {
					form: {
						initialHour: "Start time",
						finalHour: "End time",
						save: "Save",
						days: {
							monday: "Mon",
							tuesday: "Tue",
							wednesday: "Wed",
							thursday: "Thu",
							friday: "Fri",
							saturday: "Sat",
							sunday: "Sun",
						},
						workdays: "Weekdays",
						workdaysHint: "Monday to Friday",
						allDays: "Every day",
						allDaysHint: "All days of the week",
						closedDay: "Closed",
						closedNote: "Closed on these days",
						remove: "Remove",
						add: "Add hours",
						empty: "No hours set. Use \"Add hours\" to start.",
						errors: {
							noDays: "Select at least one day",
							missingTime: "Fill in the start and end time",
							invalidTime: "Use the HH:MM format",
							endBeforeStart: "The end time must be after the start time",
						},
					},
					exceptions: {
						add: "Add date",
						empty: "No holidays or special dates.",
						loadError: "Couldn't load the holidays.",
						columns: {
							date: "Date",
							note: "Note",
							status: "Hours",
						},
						closed: "Closed",
						edit: "Edit",
						remove: "Remove",
						dialog: {
							newTitle: "New holiday or special date",
							editTitle: "Edit date",
							description: "On this date, what you set here replaces the weekly hours.",
							date: "Date",
							closedAllDay: "Closed all day",
							closedHint: "No service on this date.",
							reducedHint: "Open only during the hours below.",
							hours: "Business hours",
							note: "Note (optional)",
							notePlaceholder: "E.g.: Christmas, New Year, half day",
							cancel: "Cancel",
							create: "Add",
							save: "Save",
						},
						errors: {
							date: "Enter the date",
						},
						deleteTitle: "Remove this date?",
						deleteBody: "The weekly hours will apply on this date again.",
						deleteConfirm: "Remove",
						national: {
							button: "National holidays",
							title: "Import national holidays",
							description: "Checked dates are added as closed days. Optional ones start unchecked and dates already added are left out.",
							year: "Year",
							empty: "There are no more national holidays this year.",
							registered: "already added",
							optional: "optional",
							cancel: "Cancel",
							import: "Add {{count}} date",
							import_plural: "Add {{count}} dates",
							imported: "{{count}} date added.",
							imported_plural: "{{count}} dates added.",
							names: {
								newYear: "New Year's Day",
								carnivalMonday: "Carnival (Monday)",
								carnivalTuesday: "Carnival (Tuesday)",
								goodFriday: "Good Friday",
								easter: "Easter",
								tiradentes: "Tiradentes Day",
								labourDay: "Labour Day",
								corpusChristi: "Corpus Christi",
								independence: "Independence Day",
								aparecida: "Our Lady of Aparecida",
								allSouls: "All Souls' Day",
								republic: "Republic Day",
								blackConsciousness: "Black Consciousness Day",
								christmas: "Christmas",
							},
						},
						toasts: {
							created: "Date added.",
							updated: "Date updated.",
							removed: "Date removed.",
						},
					},
				},
				settings: {
					userCreation: {
						name: "User creation",
						options: {
							enabled: "Enabled",
							disabled: "Disabled",
						},
					},
				},
			},
			startConversationModal: {
				title: "Start conversation",
				subtitle: "Opens a conversation with this number.",
				number: "Number",
				connection: "Connection",
				selectConnection: "Select the connection",
				noConnection: "No active connection. Connect a WhatsApp in Connections to start the conversation.",
				copy: "Copy number",
				copied: "Number copied",
				copyFailed: "Couldn't copy. Select the number and copy it manually.",
				cancel: "Cancel",
				start: "Start chat",
				starting: "Starting…",
				started: "Conversation started",
				selectRequired: "Select a connection",
				invalidNumber: "This doesn't look like a valid phone number. Check it before starting the conversation.",
			},
			sendContactModal: {
				menuItem: "Contact",
				title: "Send contact",
				searchPlaceholder: "Search name or number",
				thisConnection: "This connection",
				contacts: "Contacts",
				minChars: "Type at least 3 letters or numbers to search.",
				empty: "No contacts found.",
				cancel: "Cancel",
				send: "Send",
				sending: "Sending…",
				unconfirmed: "Couldn't confirm the send. Check the chat before trying again.",
			},
			sharedContactCard: {
				contacts: "{{count}} contacts",
				talk: "Chat",
				copy: "Copy",
				copied: "Copied",
				save: "Save",
				saving: "Saving…",
				saved: "Saved",
				savedToast: "Contact saved",
				copiedToast: "Number copied",
				copyFailed: "Couldn't copy. Select the number and copy it manually.",
				noNumber: "No number",
				showAll: "See all ({{count}})",
				showLess: "See less",
			},
			messagesList: {
				header: {
					assignedTo: "Assigned to:",
					buttons: {
						return: "Return",
						resolve: "Resolve",
						reopen: "Reopen",
						accept: "Accept",
						download: "Download",
					},
				},
				lostCall: "Missed voice/video call at",
				deletedMessage: "This message was deleted by the contact",
				aiBadge: "AI",
				aiSentBy: "Message sent by AI",
				edited: "Edited",
				audio: "Audio",
				location: "Location",
				saudation: "Say hello to your new contact!",
			},
			messagesInput: {
				placeholderOpen: "Type a message",
				placeholderClosed: "Reopen or accept this ticket to send a message.",
				placeholderAttendedByOther: "Another agent is handling this conversation.",
				signMessage: "Sign",
			},
			contactDrawer: {
				header: "Contact Information",
				close: "Close",
				expand: "Expand panel",
				collapse: "Collapse panel",
				copyNumber: "Copy number",
				numberCopied: "Number copied",
				copyFailed: "Couldn't copy. Select the number and copy it manually.",
				buttons: {
					edit: "Edit contact",
				},
				extraInfo: "Other information",
			},
			fileModal: {
				title: {
					add: "Add file list",
					edit: "Edit file list",
				},
				buttons: {
					okAdd: "Save",
					okEdit: "Edit",
					cancel: "Cancel",
					fileOptions: "Add file",
				},
				form: {
					name: "File list name",
					message: "List details",
					fileOptions: "File list",
					extraName: "Message to send with file",
					extraValue: "Option value",
				},
				formErrors: {
					name: {
						required: "Name is required",
						short: "Name is too short",
					},
					message: {
						required: "Message is required",
					},
				},
				success: "File list saved successfully!",
			},
			ticketOptionsMenu: {
				schedule: "Schedule",
				delete: "Delete",
				transfer: "Transfer",
				registerAppointment: "Contact Notes",
				appointmentsModal: {
					title: "Contact Notes",
					textarea: "Note",
					placeholder: "Enter the information you want to record here",
				},
				confirmationModal: {
					title: "Delete ticket",
					titleFrom: "from contact ",
					message: "Warning! All messages related to this ticket will be lost.",
				},
				buttons: {
					delete: "Delete",
					cancel: "Cancel",
				},
			},
			unsavedChanges: {
				hint: "Unsaved changes",
				title: "Discard changes?",
				body: "Your changes haven't been saved yet. If you continue, they'll be lost.",
				leaveConfirm: "Your changes haven't been saved yet. Leave anyway?",
				discard: "Discard",
			},
			confirmationModal: {
				eyebrow: "Confirmation",
				buttons: {
					confirm: "Ok",
					cancel: "Cancel",
				},
			},
			messageOptionsMenu: {
				delete: "Delete",
				reply: "Reply",
				forward: "Forward",
				edit: "Edit",
				retry: "Retry",
				retrying: "Retrying...",
				confirmationModal: {
					title: "Delete message?",
					message: "This action cannot be undone.",
				},
			},
			editMessageModal: {
				title: "Edit message",
				originalMessage: "Original message:",
				newMessage: "New message",
				characters: "characters",
				timeRemaining: "Time remaining",
				timeExpired: "Edit time expired",
				success: "Message edited successfully!",
				errors: {
					emptyMessage: "Message cannot be empty",
					noChanges: "No changes detected",
					timeExpired: "Edit time expired (10 minute limit)",
				},
				buttons: {
					cancel: "Cancel",
					save: "Save",
				},
			},
			templateModal: {
				title: "Select Template",
				searchPlaceholder: "Search by name or content...",
				loading: "Loading templates...",
				recents: "Recent",
				favorites: "Favorites",
				favorite: "Favorite template",
				unfavorite: "Remove from favorites",
				all: "All",
				previewEmpty: "Select a template to see the preview",
				sentToast: "Template \"{name}\" sent successfully!",
				categories: {
					all: "All",
					marketing: "Marketing",
					utility: "Utility",
					auth: "Authentication",
					other: "Other",
				},
				empty: {
					title: "No templates found",
					description: "Configure approved templates in your Meta for Developers account to use this feature.",
				},
				noResults: {
					title: "No results",
					description: "Try another search or remove the category filter.",
				},
				hints: {
					navigate: "navigate",
					send: "send",
					close: "close",
				},
				buttons: {
					cancel: "Cancel",
					send: "Send template",
					sending: "Sending...",
				},
			},
			academy: {
				title: "Training",
				empty: "No courses available yet.",
				lessons: "lessons",
				progress: "complete",
				courseContent: "Course content",
				ofLessonsCompleted: "of {{total}} completed",
				lessonTag: "LESSON",
				back: "Back",
				videoUnavailable: "Video unavailable",
				continueWatching: "Continue watching",
				courseCompleted: "Course completed",
				markComplete: "Mark as complete",
				unmarkComplete: "Unmark",
				toasts: {
					disabled: "The Training area is disabled.",
					noPermission: "You don't have permission to access this area.",
				},
				manage: {
					title: "Manage Training",
					draft: "Draft",
					buttons: {
						cancel: "Cancel",
						save: "Save",
						addCourse: "New course",
						addModule: "New module",
					},
					validation: {
						titleRequired: "Enter a title (min. 2 characters).",
						videoUrlRequired: "Enter the video URL.",
					},
					toasts: {
						courseSaved: "Course saved.",
						moduleSaved: "Module saved.",
						lessonSaved: "Lesson saved.",
						thumbnailRemoved: "Thumbnail removed.",
					},
					confirm: {
						deleteCourse: "Delete course",
						deleteModule: "Delete module",
						deleteLesson: "Delete lesson",
						message: "This action cannot be undone.",
					},
					courseModal: {
						addTitle: "New course",
						editTitle: "Edit course",
						title: "Title",
						description: "Description",
						published: "Published",
						thumbnail: "Thumbnail (image)",
						removeThumbnail: "Remove",
					},
					moduleModal: {
						addTitle: "New module",
						editTitle: "Edit module",
						title: "Title",
					},
					lessonModal: {
						addTitle: "New lesson",
						editTitle: "Edit lesson",
						title: "Title",
						description: "Description",
						videoUrl: "Video URL (YouTube or Vimeo)",
						currentVideo: "Current video",
						keepHint: "leave the URL blank to keep it",
					},
				},
			},
			flowResults: {
				back: "Flows",
				edit: "Edit flow",
				view: "View flow",
				openEditor: "Open the editor",
				active: "Active",
				inactive: "Inactive",
				noDescription: "No description.",
				addDescription: "Add",
				editDescription: "Edit",
				descriptionLabel: "Flow description",
				descriptionPlaceholder: "What is this flow for?",
				save: "Save",
				cancel: "Cancel",
				close: "Close",
				saved: "Saved.",
				refresh: "Refresh",
				refreshing: "Refreshing…",
				trackingSince: "Measuring since {{date}}. There is no data before that date.",
				notTrackedYet: "Not measured yet: numbers show up when the first contact goes through the flow.",
				disabled: "Flow measurement is turned off on this server. The numbers are not being updated.",
				period: {
					label: "Period",
					"7d": "7 days",
					"30d": "30 days",
					"90d": "90 days",
					custom: "Custom",
					from: "From",
					to: "to",
					apply: "Apply",
					range: "{{from}} to {{to}}",
				},
				metrics: {
					entries: "Entries",
					sent: "Sent",
					delivered: "Delivered",
					clicks: "Clicks",
					conversions: "Conversions",
					help: {
						entries: "Contacts that entered the flow in the period. Each conversation counts once.",
						sent: "Messages the flow sent and WhatsApp accepted.",
						delivered: "Messages that reached the contact's phone.",
						clicks: "Links sent by the flow that the contact opened. Each link counts once.",
						conversions: "Purchases by people who got a message from this flow before buying.",
					},
					rate: {
						delivery: "{{value}} of sent",
						click: "{{value}} of delivered",
						conversion: "{{value}} of entries",
					},
					inPeriod: "in the period",
					messages: "messages",
					sendFailed: "{{count}} was not sent",
					sendFailed_plural: "{{count}} were not sent",
					notConfigured: "Not set up",
					unavailable: "Unavailable",
					configure: "Set up",
					clicksUnavailableHint: "This flow only sends ready-made templates, and their link cannot be replaced.",
					clicksNotConfiguredHint: "Turn on link tracking to count them.",
					conversionsNotConfiguredHint: "Connect your store to count purchases.",
					conversionValue: "{{value}} in sales",
				},
				summary: {
					running: "{{count}} in progress",
					completed: "{{count}} completed",
					completed_plural: "{{count}} completed",
					failed: "{{count}} failed",
					cancelled: "{{count}} cancelled",
					cancelled_plural: "{{count}} cancelled",
					read: "{{count}} read ({{value}} of delivered)",
					read_plural: "{{count}} read ({{value}} of delivered)",
				},
				chart: {
					title: "Entries per day",
					hint: "Contacts that entered the flow each day.",
					empty: "No entries in the period.",
					entries: "{{count}} entry",
					entries_plural: "{{count}} entries",
					day: "Day",
				},
				history: {
					title: "Run history",
					subtitle: "Each contact that entered the flow in the period.",
					search: "Search by name or phone",
					allStatuses: "All statuses",
					allKinds: "All types",
					kind: "Type",
					columns: {
						contact: "Contact",
						startedAt: "Entered",
						status: "Status",
						step: "Current step",
						duration: "Time",
						messages: "Messages",
					},
					empty: {
						title: "No runs in the period",
						description: "When a contact enters the flow, it shows up here.",
						filtered: "No runs match these filters.",
					},
					loadMore: "Load more",
					loading: "Loading…",
					removedContact: "Removed contact",
					noContact: "No contact",
					steps: "{{count}} step done",
					steps_plural: "{{count}} steps done",
					sentCount: "{{count}} sent",
					sentCount_plural: "{{count}} sent",
					deliveredCount: "{{count}} delivered",
					deliveredCount_plural: "{{count}} delivered",
					failedMessages: "{{count}} failed",
					searchWithOrder: "Search by name, phone or work order number",
					order: "WO {{number}}",
					orderStatus: "WO {{number}} · {{status}}",
				},
				status: {
					running: "In progress",
					completed: "Completed",
					failed: "Failed",
					cancelled: "Cancelled",
				},
				kind: {
					ai_agent: "AI Agent",
					mass_send: "Mass send",
					triage: "AI Triage",
					order_status: "Work order update",
				},
				endReason: {
					resolved: "The AI closed the conversation",
					transferred: "Handed to a person",
					ticket_closed: "The conversation was closed",
					human_takeover: "A person took over the conversation",
					flow_deactivated: "The flow was turned off",
					ticket_removed: "The conversation was deleted",
					inactivity: "The contact stopped writing",
					sent: "Message sent",
					send_failed: "Sending failed",
					triage_completed: "Triage completed",
					triage_cancelled: "Triage cancelled",
					triage_failed: "The triage could not continue",
					opt_out: "The customer opted out of messages",
					invalid_phone: "The notice had no valid phone",
					flow_inactive: "The flow was turned off before sending",
					flow_invalid: "The trigger has no template linked",
					expired: "The work order changed status more than 6 hours ago",
					event_missing: "The notice data is no longer stored",
					connection_invalid: "The trigger's connection no longer works",
					connection_unavailable: "The connection was disconnected",
					template_check: "Could not read the connection's templates",
					template_unavailable: "The template is not available",
					invalid_variables: "A value for the template was missing",
					busy: "Another send to the customer was in progress",
					send_uncertain: "Could not confirm whether it was sent",
				},
				detail: {
					title: "Run",
					openConversation: "Open conversation",
					summary: {
						startedAt: "Entered",
						finishedAt: "Ended",
						stillRunning: "In progress",
						duration: "Run time",
						runs: "AI replies",
					},
					messages: "Messages sent",
					noMessages: "No messages sent.",
					media: "File sent",
					deletedMessage: "Message deleted from the conversation",
					messageStatus: {
						sent: "Sent",
						notSent: "Not sent",
						delivered: "Delivered",
						read: "Read",
						failed: "Failed",
					},
					error: "Error",
					lastError: "Last error (already recovered)",
					reason: "Why it was not sent",
					orderNumber: "Work order",
					orderStatus: "Work order status",
					unknownError: "Error with no description.",
					triageErrorHint: "The reason is in the triage monitoring (Flows > Manage Triages).",
					path: "Path through the flow",
					version: "Flow version {{number}}",
					versionOld: "The flow was edited later. This is the layout in effect during this run.",
					noVersion: "The flow layout for this run was not saved.",
					legend: {
						done: "Passed",
						current: "Current step",
						error: "Failed",
						pending: "Not reached",
					},
					links: "Tracked links",
					clicks: "opened {{count}} time",
					clicks_plural: "opened {{count}} times",
					notClicked: "not opened",
					conversions: "Conversions",
					order: "Order {{id}}",
					reverted: "undone (refund or cancellation)",
					timeline: "Timeline",
					noEvents: "Nothing recorded yet.",
					tokens: "{{count}} tokens",
					unknownStep: "Step removed from the flow",
					notFound: "This run no longer exists.",
					event: {
						started: "Entered the flow",
						finished: "Run ended",
						runFailed: "The AI could not reply",
						nodeDone: "done",
						nodeFailed: "Failed at this step.",
						tool: "Tool {{tool}}",
						linkClicked: "Opened a link",
						conversion: "Purchase attributed to this flow",
					},
					action: {
						send: "The AI replied",
						escalate: "The AI handed the conversation to a person",
						silent: "The AI did not reply",
						none: "Round with no reply",
					},
				},
				measure: {
					button: "Measurement",
					title: "Flow measurement",
					copy: "Copy",
					copied: "Copied.",
					links: {
						title: "Link clicks",
						description: "Replaces the links in the AI's messages with an address that counts the click and leads to the same place.",
						toggle: "Track links",
						hosts: "Tracked sites",
						hostsHint: "Only links from these sites are replaced. Enter your store's site.",
						hostPlaceholder: "mystore.com",
						addHost: "Add",
						removeHost: "Remove {{host}}",
						note: "A link sent together with an AI action (tagging, transferring) goes out untracked.",
						unavailable: "This flow only sends ready-made templates, and their link cannot be replaced.",
					},
					conversions: {
						title: "Conversions",
						description: "Counts purchases by people who got a message from this flow before buying.",
						source: "Store",
						none: "Do not measure conversions",
						webhookUrl: "Delivery URL",
						secret: "Secret",
						secretOnce: "Copy the secret now. It will not be shown again.",
						secretHidden: "The secret was already shown and will not appear again.",
						rotate: "Generate another",
						rotateWarning: "The store uses the current secret. If you generate another, its notices stop being accepted until you paste the new one there.",
						rotateConfirm: "Generate another secret",
						howTo: "In the store: WooCommerce > Settings > Advanced > Webhooks. Create a webhook with the topic \"Order updated\" and paste the URL and the secret.",
						sourceError: "Problem with the store's last notice: {{error}}",
						lastEvent: "Last notice from the store {{when}}.",
						noEvents: "The store has not sent any notice yet.",
						window: "Time limit to count",
						windowHint: "Longest time between the flow's message and the purchase.",
						days: "{{count}} day",
						days_plural: "{{count}} days",
						requireClick: "Only count if the contact opened a link from the flow",
						newSource: "Connect another store",
						firstSource: "Connect a store",
						chooseIntegration: "Choose the store",
						create: "Generate URL",
						noIntegrations: "Register the store under Integrations to measure conversions.",
					},
				},
				errors: {
					forbidden: "Only administrators can see flow results.",
					notFound: "This flow no longer exists.",
					load: "Could not load the results.",
					retry: "Try again",
					history: "Could not load the history.",
					execution: "Could not load this run.",
				},
			},
			workOrders: {
				triage: {
					title: "Triage",
					disabled: "AI triage is not turned on for this company.",
					waiting: "Waiting to start (next attempt: {{when}}).",
					failed: "The automatic triage did not start:",
					none: "No triage for this work order.",
					unnamed: "Triage",
					openConversation: "Open conversation",
					noDefinitions: "No published triage starts from work orders of this source. Turn it on in the triage's Work orders tab (Flows > Manage Triages).",
					start: "Start triage",
					noPhones: "The work order has no phone that works for WhatsApp.",
					dialogTitle: "Start triage from the work order",
					definition: "Triage",
					chooseDefinition: "Choose the triage",
					phone: "Customer phone",
					mobile: "mobile",
					landline: "landline",
					connectionNote: "The conversation opens through the connection:",
					dialogHint: "If the customer is already with an agent, the AI does not take over: the triage waits and the agent is notified.",
					cancel: "Cancel",
					confirm: "Start",
					started: "Triage started: the AI will reach the customer.",
					startedHuman: "The customer is already with an agent: the triage is waiting and the agent was notified.",
					chips: {
						waitingHuman: "Waiting for agent",
						failed: "Not started",
						scheduled: "Scheduled",
						none: "No triage",
					},
				},
				title: "Work orders",
				subtitle: "Work orders received from the manufacturer's system, sent by the service center's collector.",
				configure: "Configure in Integrations",
				search: "Search by order number, name, phone or tax ID",
				allSources: "All integrations",
				sourceFilter: "Integration",
				inactiveSource: "{{name}} (inactive)",
				sourceHealth: {
					online: "Online",
					offline: "No signal",
					needs_login: "Waiting for someone to sign in to the portal",
					error: "Last cycle failed",
					never: "Collector has not sent a signal yet",
					inactive: "Disabled",
				},
				allStatuses: "All statuses",
				allTypes: "All types",
				allWarranties: "All warranties",
				warrantyFilter: {
					in: "In warranty",
					out: "Out of warranty",
					unknown: "Warranty not informed",
				},
				triageFilter: {
					label: "Triage",
					all: "All",
				},
				sort: {
					label: "Sort",
					options: {
						received_desc: "Newest first",
						received_asc: "Oldest first",
						number_asc: "Order number, lowest first",
						number_desc: "Order number, highest first",
						customer_asc: "Customer, A to Z",
						customer_desc: "Customer, Z to A",
						city_asc: "City, A to Z",
						city_desc: "City, Z to A",
						product_asc: "Product, A to Z",
						product_desc: "Product, Z to A",
						triage_asc: "Triage, needs someone first",
						triage_desc: "Triage, reversed",
					},
				},
				total: "{{count}} orders",
				loadMore: "Load more",
				updatesAvailable: "There are new or changed work orders.",
				refresh: "Refresh",
				columns: {
					number: "Order no.",
					received: "Received",
					status: "Status",
					type: "Type",
					warranty: "Warranty",
					customer: "Customer",
					city: "City",
					product: "Product",
					triage: "Triage",
				},
				empty: {
					title: "No work orders received yet",
					description: "As soon as the collector sends work orders, they show up here.",
					filtered: "No work orders match these filters.",
					noSources: "This company does not receive work orders yet. Sttelar enables it.",
				},
				source: {
					online: "{{name}}: online, last signal {{time}}",
					offline: "{{name}}: no signal ({{time}}). Check that the service center computer is on and online.",
					needs_login: "{{name}}: the collector is waiting for someone to sign in to the manufacturer's portal, in the robot window.",
					error: "{{name}}: the collector's last cycle failed: {{error}}",
					never: "{{name}}: the collector has not sent a signal yet. Set it up on the service center computer with the integration token.",
					inactive: "{{name}}: source disabled.",
				},
				detail: {
					title: "Order {{number}}",
					close: "Close",
					customer: "Customer",
					name: "Name",
					document: "Tax ID",
					phones: "Phones",
					mentionedWhatsapp: "WhatsApp mentioned in the text",
					email: "Email",
					address: "Address",
					product: "Product",
					brandModel: "Brand and model",
					serial: "Serial number",
					category: "Category",
					purchaseDate: "Purchase date",
					warrantyEnd: "Warranty end",
					warrantyStatus: "Warranty",
					invoice: "Invoice",
					invoiceNumber: "Invoice number",
					reseller: "Reseller",
					request: "Customer request",
					symptom: "Symptom",
					notes: "Manufacturer notes",
					service: "Service",
					type: "Type",
					urgency: "Urgency",
					visit: "Scheduled visit",
					acceptedAt: "Accepted by the service center on",
					openedAt: "Opened on",
					receipt: "Receipt",
					receivedAt: "Arrived at Sttelar Talk",
					lastChangedAt: "Last change",
					lastSeenAt: "Last sent by the collector",
					source: "Source",
					purged: "This order's data was deleted by the retention policy.",
				},
			},
			triage: {
				tab: "Triage",
				badge: "Triage {{done}}/{{total}}",
				badgeTooltip: "Open this conversation's triage",
				sessionStatus: {
					PENDING: "Not started",
					IN_PROGRESS: "In progress",
					WAITING_CUSTOMER: "Waiting for the customer",
					COMPLETED: "Completed",
					CANCELLED: "Cancelled",
					FAILED: "Failed",
				},
				requirementStatus: {
					NOT_REQUESTED: "Not requested",
					REQUESTED: "Requested",
					PROCESSING: "Under review",
					COMPLETED: "Received",
					INVALID: "Invalid",
					WAITING_RESEND: "Waiting for resend",
					WAIVED: "Waived",
				},
				inputTypes: {
					text: "Text",
					image: "Image",
					document: "Document",
					document_or_image: "Document or image",
					video: "Video",
					audio: "Audio",
				},
				fieldTypes: {
					string: "Text",
					number: "Number",
					date: "Date",
					cpf_cnpj: "CPF/CNPJ",
					nfe_key: "NF-e access key",
					phone: "Phone",
					email: "Email",
				},
				events: {
					TRIAGE_CREATED: "Triage started",
					TRIAGE_STARTED: "AI took over",
					TRIAGE_COMPLETED: "Triage completed",
					TRIAGE_HANDOFF: "Handed off to the team",
					TRIAGE_CANCELLED: "Triage cancelled",
					TRIAGE_FAILED: "Triage failed",
					OUTREACH_SENT: "Opening message sent",
					OUTREACH_FAILED: "Opening message failed",
					FOLLOWUP_SENT: "Reminder sent",
					FOLLOWUP_SKIPPED: "Reminder not sent",
					REQUIREMENT_REQUESTED: "Item requested",
					REQUIREMENT_RECEIVED: "Item received for review",
					REQUIREMENT_VALIDATED: "Item validated",
					REQUIREMENT_REJECTED: "Item marked invalid",
					ERROR_CODE_DETECTED: "Error code found",
					REQUIREMENT_WAIVED: "Item waived",
					REQUIREMENT_MANUALLY_COMPLETED: "Item marked received",
					AI_PAUSED: "AI paused",
					AI_RESUMED: "AI resumed",
					CRM_MOVED: "CRM card moved",
					CRM_SKIPPED: "CRM unchanged",
					NOTE_UPDATED: "Note updated",
					CONTACT_RENAMED: "Contact name built by the triage",
					DATA_PURGED: "Documents and data deleted (90-day retention)",
					CUSTOMER_EXPORTED: "Location sent to Contele",
					CUSTOMER_EXPORT_FAILED: "Failed to send the location to Contele",
					CUSTOMER_EXPORT_SKIPPED: "Location not sent to Contele",
					ERROR: "Error",
				},
				noteStages: {
					created: "Summary note created in notes",
					final: "Final summary note in notes",
				},
				crmReasons: {
					moved_by_person: "the card was moved by a person",
					column_missing: "the target column no longer exists",
					card_deleted: "the card was deleted",
				},
				followUpEvents: {
					sent: "Reminder {{n}} sent",
					skipped: "Reminder {{n}} not sent",
					template: "template {{name}}",
				},
				followUpReasons: {
					window_closed: "the official API 24-hour window was closed and the reminder has no template",
				},
				customerExport: {
					status: {
						sentCreated: "Location created in Contele on {{date}}",
						sentUpdated: "Location updated in Contele on {{date}}",
						pending: "Sending the location to Contele…",
						retrying: "Contele did not respond: next try on {{date}}",
						failed: "Location not sent to Contele: {{reason}}",
						skipped: "Location not sent to Contele: {{reason}}",
					},
					reasons: {
						auth: "Contele rejected the account keys",
						rejected: "Contele rejected the data",
						unavailable: "Contele did not respond",
						integration_missing: "the Contele account was removed",
						integration_inactive: "the Contele account is turned off",
						keys_missing: "the Contele account has no keys",
						data_purged: "the triage data was already deleted",
						internal: "Talk error while sending",
						no_name: "the customer's name is missing",
						no_address: "the customer's address is missing",
					},
					outcomes: {
						created: "new location",
						updated: "location updated",
					},
					addressChanged: "new address",
					mediaLink: "with the media link",
					account: "Account: {{name}}",
					retrying: "will try again",
					retry: "Send again",
					retried: "Sending the location to Contele again",
				},
				actors: {
					system: "System",
					ai: "AI",
					customer: "Customer",
					integration: "Integration",
				},
				attention: {
					customer_requested_human: "the customer asked to talk to a person",
					customer_refused: "the customer did not want to continue the triage",
					cannot_provide: "the customer said they do not have a required item",
					max_attempts: "the attempts for an item ran out",
					ai_unavailable: "the AI became unavailable (check the OpenAI key and credits in Integrations)",
					ai_limit: "the triage reached the AI call limit",
					ai_quota: "this month's AI messages ran out",
					billing_suspended: "sending is suspended due to a late payment",
					send_failed: "the reply could not be sent to the customer",
					ticket_with_human: "the work order arrived while the customer was with an agent: hand it to the AI whenever you want",
					no_response: "the customer did not reply within the triage deadline",
					no_reply_to_reminders: "the customer did not reply to the reminders",
				},
				crm: {
					card: "Triage {{done}}/{{total}}",
					cardPending: "Triage waiting to start",
					cardAttention: "Triage: call a person",
					cardCompleted: "Triage completed",
					cardCancelled: "Triage cancelled",
					cardFailed: "Triage failed",
					section: "Triage",
					progress: "{{done}} of {{total}} required",
					workOrder: "WO {{number}}",
					openConversation: "Open conversation",
					loadError: "Could not load this card's triage.",
				},
				panel: {
					empty: "No triage in progress in this conversation.",
					start: "Start triage",
					chooseDefinition: "Choose the triage",
					noDefinitions: "No published triage. An administrator creates one in Flows > Manage Triages.",
					group: "Triage does not work in group conversations.",
					closed: "Reopen the conversation to start a triage.",
					started: "Triage started.",
					progress: "{{done}} of {{total}} required",
					version: "version {{version}}",
					aiActive: "AI active in this conversation",
					aiPaused: "A person is handling the conversation; the AI does not reply",
					attention: "Needs attention: {{reason}}",
					nextFollowUp: "Next reminder ({{n}} of {{total}}): {{date}}",
					nextHandoff: "No reply to the reminders: hands over to a person at {{date}}",
					workOrder: "Work order {{number}}",
					pause: "Pause AI",
					resume: "Hand to AI",
					complete: "Complete",
					cancel: "Cancel",
					required: "Required",
					optional: "Optional",
					attempts: "Attempt {{attempts}} of {{max}}",
					reason: "Reason: {{reason}}",
					routedBy: "By rule {{detail}}",
					warrantyList: "List: {{list}}",
					purged: "Documents and data deleted by the retention policy on {{date}}.",
					note: "Note: {{note}}",
					itemActions: "Item actions",
					actions: {
						complete: "Mark as received",
						invalidate: "Mark as invalid",
						request: "Mark as requested",
						waive: "Waive",
						edit: "Fix data",
					},
					timeline: "Timeline",
					history: "Previous triages",
					viewing: "Triage from {{date}}",
					back: "Back",
					unverified: "sent by the customer, not checked",
					workOrderToConfirm: "from the work order, to be confirmed by the customer",
					problem: {
						title: "Customer's problem",
						code: "Error code {{code}}",
						from: {
							workOrder: "from the work order, confirmed by the customer",
							manual: "recorded by a person",
							video: "seen in the video",
							audio: "spoken by the customer",
							customer: "given by the customer",
							attachment: "sent by the customer",
						},
						via: {
							message: "code given by the customer",
							photo: "code read in the photo",
							video: "code seen in the video",
						},
					},
					workOrderConfirmed: "from the work order, confirmed by the customer",
					evidenceOpen: "Open the attachment",
					evidenceUnread: "The AI did not read this attachment: check it",
					evidencePhoto: "Photo sent by the customer",
					evidenceKinds: {
						pdf: "PDF",
						file: "File",
						video: "Video",
						audio: "Audio",
					},
					answer: "Answer",
					confidence: "AI reading confidence",
				},
				dialogs: {
					back: "Back",
					confirm: "Confirm",
					reasonLabel: "Reason",
					reasonRequired: "Enter the reason.",
					noteLabel: "Note (optional)",
					cancelTitle: "Cancel the triage",
					cancelText: "The triage stops here and stays in the history. If the AI had the conversation, it moves to you.",
					completeTitle: "Complete the triage",
					completeText: "Missing items stay as they are. If the AI had the conversation, it moves to you.",
					completePending: "Required items still missing: {{count}}.",
					invalidateTitle: "Mark \"{{name}}\" as invalid",
					invalidateText: "The data in this item is discarded.",
					waiveTitle: "Waive \"{{name}}\"",
					waiveText: "The item is no longer requested from the customer.",
					completeItemTitle: "Mark \"{{name}}\" as received",
					completeItemText: "Fill in what you checked (optional).",
					requestTitle: "Mark \"{{name}}\" as requested",
					requestText: "Records that the item was requested from the customer. No message is sent from here.",
					editItemTitle: "Fix the data of \"{{name}}\"",
				},
				manager: {
					button: "Manage Triages",
					title: "Triages",
					tabs: {
						definitions: "Triages",
						sessions: "Tracking",
					},
					new: "New triage",
					empty: "No triage yet.",
					emptyHint: "A triage defines what the AI asks the customer for (photos, documents, data) and what to do once everything arrives.",
					status: {
						draft: "Draft",
						active: "Published",
						archived: "Archived",
					},
					publishedVersion: "Version {{version}} published",
					notPublished: "Never published",
					activeSessions: "{{count}} in progress",
					updatedAt: "Changed on {{date}}",
					showArchived: "Show archived",
					actions: {
						edit: "Edit",
						duplicate: "Duplicate",
						archive: "Archive",
						restore: "Restore",
					},
					copyName: "{{name}} (copy)",
					archiveConfirm: "Archive \"{{name}}\"? It leaves the start options; triages in progress continue.",
					archived: "Triage archived.",
					restored: "Triage restored.",
					duplicated: "Copy created as a draft.",
					published: "Version {{version}} published.",
					saved: "Draft saved.",
					editor: {
						back: "Back to the list",
						newTitle: "New triage",
						howItWorks: "How it works",
						tabs: {
							general: "General",
							items: "Items",
							messages: "Messages",
							outreach: "Outreach",
							workOrders: "Work orders",
							contact: "Contact record",
							crm: "CRM",
							followUp: "Reminders",
							completion: "Completion",
						},
						save: "Save draft",
						publish: "Publish",
						unpublished: "There are unpublished changes.",
						upToDate: "Version {{version}} published, no pending changes.",
						neverPublished: "Draft: not published yet.",
						unsaved: "Unsaved changes",
						discardConfirm: "There are unsaved changes. Leave and discard them?",
						errors: {
							name: "Give the triage a name.",
							noItems: "Add at least one item.",
							noRequired: "Mark at least one item as required.",
							itemName: "Item {{n}}: give the item a name.",
							itemRequest: "Item {{n}}: write the request message.",
							fieldLabel: "Item {{n}}: give every extraction field a name.",
							noFields: "Item {{n}}: add at least one field or turn extraction off.",
							completion: "Write the completion message.",
							outreachVariable: "Outreach: choose the value of template variable {{n}}.",
							outreachFixed: "Outreach: write the fixed text of template variable {{n}}.",
							outreachWorkOrder: "Outreach: the template uses work order data; turn on the start in the Work orders tab.",
							workOrderSource: "Work orders: choose the work order source.",
							workOrderStatuses: "Work orders: choose at least one status.",
							workOrderTypes: "Work orders: choose at least one work order type.",
							workOrderDays: "Work orders: choose at least one sending day.",
							workOrderWindow: "Work orders: the sending hours must end after they start.",
							workOrderConnection: "Work orders: choose, in the Outreach tab, the connection that opens the conversation.",
							crmBoard: "CRM: choose the board.",
							crmEntry: "CRM: choose the entry column.",
							crmCompleted: "CRM: choose the completed column.",
							crmSame: "CRM: the entry and completed columns must be different.",
							crmRule: "CRM, rule {{n}}: choose the item and the column.",
							crmCompletedOnly: "CRM: the completed column is only for completed triages; choose another one for \"in triage\", attention and rules.",
							crmOnlyPending: "CRM, rule {{n}}: \"only required item left\" only works for a required item.",
							routeValues: "Routing, rule {{n}}: enter at least one value.",
							routeTooMany: "Routing, rule {{n}}: at most {{max}} values.",
							routeLong: "Routing, rule {{n}}: each value can have up to {{max}} characters.",
							routeDestination: "Routing, rule {{n}}: choose the queue or the person.",
							warrantyRequired: "{{list}}: mark at least one item as required.",
							routeValuesAt: "Routing, rule {{n}}, data {{m}}: enter at least one value.",
							routeTooManyAt: "Routing, rule {{n}}, data {{m}}: at most {{max}} values.",
							routeLongAt: "Routing, rule {{n}}, data {{m}}: each value can have up to {{max}} characters.",
							routeRepeated: "Routing, rule {{n}}: use each work order data only once.",
							customerExportAccount: "Contele: choose the account where the location is created.",
							customerExportNeedsWorkOrders: "Contele: the location uses the work order data; turn on the start in the Work orders tab.",
							followUpNone: "Reminders: add at least one reminder or turn reminders off.",
							followUpTime: "Reminder {{n}}: the time without a reply goes from 10 minutes to 30 days.",
							followUpOrder: "Reminder {{n}}: needs more time without a reply than the previous one.",
							followUpMessage: "Reminder {{n}}: write the message.",
							followUpVariable: "Reminder {{n}}: choose the value of template variable {{v}}.",
							followUpFinal: "Reminders: handing over to a person needs more time without a reply than the last reminder (up to 30 days).",
							followUpDays: "Reminders: choose at least one day for sending.",
							followUpWindow: "Reminders: the sending hours must end after they start.",
							followUpExpire: "Reminders: the last step must come before the triage expires ({{days}} days without a reply, in the Completion tab).",
							contactNameEmpty: "Contact record: write how the name should look or turn the option off.",
							contactNameTooLong: "Contact record: the name can have up to {{max}} characters.",
							contactNameLines: "Contact record: the name must fit on one line.",
							contactNameBraces: "Contact record: a brace { } is not opened or closed.",
							contactNameToken: "Contact record: {{token}} can't go in the name. Use the buttons below the field.",
							contactNameWorkOrder: "Contact record: {{token}} uses work order data; turn on the start in the Work orders tab.",
							contactNameNoData: "Contact record: add at least one piece of data to the name (buttons below the field).",
							contactNameTooMany: "Contact record: use at most {{max}} pieces of data in the name.",
						},
						general: {
							name: "Name",
							namePlaceholder: "E.g.: Midea warranty",
							description: "Description (optional)",
							model: "AI model",
							modelHint: "The OpenAI key comes from Integrations.",
							aiUser: "AI user",
							aiUserHint: "Shown as the conversation owner while the AI leads. \"AI Active\" must be off in the user settings.",
							aiUserNone: "Choose an AI user",
							aiUserUnusable: "{{name}} (AI Active on)",
							noAiUsers: "No AI user in this company. Create one in Users, marked as AI and with \"AI Active\" off.",
							waitTime: "Wait before replying (seconds)",
							waitTimeHint: "Groups consecutive customer messages into a single reply.",
						},
						items: {
							add: "Add item",
							empty: "No items. Add what the AI should ask the customer for.",
							untitled: "Untitled item",
							name: "Item name",
							namePlaceholder: "E.g.: Photo of the label",
							key: "Key",
							keyHint: "Internal identifier: lowercase letters, numbers and _.",
							description: "Description (optional)",
							inputType: "The customer sends",
							required: "Required",
							requestMessage: "Request message",
							requestPlaceholder: "E.g.: Please send a photo of the appliance label.",
							workOrderField: "Work order data to confirm",
							workOrderFieldHint: "When the triage starts from a work order, the AI shows this data and the customer confirms or corrects it instead of typing everything. Tax IDs and sensitive fields never appear in the message.",
							workOrderFieldNone: "None: ask the customer",
							compareWith: "Compare with the work order",
							compareWithNone: "Don't compare",
							compareWithFields: {
								productModel: "Product model",
								serialNumber: "Serial number",
								invoiceNumber: "Invoice number",
								purchaseDate: "Purchase date",
								customerDocument: "Customer tax ID",
							},
							workOrderFields: {
								address: "Customer address",
								buyer: "Customer name and tax ID",
								product: "Product brand and model",
								purchaseDate: "Purchase date",
								problem: "Customer request (defect)",
								invoiceNumber: "Invoice number",
							},
							retryMessage: "Message to ask again (optional)",
							aiInstruction: "Instruction for the AI (optional)",
							aiInstructionPlaceholder: "E.g.: The label is on the side of the indoor unit.",
							maxAttempts: "Attempts",
							onMaxAttempts: "When attempts run out",
							onMax: {
								handoff: "Call a person",
								waive: "Waive the item",
								keep_asking: "Keep asking",
							},
							optionalOnMax: "Optional item: waived at the limit.",
							extraction: "Extract data from what the customer sends",
							extractionHint: "The AI reads what arrived and fills in these fields.",
							minConfidence: "Minimum confidence (%)",
							addField: "Add field",
							fieldLabel: "Field name",
							fieldKey: "Key",
							fieldType: "Type",
							fieldRequired: "Required",
							fieldSensitive: "Sensitive",
							fieldSensitiveHint: "Masked in logs and in the timeline.",
							moveUp: "Move up",
							moveDown: "Move down",
							remove: "Remove",
							expand: "Edit item",
							extractsData: "extracts data",
							isProblem: "Customer's problem",
							isProblemHint: "Once confirmed, it shows at the top of the agent's panel, with the error code the customer gives in the conversation (typed, spoken, in a photo of the display or in a video).",
							warranty: {
								split: "Different items in and out of warranty",
								splitInfo: "The warranty comes from the work order when the triage starts. Each item applies in warranty, out of warranty or both, and the AI asks only for the items of the work order's situation.",
								whenUnknown: "Without the warranty in the work order, use the list",
								noWorkOrders: "Without starting from work orders, every triage uses the {{list}} list.",
								lists: { in: "In warranty", out: "Out of warranty" },
								listsAria: "Item list",
								askIn: "Ask in",
								askInOptions: { both: "In and out of warranty", in: "In warranty only", out: "Out of warranty only" },
								both: "in and out of warranty",
								removeFromList: "Remove from this list",
								viewingIn: "What the AI asks when the work order is in warranty",
								viewingOut: "What the AI asks when the work order is out of warranty",
								allBoth: "For now, every item applies in and out of warranty. To separate them, open an item and choose under “Ask in”.",
								emptyList: "No items in this list. Add what the AI should ask for.",
								turnOffTitle: "Merge the lists?",
								turnOffText: "The two lists become one: the AI will ask for all {{count}} items on every work order.",
								turnOffConfirm: "Merge",
							},
						},
						messages: {
							replyMode: "How the AI writes",
							replyModes: {
								humanized: "In its own words, based on the messages",
								configured: "Exactly the configured messages",
							},
							persona: "Persona (optional)",
							personaPlaceholder: "E.g.: Technical support assistant. Friendly and objective tone, short sentences.",
							supportText: "Support text (optional)",
							supportTextHint: "Information the AI can use to answer questions: deadlines, hours, address.",
							intro: "Opening message (optional)",
							introHint: "Sent when the triage starts.",
							completion: "Completion message",
							completionPlaceholder: "E.g.: Thank you! We have everything. Our team will contact you to schedule.",
							byWarranty: {
								supportText: "Support text for each warranty (optional)",
								completion: "Completion message for each warranty (optional)",
								hint: "Empty: the text above is used.",
							},
						},
						completion: {
							ticketAction: "When the triage ends",
							actions: {
								close: "Close the conversation",
								keep_pending: "Hand over to the team",
							},
							ticketActionInfo: "First, the AI sends the completion message to the customer. If a person completes it in the panel, the conversation stays with them.",
							handoff: "When the AI calls a person",
							handoffInfo: "Happens when the customer asks for an agent, doesn't answer the reminders, runs out of attempts on an item or the AI can't continue. With an agent chosen, the conversation goes to them; without one, it waits in the queue.",
							destination: {
								queue: "Queue",
								queueKeep: "Keep the current queue",
								queueNone: "No queue",
								user: "Agent",
								userNone: "Nobody: stays waiting",
								removed: "The chosen queue or agent changed (it was deleted or left the queue). Choose again or leave it blank.",
							},
							expireTitle: "Customer stopped replying",
							expireBefore: "End the triage after",
							expireUnit: "days (0 = never)",
							expireInfo: "The triage expires and the CRM card goes to the attention column. If the AI was handling the conversation, it is closed without rating and without a message.",
						},
						routes: {
							title: "Work order exceptions",
							info: "Based on the work order data, they send the conversation to another queue or agent instead of the settings above (even with “Close the conversation”). The first one that fits applies, top to bottom.",
							needsWorkOrders: "Only applies when triages start from work orders (Work orders tab).",
							editRule: "Edit rule {{n}}",
							noValue: "no value",
							goesTo: "Goes to {{destination}}",
							noDestination: "Choose where it goes",
							field: "Work order data",
							match: "Comparison",
							fields: {
								model: "Model",
								brand: "Brand",
								category: "Category",
								type: "Work order type",
								warranty: "Warranty",
								city: "City",
								state: "State",
							},
							matches: {
								equals: "is one of",
								startsWith: "starts with",
								contains: "contains",
							},
							values: "Values (one per line)",
							valuesHint: "You can paste a list; case and accents don't matter.",
							placeholders: {
								model: "UN50TU8000GXZD\nQN50Q60TAGXZD",
								brand: "Samsung",
								category: "LED TV",
								type: "Em domicílio",
								warranty: "Fora da garantia",
								city: "Passo Fundo",
								state: "RS",
							},
							destination: "Goes to",
							add: "Add rule",
							and: "And",
							addCondition: "And also",
							removeCondition: "Remove this data",
							joinLast: "and",
							moveUp: "Move up",
							moveDown: "Move down",
							remove: "Remove rule",
						},
						customerExport: {
							title: "Contele",
							info: "On completion (by the AI or a person), creates the work order location in Contele with the order number and the customer's name, document, address and phone. If the work order is triaged again, the same location is updated without erasing what the team wrote.",
							enabled: "Create the work order location on completion",
							account: "Contele account",
							accountChoose: "Choose the account",
							inactiveTag: "turned off",
							inactive: "This account is turned off in Integrations: sending fails until it is turned on again.",
							removed: "The chosen account was removed. Choose another one.",
							removedNone: "The chosen account was removed and there is no other. Sttelar registers it in Integrations.",
							noAccounts: "No Contele account registered. Sttelar registers it in Integrations.",
							needsWorkOrders: "Requires starting from work orders (Work orders tab).",
							items: "Send along",
							itemsInfo: "Text goes in the location Notes. Photos, videos and documents go through a link valid for 60 days: anyone with the link sees the files, without logging in.",
							byLink: "via link",
						},
						followUp: {
							intro: "When the customer stops replying, the AI sends a reminder of what is missing. Time counts from the AI's last request; if the customer replies, the count restarts at the next request.",
							enabled: "Remind the customer who stopped replying",
							stageTitle: "Reminder {{n}}",
							remove: "Remove reminder",
							removeStage: "Remove reminder {{n}}",
							after: "Send when the customer has not replied for",
							unit: "Unit",
							units: {
								minutes: "minutes",
								hours: "hours",
								days: "days",
							},
							message: "Message",
							messagePlaceholder: "E.g.: Hi, {primeiro_nome}! We are still waiting for {item} to continue with your service.",
							messageHint: "The AI writes the reminder in its own words, based on this message. Use {primeiro_nome} and {item} (what the customer still has to send).",
							messageHintConfigured: "Sent exactly as written. Use {primeiro_nome} and {item} (what the customer still has to send).",
							template: "Template (24-hour window closed)",
							templateHint: "On the official API, after 24 hours without the customer writing, only templates can be sent. Without a template, the reminder is skipped in that case.",
							templateNone: "None",
							needsTemplate: "With 24 hours or more without a reply, the official API window has closed: without a template, this reminder is not sent.",
							add: "Add reminder",
							final: "After the last reminder",
							finalWait: "Keep waiting (the triage expires after {{days}} days without a reply, per the Completion tab)",
							finalWaitNever: "Keep waiting (no deadline: the Completion tab is set to 0)",
							finalHandoff: "Hand the conversation over to a person",
							finalAfter: "When the customer has not replied for",
							finalToUserQueue: "Goes to {{user}}, in Attending, in the {{queue}} queue.",
							finalToUser: "Goes to {{user}}, in Attending, with no queue.",
							finalToQueue: "Waits with no owner in the {{queue}} queue.",
							finalToKeep: "Waits with no owner in its current queue.",
							finalFrom: "This is the destination of \"When the AI hands the conversation to a person\", in the Completion tab.",
							finalChange: "Change destination",
							finalRoutesFirst: "Before that, the \"Route by work order data\" rules apply.",
							finalCrmNothingSent: "The CRM card goes to attention. Nothing is sent to the customer.",
							finalNothingSent: "Nothing is sent to the customer.",
							window: "Sending hours",
							windowHint: "Days and hours (Brasília time) when a reminder can be sent. Outside them, the reminder waits for the next window.",
							countNote: "Example: with reminders at 4 hours and 1 day, a customer who does not answer the 10 AM request gets one at 2 PM and another at 10 AM the next day.",
							stopNote: "Reminders stop when the customer replies, when a person takes over or pauses the AI, and when the triage ends.",
							metaNote: "Official API: within the 24-hour window the reminder goes as a regular message; outside it, only with the template chosen in the reminder.",
							defaultMessage1: "Hi, {primeiro_nome}! We are still waiting for {item} to continue with your service.",
							defaultMessage2: "{primeiro_nome}, just a reminder: to continue your service, we need {item}. Just send it here.",
						},
						crm: {
							intro: "The triage card on a CRM board: it is created when the triage starts and moves by itself through the columns up to the completed one, where the scheduling team works.",
							enabled: "Create and move the triage card in the CRM",
							noBoards: "This company has no CRM board yet. Create one on the CRM screen.",
							board: "Board",
							boardNone: "Choose the board",
							columnChoose: "Choose the column",
							columnNone: "None (the card stays where it is)",
							entry: "Entry",
							entryHint: "Where the card is created, when the triage starts.",
							inProgress: "In triage",
							inProgressHint: "While the AI conducts. Without it, the card stays in the entry.",
							attention: "Attention",
							attentionHint: "When the triage calls a person or expires without a reply.",
							completed: "Completed",
							completedHint: "When the triage ends.",
							rules: "Rules per item",
							rulesHint: "Columns for moments of the triage (e.g. \"Waiting for invoice\"). The first matching rule, in list order, applies.",
							ruleItem: "Item",
							ruleItemChoose: "Choose the item",
							ruleWhen: "When",
							ruleColumn: "Column",
							ruleRemove: "Remove rule",
							whenOptions: {
								awaiting: "Waiting for the customer",
								only_pending: "Only required item left",
							},
							addRule: "Add rule",
							manualNote: "A card someone moves to a column outside these stays where the person left it: the triage no longer moves it.",
							cancelNote: "Triage cancelled by a person: the card stays where it is.",
						},
						contact: {
							intro: "Build the contact name with triage data, such as the work order number and the confirmed name, so the team can find the conversation by search.",
							enabled: "Build the contact name with the triage data",
							format: "Contact name",
							formatPlaceholder: "E.g.: {os} {nome}",
							formatHint: "Use the buttons below to add the data. You can type text between them.",
							groups: {
								contact: "From the contact",
								workOrder: "From the work order",
								items: "Confirmed in the conversation",
							},
							tokens: {
								nome: "Customer name",
								primeiro_nome: "First name",
								os: "Work order number",
							},
							workOrderOff: "To use work order data, turn on the start from work orders.",
							workOrderOpen: "Open Work orders",
							itemsNone: "No item has data that can go in the name.",
							itemsExcluded: "Tax IDs, sensitive fields, audio and video don't go in the name.",
							add: "Add \"{{label}}\" to the name",
							preview: "How it looks (example)",
							and: " and ",
							when: {
								title: "When it changes:",
								start: "when the triage starts.",
								items: "when the AI completes {{items}}. If the triage ends earlier, it changes on completion, with whatever is available.",
								end: "on triage completion, because no item confirms the customer name.",
								linkHint: "To change it when the AI confirms the name, link the name item to “Customer name and tax ID”.",
								openItems: "Open Items",
							},
							notes: {
								customer: "In messages, the customer is still called only by their name.",
								once: "The name changes once per triage. If someone edits the name later, the edit stays.",
								missing: "Data that doesn't arrive is left out of the name.",
							},
						},
						workOrders: {
							intro: "Start the triage from the work orders that arrive from the source, without anyone having to open the conversation.",
							enabled: "Start the triage from work orders",
							noSources: "This company has no work order source yet. The super admin adds one in Integrations.",
							source: "Work order source",
							sourceNone: "Choose the source",
							inactive: "inactive",
							startMode: "Start",
							modes: {
								auto: "Automatic: the AI reaches the customer as soon as the work order arrives",
								manual: "Manual: with the button on the Work orders page",
							},
							autoHint: "A work order that arrives (or changes) to one of the statuses below starts the triage by itself, within the sending hours. The button on the Work orders page stays available.",
							manualHint: "Nothing is sent by itself: an admin picks the work order and clicks Start triage.",
							statuses: "Work order statuses",
							statusesHint: "The triage only starts with the work order in one of these statuses.",
							types: "Work order types",
							typesHint: "The work order types this triage handles.",
							window: "Sending hours",
							windowHint: "Days and hours (Brasília time) when the AI may reach the customer. Outside them, the work order waits for the next window.",
							contactName: "The work order number in the contact name is now in the Contact record tab.",
							contactNameOpen: "Open",
							days: "Sending days",
							dayNames: {
								d0: "Sun",
								d1: "Mon",
								d2: "Tue",
								d3: "Wed",
								d4: "Thu",
								d5: "Fri",
								d6: "Sat",
							},
							from: "From",
							to: "To",
							connectionNote: "The conversation opens through the connection chosen in the Outreach tab.",
							noConnection: "Choose the connection in the Outreach tab.",
							metaNeedsTemplate: "On the official API, starting from work orders needs a template in the Outreach tab: a new customer has not opened the 24-hour window yet.",
							autoSinceNote: "Once published, the automatic start applies only to work orders that arrive from then on; the ones already listed start with the button.",
							humanNote: "If the customer is already with an agent, the AI does not take over: the triage waits and the agent is notified.",
						},
						outreach: {
							intro: "How the triage opens the conversation with someone who has not written yet. On the official API, outside the 24-hour window, only with an approved template; on WAHA, with the opening message.",
							connection: "Connection",
							connectionHint: "The WhatsApp connection that opens the conversation.",
							connectionNone: "None (the triage only starts in conversations already open)",
							providers: {
								"meta-api": "Official API",
								waha: "WAHA",
							},
							disconnected: "This connection is disconnected right now.",
							template: "Template",
							templateHint: "Templates approved by Meta on this connection. Create yours in Meta Templates; for this use, the Utility category is recommended.",
							loadingTemplates: "Loading templates…",
							noTemplates: "No approved templates on this connection.",
							templatesError: "Could not load the templates of this connection.",
							chooseTemplate: "Choose a template so the triage can open the conversation outside the 24-hour window.",
							mediaHeader: "Has media in the header, which the triage does not send yet.",
							headerVariable: "Has a variable in the title, which the triage does not fill yet.",
							authTemplate: "It is an authentication template, which the triage does not use.",
							namedVariable: "Uses a named variable, which the triage does not fill yet.",
							codeButton: "Has a code or flow button, which the triage does not send.",
							urlVariable: "Has a link button with a variable, which the triage does not fill yet.",
							removedConnection: "The connection chosen before was removed. Choose another one (or none) and save.",
							missingTemplate: "The chosen template ({{name}}) is no longer approved on this connection.",
							variables: "Template variables",
							variablesHint: "Where the value of each variable in the text comes from.",
							variableLabel: "Value of variable {{n}}",
							sourceNone: "Choose",
							sources: {
								contactFirstName: "Contact first name",
								contactName: "Contact name",
								companyName: "Company name",
								woCustomerFirstName: "Customer first name (work order)",
								woCustomerName: "Customer name (work order)",
								woNumber: "Work order number",
								woProductBrand: "Product brand (work order)",
								woProductModel: "Product model (work order)",
								fixed: "Fixed text",
							},
							fixedPlaceholder: "Text in place of the variable",
							fixedLabel: "Fixed text of variable {{n}}",
							preview: "Preview (with sample data)",
							metaInfo: "After the template, the AI waits for the customer to reply: Meta only allows free text after the reply. End the template with a question or a button.",
							wahaInfo: "WAHA has no templates: the conversation opens with the opening message (Messages tab), and the AI goes straight to the first request.",
							wahaWarning: "Warning: sending the first message to someone who never talked to this number, over an unofficial channel, increases the risk of WhatsApp banning the number.",
						},
					},
					sessions: {
						status: "Status",
						statuses: {
							active: "In progress",
							COMPLETED: "Completed",
							CANCELLED: "Cancelled",
							FAILED: "Failed",
							all: "All",
						},
						allDefinitions: "All triages",
						columns: {
							customer: "Customer",
							definition: "Triage",
							status: "Status",
							started: "Started",
							ended: "Ended",
						},
						open: "Open conversation",
						noContact: "Contact deleted",
						empty: "No triage found.",
						loadMore: "Load more",
						total: "{{count}} in total",
					},
				},
			},
			smartApproaches: {
				title: "Smart follow-ups",
				subtitle: "Messages sent automatically when the customer doesn't reply to an official API template. If the customer replies, the next messages are cancelled.",
				newFlow: "New flow",
				noConnection: "To use it, connect an official API number in Connections.",
				onlyAdmins: "Only administrators can set up smart follow-ups.",
				loadError: "Couldn't load the flows.",
				conflict: "The initial template already starts the active flow \"{{name}}\" on this connection. Deactivate it before activating this one.",
				filtersLabel: "Filter flows",
				filters: {
					all: "All",
					active: "Active",
					inactive: "Inactive",
				},
				empty: {
					title: "No flows yet",
					text: "Create a flow to choose a template and the messages sent automatically when the customer doesn't reply.",
					filtered: "No flows in this filter.",
				},
				active: "Active",
				inactive: "Inactive",
				messagesOne: "1 message",
				messagesMany: "{{count}} messages",
				openRunsOne: "1 follow-up in progress",
				openRunsMany: "{{count}} follow-ups in progress",
				activate: "Activate",
				deactivate: "Deactivate",
				edit: "Edit",
				delete: "Delete",
				activated: "Flow activated.",
				deactivated: "Flow deactivated.",
				deleted: "Flow deleted.",
				confirmDeactivateTitle: "Deactivate the flow?",
				confirmDeactivateText: "The follow-ups in progress ({{count}}) stop now: their automatic messages won't be sent. The conversations stay open.",
				confirmDeleteTitle: "Delete the flow?",
				confirmDeleteText: "The flow leaves the list and the template becomes a regular one again. Follow-ups in progress stop. The numbers stay on the Dashboard.",
				issues: {
					unchecked: "Couldn't check the templates on Meta right now.",
					line: "{{step}}: the template \"{{name}}\" is {{status}} on Meta.",
					initial: "Initial template",
					step: "Message {{n}}",
					status: {
						PAUSED: "paused",
						DISABLED: "disabled",
						REJECTED: "rejected",
						PENDING: "in review",
						IN_APPEAL: "in appeal",
						MISSING: "deleted",
						other: "unavailable",
					},
				},
				sequence: {
					now: "now",
					after: "+{{hours}} h",
				},
				days: {
					d0: "Sun",
					d1: "Mon",
					d2: "Tue",
					d3: "Wed",
					d4: "Thu",
					d5: "Fri",
					d6: "Sat",
					range: "{{from}} to {{to}}",
				},
				ordinals: {
					n1: "1st",
					n2: "2nd",
					n3: "3rd",
				},
				duration: {
					minutes: "{{minutes}} min",
					hours: "{{hours}} h",
					hoursMinutes: "{{hours}} h {{minutes}} min",
				},
				when: {
					soon: "shortly",
					in: "in {{duration}}",
					at: "on {{date}} at {{time}}",
					late: "late",
				},
				bar: {
					title: "Smart follow-up",
					active: "Smart follow-up active",
					awaiting: "waiting for a reply",
					sent: "{{step}} message sent",
					next: "{{step}} message {{when}}",
					allSent: "all messages sent, waiting for a reply",
					interrupted: "Smart follow-up stopped",
					completed: "Smart follow-up finished",
					cancelledTitle: "Smart follow-up cancelled",
					replied: "the customer replied (after the {{step}} message)",
					noReply: "no reply",
					void: "the 1st message was not delivered",
					hint: "Sending a message in this conversation cancels the next automatic messages.",
					details: "Details",
					cancel: "Cancel smart flow",
					dismiss: "Hide",
					cancelled: {
						manual: "by an agent",
						manualBy: "by {{name}}",
						agent_message: "a message was sent in the conversation",
						agent_messageBy: "{{name}} sent a message",
						ticket_closed: "ticket closed",
						flow_disabled: "flow deactivated",
						opt_out: "the customer asked not to receive messages",
						expired: "the {{step}} message was too late and was not sent",
						other: "the automatic messages stopped",
					},
					failed: {
						template_unavailable: "the {{step}} message template is not approved by Meta",
						invalid_variables: "a variable of the {{step}} message has no value",
						no_phone: "the contact has no phone number",
						send_failed: "Meta rejected the {{step}} message",
						send_uncertain: "the {{step}} message could not be confirmed",
						delivery_failed: "the {{step}} message was not delivered",
						start_interrupted: "the 1st message could not be confirmed",
						other: "error sending the {{step}} message",
					},
				},
				details: {
					title: "Smart follow-up",
					close: "Close",
					step: "{{step}} message",
					planned: "Planned: {{hours}} h after the previous one, if the customer doesn't reply",
					scheduled: "Scheduled: goes out {{when}}",
					reply: "The customer replied",
					startedBy: "Started by {{name}} · {{date}}",
					startedAt: "Started · {{date}}",
					status: {
						sent: "Sent",
						delivered: "Delivered",
						read: "Read",
						failed: "Failed",
						sending: "Sending",
						cancelled: "Not sent",
						uncertain: "Not confirmed",
						expired: "Not sent: too late",
					},
				},
				cancel: {
					title: "Cancel smart flow?",
					text: "The next automatic messages of this conversation won't be sent. The ticket stays open.",
					confirm: "Cancel flow",
					done: "Smart flow cancelled.",
				},
				messageMark: "Automatic message · Smart follow-up ({{step}} of {{total}})",
				messageMarkTitle: "Sent by the system because the customer didn't reply",
				modal: {
					filter: "Smart",
					tooltip: "Smart follow-up – automatic follow-up messages",
					sectionTitle: "Smart follow-up",
					first: "Now: this template",
					afterFirst: "After {{hours}} h without a reply",
					afterNext: "After {{hours}} more h without a reply",
					stop: "If the customer replies, the next messages are cancelled.",
					window: "Automatic messages go out: {{window}}.",
					onlyFirst: "1-message flow: nothing goes out automatically; the customer's reply counts on the Dashboard.",
					notice: {
						blocked: "This conversation already has a smart follow-up in progress ({{name}}). To send another smart template, cancel the current one.",
						cancels: "This conversation has a smart follow-up in progress ({{name}}). Sending this template cancels the next automatic messages.",
						unknown: "This conversation has a smart follow-up in progress ({{name}}). A regular template cancels the next automatic messages; a smart template only goes out after you cancel the current one.",
					},
					cancelRun: "Cancel current follow-up",
					started: "Smart follow-up started: the {{step}} message goes out {{when}} if the customer doesn't reply.",
					startedSingle: "Template sent. Smart follow-up started.",
					notStarted: "Template sent, but the smart follow-up didn't start: the next messages won't go out automatically.",
				},
				dashboard: {
					title: "Smart follow-ups",
					help: "Follow-ups started in the period: how many customers replied and after which message.",
					flowFilter: "Filter by flow",
					templateFilter: "Filter by initial template",
					allFlows: "All flows",
					allTemplates: "All templates",
					deleted: "{{name}} (deleted)",
					started: "Started",
					replied: "Replied",
					noReply: "No reply",
					inProgress: "Awaiting reply",
					rate: "Reply rate",
					rateHelp: "Of the follow-ups that already finished, how many got a reply. Those awaiting a reply count once the flow's deadline ends.",
					chartTitle: "Replies by message",
					chartHelp: "After which message the customer replied.",
					stepLabel: "{{step}} message",
					repliedAfter: "replied after the {{step}} message",
					tableLabel: "Show the table by",
					byFlow: "By flow",
					byTemplate: "By template",
					columns: {
						flow: "Flow",
						template: "Initial template",
						started: "Started",
						replied: "Replied",
						noReply: "No reply",
						rate: "Rate",
					},
					empty: "No smart follow-up started in this period.",
					loadError: "Couldn't load the numbers.",
					refreshError: "Couldn't refresh the numbers: the previous ones are dimmed.",
					retry: "Try again",
				},
				editor: {
					titleNew: "New flow",
					titleEdit: "Edit flow",
					close: "Close",
					name: "Flow name",
					namePlaceholder: "E.g.: Customer recovery – Technical support",
					connection: "Official API connection",
					chooseConnection: "Choose the connection",
					connectionFirst: "Choose the connection to see the approved templates.",
					count: "Number of messages",
					countHint: "Counting the first one, sent by the agent.",
					initial: "Initial template",
					initialHint: "The agent sends this template from the chat; the flow starts when Meta accepts the message.",
					chooseTemplate: "Choose the template",
					loadingTemplates: "Loading the approved templates...",
					templatesError: "Couldn't load this connection's templates.",
					missingTemplate: "The template \"{{name}}\" is no longer approved on this connection. Choose another one.",
					stepTitle: "Message {{n}}",
					stepSubFirst: "Sent if the customer doesn't reply to the 1st message.",
					stepSubNext: "Sent if the customer still doesn't reply.",
					hours: "Hours without a reply",
					hoursHint: "Counted from the previous message ({{min}} to {{max}}).",
					template: "Template",
					templateHint: "Only templates without an image, video or document header, without variables in the header or link, and that aren't authentication templates can be sent automatically.",
					sources: {
						contactFirstName: "Contact's first name",
						contactName: "Contact's name",
						companyName: "Company name",
						companyExample: "Support",
						initial: "Value from the 1st message: {{var}}",
						initialExample: "[{{var}} from the 1st message]",
						fixed: "Fixed text",
					},
					window: "Automatic sending hours",
					windowHint: "Brasília time. A message due outside these hours goes out at the next opening.",
					attribution: "Attribution window (hours)",
					attributionHint: "How many hours after the last message a customer reply still counts for the flow on the Dashboard ({{min}} to {{max}}).",
					activeLabel: "Flow active",
					preview: "Sequence preview",
					previewFirst: "1st · now, sent by the agent",
					previewAfterFirst: "2nd · after {{hours}} h without a reply",
					previewAfterNext: "3rd · after {{hours}} more h without a reply",
					previewNoTemplate: "Choose the template to see the message.",
					previewStop: "If the customer replies at any time, the next messages are cancelled.",
					costNote: "Each automatic message is a template charged by Meta.",
					editNote: "Changes apply to the next follow-ups. The ones in progress keep going as they started; to stop them, deactivate the flow.",
					cancel: "Cancel",
					save: "Save",
					saving: "Saving...",
					created: "Flow created.",
					saved: "Flow saved.",
					discardTitle: "Discard the changes?",
					discardText: "The changes made to this flow weren't saved.",
					discard: "Discard",
					templateProblems: {
						authTemplate: "is an authentication template",
						namedVariable: "uses a named variable",
						mediaHeader: "has an image, video or document header",
						headerVariable: "has a variable in the header",
						codeButton: "has a code or flow button",
						urlVariable: "has a variable in the button link",
					},
					problems: {
						name: "Give the flow a name.",
						connection: "Choose the connection.",
						initial: "Choose the initial template.",
						hours: "Message {{n}}: hours go from {{min}} to {{max}}.",
						template: "Message {{n}}: choose the template.",
						variable: "Message {{n}}: choose the value of variable {{variable}}.",
						fixed: "Message {{n}}: write the fixed text of variable {{variable}}.",
						days: "Select at least one day for the automatic messages.",
						window: "The sending hours must start before they end.",
						attribution: "The attribution window goes from {{min}} to {{max}} hours.",
					},
				},
			},
			backendErrors: {
				ERR_AI_UNAVAILABLE: "AI is not available for this account yet. Try again in a few minutes.",
				ERR_AI_KEY_REQUIRED: "To use AI in this company, add your OpenAI key in Integrations or talk to Sttelar.",
				ERR_AI_TEXT_TOO_LONG: "The text is too long for the AI. Shorten it and try again.",
				ERR_AI_SETUP_INVALID_URL: "Check the website address, like yourcompany.com.",
				ERR_AI_SETUP_DESCRIPTION_SHORT: "Tell us a bit more about the business (at least 40 characters).",
				ERR_AI_SETUP_TOO_MANY: "Too many attempts this hour. Try again later.",
				ERR_AI_SETUP_EMPTY: "We couldn't find information on the website. You can describe the business instead.",
				ERR_AI_SETUP_BUSY: "The AI is busy right now. Try again in a moment.",
				ERR_AI_SETUP_FAILED: "Couldn't build the knowledge base right now. Try again.",
				ERR_AI_SETUP_NOT_FOUND: "This preparation expired. Start over.",
				ERR_AI_SETUP_INVALID_NAME: "The AI name must have 2 to 60 characters.",
				ERR_AI_SETUP_KNOWLEDGE_SHORT: "The knowledge base is too short. Write a little more.",
				ERR_AI_SETUP_KNOWLEDGE_LONG: "The knowledge base is over the size limit. Shorten it a bit.",
				ERR_AI_SETUP_AGENT_NOT_FOUND: "We couldn't find this agent. Start over.",
				ERR_AI_SETUP_INVALID_SOURCE: "Choose the website or the description.",
				ERR_AI_SETUP_RUNNING: "A knowledge base is already being prepared for this account. Wait for it to finish.",
				ERR_AI_SETUP_OTHER_FLOW: "Another active flow already answers every connection in this account. Turn it off in Flows before turning on the AI.",
				ERR_AI_SETUP_VERSION_NOT_FOUND: "We couldn't find this version of the AI. Save again and test it before turning it on.",
				ERR_TRIAGE_DISABLED: "AI triage is not enabled for this company.",
				ERR_TRIAGE_ALREADY_ACTIVE: "This conversation already has a triage in progress.",
				ERR_TRIAGE_SESSION_NOT_FOUND: "Triage not found.",
				ERR_TRIAGE_DEFINITION_NOT_FOUND: "Triage not found.",
				ERR_TRIAGE_REQUIREMENT_NOT_FOUND: "Triage item not found.",
				ERR_TRIAGE_VERSION_NOT_FOUND: "Triage version not found.",
				ERR_INVALID_ACTION: "Invalid action.",
				ERR_INVALID_ID: "Invalid identifier.",
				ERR_SMART_APPROACH_CONNECTION: "Choose an official API connection from this company.",
				ERR_SMART_APPROACH_TEMPLATES_UNAVAILABLE: "Couldn't read the templates from Meta right now. Try again in a moment.",
				ERR_SMART_APPROACH_NOT_FOUND: "Flow not found.",
				ERR_SMART_APPROACH_ACTIVE: "This conversation already has a smart follow-up in progress. Cancel it before sending another smart template.",
				ERR_SMART_APPROACH_RUN_NOT_FOUND: "Follow-up not found.",
				ERR_SMART_APPROACH_NOT_ACTIVE: "This follow-up has no pending messages anymore.",
				ERR_SMART_APPROACH_TEMPLATE_IN_USE: "The initial template already starts another active flow on this connection. Deactivate the other one first.",
				ERR_INVALID_PARAMS: "Invalid parameters.",
				ERR_INVALID_SETTING_VALUE: "Invalid value for this setting.",
				ERR_WORK_ORDER_NOT_FOUND: "Work order not found.",
				ERR_TICKETNOTE_TOO_LONG: "The note can have at most 255 characters.",
				ERR_TICKETNOTE_READ_ONLY: "The triage summary note is written by the system: it cannot be edited or deleted.",
				ERR_TICKETNOTE_CONTACT_REQUIRED: "Choose the contact for the note.",
				ERR_SUPER_ONLY: "Only super admins can use this tool.",
				ERR_TICKET_CLOSE_DISABLED: "In this company, only admins can resolve conversations.",
				ERR_INVALID_TICKET_STATUS: "Invalid conversation status.",
				ERR_INVALID_FILTERS: "Invalid filters. Check the statuses and the period.",
				ERR_NOTHING_TO_DELETE: "Nothing left to delete with these filters — the data changed since the preview.",
				ERR_NOTHING_TO_RESOLVE: "Nothing left to resolve with these filters — the conversations changed since the preview.",
				ERR_INVALID_PASSWORD: "Wrong password.",
				ERR_INVALID_CONFIRMATION_PHRASE: "The confirmation phrase doesn't match.",
				ERR_TOO_MANY_ATTEMPTS: "Too many attempts. Wait 15 minutes and try again.",
				ERR_TOO_MANY_AUTH_ATTEMPTS: "Too many attempts. Wait a few minutes and try again.",
				ERR_ACCOUNT_SETUP_PENDING: "Your sign-up is ready, but your company setup hasn't been released yet.",
				ERR_AUTH_LINK_INVALID: "This link is no longer valid. Ask for a new one.",
				ERR_PASSWORD_TOO_SHORT: "The password must have at least 8 characters.",
				ERR_PASSWORD_TOO_LONG: "The password is too long. Use up to 72 characters.",
				ERR_SIGNUP_DISABLED: "New account sign-up is closed right now.",
				ERR_SIGNUP_INVALID_NAME: "Enter your full name.",
				ERR_INVALID_EMAIL: "Enter a valid email.",
				ERR_EMAIL_DOMAIN_NOT_FOUND: "This email domain does not exist. Check the spelling.",
				ERR_EMAIL_DISPOSABLE: "Use an email you check regularly, not a temporary one.",
				ERR_TERMS_NOT_ACCEPTED: "To create the account, accept the Terms of Use and the Privacy Policy.",
				ERR_META_NUMBER_IN_USE: "This number is already linked to another company. Contact Sttelar.",
				ERR_AI_DAILY_LIMIT: "You reached the AI usage limit for today on this feature. It frees up again tomorrow.",
				ERR_EMAIL_ALREADY_EXISTS: "Someone already uses this email.",
				ERR_EMAIL_NOT_VERIFIED: "Confirm your email to do this. You can resend the link from the banner at the top of the screen.",
				ERR_MESSAGE_NOT_FOUND: "Message not found.",
				ERR_BOARD_NOT_FOUND: "Board not found.",
				ERR_SHARE_NOT_FOUND: "Link not found.",
				ERR_SHARE_EXPIRED: "This link has expired. Create a new one.",
				ERR_SHARE_REVOKED: "This link was revoked. Create a new one.",
				ERR_SHARE_NAME_REQUIRED: "Give the link a name.",
				ERR_SHARE_NAME_TOO_LONG: "The link name can have up to 80 characters.",
				ERR_SHARE_INVALID_EXPIRATION: "Invalid validity date.",
				ERR_SHARE_EXPIRATION_TOO_SOON: "The validity must be at least 5 minutes from now.",
				ERR_SHARE_EXPIRATION_TOO_LATE: "The validity can be up to 1 year. For longer, use a permanent link.",
				ERR_SHARE_PASSWORD_REQUIRED: "Type the password.",
				ERR_SHARE_PASSWORD_TOO_SHORT: "The password must have at least 8 characters.",
				ERR_SHARE_PASSWORD_TOO_LONG: "The password is too long.",
				ERR_SHARE_INVALID_OPTION: "Invalid option.",
				ERR_SHARE_LIMIT_REACHED: "This board already has 50 active links. Revoke one before creating another.",
				ERR_SHARE_LINK_UNAVAILABLE: "This link can't be shown again. Generate a new link.",
				ERR_SHARE_URL_UNAVAILABLE: "The platform address isn't configured. Contact support.",
				ERR_LEAD_NOT_FOUND: "Card not found.",
				ERR_LEAD_STATUS_NOT_FOUND: "Column not found.",
				ERR_STATUS_HAS_LEADS: "This column still has cards. Move them to another column before deleting it.",
				ERR_ENTRY_COLUMN_CANNOT_BE_COMPLETED: "The entry column for new cards cannot be a completion column.",
				ERR_MFA_INVALID_OTP: "Invalid verification code.",
				ERR_ADMIN_OPERATION_IN_PROGRESS: "There is already an operation running on this connection.",
				ERR_ADMIN_OPERATION_NOT_FOUND: "Operation not found.",
				ERR_ADMIN_OPERATION_NOT_ACTIVE: "This operation has already finished.",
				ERR_ADMIN_OPERATION_NOT_RESUMABLE: "Only a failed operation can be resumed.",
				ERR_ADMIN_OPERATION_PREVIEW_EXPIRED: "The preview is too old (over 30 minutes).",
				ERR_QUEUE_UNAVAILABLE: "The processing queue is unavailable. Try again in a moment.",
        ERR_NO_ATTENDANCE_ACCESS: "Your profile does not have access to the Service module.",
				ERR_ACADEMY_DISABLED: "The Training area is disabled for this company.",
				ERR_ACADEMY_INVALID_VIDEO_URL: "Invalid video URL. Use a YouTube or Vimeo link.",
				ERR_ACADEMY_COURSE_NOT_FOUND: "Course not found.",
				ERR_ACADEMY_MODULE_NOT_FOUND: "Module not found.",
				ERR_ACADEMY_LESSON_NOT_FOUND: "Lesson not found.",
				ERR_INTERNAL_SERVER_ERROR: "An unexpected error occurred. Please try again later",
				ERR_NO_OTHER_WHATSAPP: "There must be at least one default WhatsApp.",
				ERR_NO_DEF_WAPP_FOUND: "No default WhatsApp found. Check the connections page.",
				ERR_WAPP_NOT_INITIALIZED: "This WhatsApp session hasn't been initialized. Check the connections page.",
				ERR_WAPP_CHECK_CONTACT: "Couldn't verify WhatsApp contact. Check the connections page",
				ERR_WAPP_INVALID_CONTACT: "This is not a valid WhatsApp number.",
				ERR_WAPP_DOWNLOAD_MEDIA: "Couldn't download WhatsApp media. Check the connections page.",
				ERR_INVALID_CREDENTIALS: "Invalid username or password",
				ERR_COMPANY_DISABLED: "This company is deactivated. Contact Sttelar support for details.",
				ERR_CANNOT_DISABLE_SYSTEM_COMPANY: "The system company cannot be deactivated.",
				ERR_USER_DONT_EXISTS: "User not found. Check the provided email.",
				ERR_SENDING_WAPP_MSG: "Error sending WhatsApp message. Check the connections page.",
				ERR_DELETE_WAPP_MSG: "Couldn't delete WhatsApp message.",
				ERR_EDIT_WAPP_MSG: "Couldn't edit WhatsApp message.",
				ERR_EDIT_ONLY_OWN_MESSAGES: "You can only edit your own messages.",
				ERR_EDIT_ONLY_WAHA_PROVIDER: "Message editing is only available for WAHA connections.",
				ERR_EDIT_TIME_LIMIT_EXCEEDED: "Edit time expired (10 minute limit).",
				ERR_CANNOT_EDIT_DELETED_MESSAGE: "Cannot edit a deleted message.",
				ERR_CANNOT_EDIT_MEDIA_MESSAGE: "Cannot edit messages with media.",
				ERR_EMPTY_MESSAGE_BODY: "Message cannot be empty.",
				ERR_OTHER_OPEN_TICKET: "There's already an open ticket for this contact.",
				ERR_SESSION_EXPIRED: "Session expired. Please log in.",
				ERR_USER_CREATION_DISABLED: "User creation has been disabled by the administrator.",
				ERR_WORKFLOW_NOT_FOUND: "This flow no longer exists.",
				ERR_AI_AGENT_NOT_FOUND: "This agent no longer exists.",
				ERR_AI_AGENT_IN_USE: "This agent works in a flow. Change the agent in those flows before deleting it.",
				ERR_AI_AGENT_INVALID_AI_USER: "Choose an AI user from this company as the attendant.",
				ERR_AI_AGENT_INVALID_REASONING: "Choose a reasoning level from the list.",
				ERR_AI_AGENT_INVALID_STYLE: "Choose a reply style from the list.",
				ERR_WORKFLOW_NODE_NOT_AI_AGENT: "This block is not an AI block. Open the flow again and retry.",
				ERR_WORKFLOW_NODE_HAS_AGENT: "This block already uses an agent.",
				ERR_AI_AGENT_CONVERT_INCOMPLETE: "Some case or file in this block does not fit whole into the agent. Review the cases and files and try again.",
				ERR_WORKFLOW_AGENT_REQUIRED: "To turn the flow on, choose the agent of the \"Agente IA\" block or, in \"Personalizado\", write the text.",
				ERR_WORKFLOW_AGENT_NOT_FOUND: "The agent chosen in the \"Agente IA\" block no longer exists. Choose another one.",
				ERR_SETUP_ALREADY_DONE: "Your company has already been created. Log in again to continue.",
				ERR_SETUP_INVALID_COMPANY_NAME: "Use a company name with 2 to 80 characters.",
				ERR_SETUP_INVALID_ACCOUNT_NAME: "Use an account name with 2 to 80 characters.",
				ERR_SETUP_COMPANY_REQUIRED: "First tell us the company name.",
				ERR_SETUP_NOT_FOUND: "There is no setup in progress for this account.",
				ERR_SETUP_INVALID_STEP: "That setup step does not exist.",
				ERR_INVITE_NO_COMPANY: "Your account is not in a Company. Contact support.",
				ERR_INVITE_INVALID_ACCOUNTS: "Choose an account from your Company for the invite.",
				ERR_INVITE_ALREADY_MEMBER: "This person is already part of the Company.",
				ERR_INVITE_ALREADY_PENDING: "There is already an open invite for this email. Resend the one in the list.",
				ERR_INVITE_NOT_FOUND: "This invite is no longer in the list. Refresh the page.",
				ERR_INVITE_RESEND_TOO_SOON: "The invite was just sent. Wait a minute to resend it.",
				ERR_INVITE_INVALID: "This invite is no longer valid. Ask the person who invited you for a new one.",
				ERR_INVITE_OTHER_EMAIL: "This invite is for another email. Sign in with the invite's email.",
				ERR_INVITE_PROFILE_MISMATCH: "You are already in this Company with another profile. Ask the person who invited you to check your access.",
				ERR_INVITE_LOGIN_REQUIRED: "This email already has an account. Sign in with it to accept the invite.",
				ERR_INVALID_PROFILE: "Choose a valid profile.",
				ERR_INVALID_ACCESS_SCOPE: "Choose a valid license.",
				ERR_BILLING_PERSON_TYPE: "Choose individual or company.",
				ERR_BILLING_NAME: "Enter the full name or the legal name.",
				ERR_BILLING_CPF: "Invalid CPF.",
				ERR_BILLING_CNPJ: "Invalid CNPJ.",
				ERR_BILLING_EMAIL: "Invalid billing email.",
				ERR_BILLING_EMAIL_IN_USE: "This billing email is already used by another company. Use a different one.",
				ERR_BILLING_PHONE: "Invalid phone: use the area code and the number.",
				ERR_BILLING_ZIP: "Invalid ZIP code.",
				ERR_BILLING_ADDRESS: "Fill in the address, number, neighborhood and city.",
				ERR_BILLING_STATE: "Choose the state.",
				ERR_BILLING_NO_COMPANY: "This account is not in a Company.",
				ERR_BILLING_REDUCTION_LATER: "To reduce the plan or the licenses, use Reduce at renewal.",
				ERR_BILLING_NOTHING_TO_CHANGE: "Choose what to increase.",
				ERR_BILLING_NO_SUBSCRIPTION: "The Company has no subscription to change.",
				ERR_BILLING_NO_SAVED_CARD: "The subscription has no saved card: pay with Pix or boleto.",
				ERR_BILLING_STARTER_CHANGE_LATER: "To move from Starter with Internal Chat to Basic or Pro, contact support.",
				ERR_BILLING_TOO_MANY_ATTEMPTS: "Too many subscription attempts today. Try again tomorrow or contact support.",
				ERR_BILLING_DISABLED: "Billing is unavailable right now. Try again later.",
				ERR_BILLING_INVALID_PAYMENT_METHOD: "Choose a valid payment method.",
				ERR_BILLING_CARD_TOKEN: "Check the card details.",
				ERR_BILLING_NOT_ON_PLANS: "This Company is billed by Sttelar support.",
				ERR_BILLING_COURTESY: "This Company is on courtesy and is not charged.",
				ERR_BILLING_ALREADY_SUBSCRIBED: "The Company already has a plan. To change it, contact support.",
				ERR_BILLING_NOTHING_TO_CHARGE: "Choose what to subscribe to.",
				ERR_BILLING_BOLETO_MINIMUM: "Boleto only from R$ 50.00. Choose another payment method.",
				ERR_BILLING_REDUCTION_BOLETO_MINIMUM: "By boleto, the monthly fee cannot go below R$ 50.00: keeping only Internal Chat requires a card. Contact Sttelar support, or cancel the plan.",
				ERR_BILLING_PROFILE_REQUIRED: "Fill in the billing details before subscribing.",
				ERR_BILLING_GATEWAY: "Pagar.me did not respond. Try again in a moment.",
				ERR_BILLING_REQUEST_OPEN: "There is already a request awaiting payment.",
				ERR_BILLING_RESUME_PENDING: "We're checking the request to undo the cancellation. Try again in a few minutes.",
				ERR_BILLING_CARD_REJECTED: "The card was not accepted. Check the details or use another card.",
				ERR_BILLING_REQUEST_NOT_FOUND: "Request not found.",
				ERR_BILLING_ALREADY_PAID: "This request has already been paid and the plan has changed.",
				ERR_BILLING_RENEWING: "The plan renewal is coming up. Try again after it.",
				ERR_BILLING_BOLETO_TOO_LATE: "Less than 10 days until the renewal: pay by card or Pix.",
				ERR_BILLING_QUOTE_CHANGED: "The amount changed since it was calculated. Check it and pay again.",
				ERR_BILLING_PAYMENT_PENDING: "There is a pending monthly fee. Settle the payment before changing or canceling the plan.",
				ERR_BILLING_REQUEST_CREATING: "We are still checking this request. Wait a few minutes before giving up.",
				ERR_BILLING_CONTRACT_MISMATCH: "The company's contract was adjusted by support. To change the plan, contact Sttelar support.",
				ERR_BILLING_REDUCTION_SCHEDULED: "There is a reduction scheduled for the renewal. Undo it to change the plan now.",
				ERR_BILLING_INCREASE_IS_CHANGE: "To increase, use Change plan.",
				ERR_BILLING_REDUCTION_NOT_CHEAPER: "This reduction does not lower the monthly fee (the plan price changed). Contact Sttelar support.",
				ERR_BILLING_UNKNOWN_ITEM: "The subscription was adjusted in Pagar.me outside Sttelar Talk. Contact Sttelar support.",
				ERR_BILLING_DUPLICATE_ITEM: "The subscription has a repeated item in Pagar.me. Contact Sttelar support.",
				ERR_BILLING_REQUEST_CLOSED: "This request is already closed.",
				ERR_BILLING_INVALID_PLAN: "Choose Basic or Pro.",
				ERR_BILLING_INVALID_SEATS: "Invalid number of licenses.",
				ERR_BILLING_INVALID_INTERNAL_CHAT: "Invalid number of Internal Chat licenses.",
				ERR_BILLING_INVALID_AI_PACK: "Choose one of the AI packs.",
				ERR_BILLING_AI_PACK_NOT_ALLOWED: "The AI pack is for Basic and Pro.",
				ERR_COMPANY_REQUIRED: "Choose the account.",
				ERR_NO_COMPANY_ACCESS: "You no longer have access to this account. Reload the page.",
				ERR_USER_NOT_FOUND: "User not found.",
				ERR_WORKFLOW_EXECUTION_NOT_FOUND: "This run no longer exists.",
				ERR_WORKFLOW_DESCRIPTION_TOO_LONG: "The description is longer than 500 characters.",
				ERR_TRACK_LINK_HOSTS_REQUIRED: "Enter at least one site to track links.",
				ERR_INVALID_TRACK_LINK_HOSTS: "One of the sites is not valid. Use the format mystore.com.",
				ERR_TOO_MANY_TRACK_LINK_HOSTS: "A flow can track at most 20 sites.",
				ERR_INVALID_CONVERSION_SOURCE: "This store is not available for the flow.",
				ERR_CONVERSION_SOURCE_EXISTS: "This store already has a webhook URL.",
				ERR_CONVERSION_SOURCE_NOT_FOUND: "Store not found.",
				ERR_INTEGRATION_NOT_FOUND: "Integration not found.",
				ERR_INTEGRATION_REQUIRED: "Choose the store.",
				ERR_INVALID_LANGUAGE: "Invalid language: choose Portuguese, English or Spanish.",
				ERR_NO_PERMISSION: "You don't have permission to access this resource.",
				ERR_USER_IN_OTHER_COMPANIES: "This person also belongs to another company that you don't manage.",
				ERR_ACCESS_UNAVAILABLE: "We couldn't check your access right now. Please try again in a moment.",
				ERR_ACCESS_OWN: "No one can change their own access. Ask another Administrator.",
				ERR_ACCESS_IMPERSONATING: "You can't change access while using \"Access as\" a user.",
				ERR_ACCESS_TARGET_ABOVE: "This person has more access than you, so you can't change it.",
				ERR_ACCESS_GRANT_ABOVE: "You can only give the permissions you have yourself.",
				ERR_ACCESS_NO_HOME: "The person needs to keep the conversations or the internal chat.",
				ERR_ACCESS_LAST_ADMIN: "The company needs at least one Administrator. Set another one first.",
				ERR_NO_EMPRESA: "This account is not part of a company with other accounts.",
				ERR_INVALID_DEPARTMENT: "Fill in the name and choose at least one account.",
				ERR_DEPARTMENT_NOT_FOUND: "Department not found.",
				ERR_DEPARTMENT_NAME_TAKEN: "There is already a department with this name.",
				ERR_COMPANY_NAME_ALREADY_EXISTS: "There is already an account with this name.",
				ERR_COMPANY_GROUP_INVALID_NAME: "The company name must have 2 to 255 characters.",
				ERR_COMPANY_GROUP_NOT_FOUND: "Company not found.",
				ERR_COMPANY_GROUP_NAME_TAKEN: "There is already another company with this name.",
				ERR_COMPANY_GROUP_STANDALONE: "Account without a named company: its name is the account name. Edit the account.",
				ERR_PLAN_LIMIT_USERS: "The company's plan has reached its user limit.",
				ERR_PLAN_LIMIT_INTERNAL_CHAT: "The company's plan has reached its limit of users with Internal Chat.",
				ERR_PLAN_LIMIT_ACCOUNTS: "The company's plan has reached its account limit.",
				ERR_PLAN_LIMIT_WHATSAPPS: "The company's plan has reached its WhatsApp number limit.",
				ERR_PLAN_LIMIT_CRM_BOARDS: "The company's plan has reached its CRM board limit.",
				ERR_MESSAGE_SEND_IN_PROGRESS: "This message is already being sent.",
				ERR_MESSAGE_QUOTA_COMMON: "This month's messages have run out. Sending resumes when the cycle renews or the plan grows; talk to the administrator.",
				ERR_MESSAGE_QUOTA_AI: "This month's AI messages have run out. The AI resumes when the cycle renews or the pack grows.",
				ERR_COMMERCIAL_REASON_REQUIRED: "Write the reason (at least 3 letters).",
				ERR_COMMERCIAL_INVALID_PLAN: "Invalid plan.",
				ERR_COMMERCIAL_INVALID_MODE: "Choose how the limits work.",
				ERR_COMMERCIAL_MODE_WITHOUT_PLAN: "Choose a plan to turn the limits on.",
				ERR_COMMERCIAL_INVALID_SEATS: "Contracted licenses below the plan minimum.",
				ERR_COMMERCIAL_INVALID_INTERNAL_CHAT: "Invalid number of Internal Chat licenses.",
				ERR_COMMERCIAL_INVALID_AI_PACK: "Invalid AI pack.",
				ERR_COMMERCIAL_AI_PACK_NOT_ALLOWED: "This plan has no AI pack. To give more AI, grant an exception.",
				ERR_COMMERCIAL_INVALID_DATES: "Invalid dates: the end must be after the start and after today.",
				ERR_COMMERCIAL_INVALID_ITEM: "Choose what the exception changes.",
				ERR_COMMERCIAL_INVALID_VALUE: "Invalid value.",
				ERR_COMMERCIAL_INVALID_USER: "Choose a person from this company.",
				ERR_COMMERCIAL_KIND_NOT_AVAILABLE: "This kind of exception is not available yet.",
				ERR_COMMERCIAL_EXCEPTION_NOT_FOUND: "Exception not found.",
				ERR_COMMERCIAL_EXCEPTION_REVOKED: "This exception has already been revoked.",
				ERR_DEPARTMENT_ACCOUNT_TAKEN: "One of the accounts is already in another department.",
				ERR_DEPARTMENT_INVALID_ACCOUNT: "Choose only accounts from this company.",
				ERR_DEPARTMENT_INVALID_VIEWER: "Choose only people from this account.",
				ERR_DEPARTMENT_WITHOUT_ACCOUNTS: "This department has no active accounts.",
				ERR_ACCESS_CHANGED: "Someone else just changed this access. Reload and try again.",
				ERR_ACCESS_INVALID: "The access data is not valid. Reload the page and try again.",
				ERR_ACCESS_ADMIN_ONLY: "This permission is only for the Administrator role.",
				ERR_CANNOT_REMOVE_ACTIVE_COMPANY: "Cannot remove the user's active Account. Set another Account as primary first.",
				ERR_COMPANY_NOT_ASSIGNED: "This Account is not linked to the user.",
				ERR_COMPANY_ALREADY_ASSIGNED: "This Account is already linked to the user.",
				ERR_COMPANY_NOT_IN_GROUP: "The Account must belong to the same Company as the user.",
				ERR_DUPLICATED_CONTACT: "A contact with this number already exists.",
				ERR_CONTACT_NOT_SHAREABLE: "This contact has no phone number that can be sent.",
				ERR_CONTACT_SEND_UNSUPPORTED: "This connection doesn't send contact cards.",
				ERR_SEND_CONTACT: "Couldn't send the contact. Try again.",
				ERR_NO_SETTING_FOUND: "No settings found with this ID.",
				ERR_NO_CONTACT_FOUND: "No contact found with this ID.",
				ERR_NO_TICKET_FOUND: "No ticket found with this ID.",
				ERR_MAX_PINNED_TICKETS: "Maximum of 3 pinned conversations reached.",
				ERR_NO_USER_FOUND: "No user found with this ID.",
				ERR_NO_WAPP_FOUND: "No WhatsApp found with this ID.",
				ERR_TICKET_NO_CONNECTION: "This ticket has no WhatsApp connection defined. Select a connection before resolving.",
				ERR_CREATING_MESSAGE: "Error creating message in database.",
				ERR_CREATING_TICKET: "Error creating ticket in database.",
				ERR_FETCH_WAPP_MSG: "Error fetching WhatsApp message, it might be too old.",
				ERR_QUEUE_COLOR_ALREADY_EXISTS: "This color is already in use, choose another.",
				ERR_WAPP_GREETING_REQUIRED: "Greeting message is required when there is more than one queue.",
				ERR_BILLING_CANCELLATION_SCHEDULED: "A cancellation is scheduled. Undo it to change the plan.",
				ERR_BILLING_SUBSCRIPTION_NOT_STARTED: "The resumed subscription has not started yet. Once it starts, the plan can be changed.",
				ERR_BILLING_CARD_UNAVAILABLE: "The saved card is not available. Contact Sttelar support to stay on the plan.",
				ERR_BILLING_RESUME_CANCELED: "The new subscription was canceled at Pagar.me. Contact Sttelar support to stay on the plan.",
				ERR_SENDING_SUSPENDED: "Sending messages is suspended due to a pending payment. Payment is made in Billing by whoever manages the company.",
				ERR_BILLING_PAYMENT_PROCESSING: "The card payment is being reviewed. Wait a few minutes and refresh.",
				ERR_BILLING_OVERDUE_CARD_REFUSED: "The card was declined again. Pay with Pix.",
				ERR_BILLING_DISPUTE_OUTCOME: "Choose the outcome: won or lost.",
				ERR_BILLING_DISPUTE_NOT_FOUND: "Dispute not found in this company.",
				ERR_BILLING_DISPUTE_RESOLVED: "This dispute already has an outcome.",
				ERR_INTERNAL_CHAT_SUSPENDED: "The Internal Chat is suspended due to a pending payment. Talk to your company administrator.",
				ERR_BILLING_OVERDUE_IN_PROGRESS: "The payment for the overdue invoice is already being prepared. Wait a moment.",
				ERR_BILLING_NOT_OVERDUE: "There's no overdue invoice to pay.",
				ERR_BILLING_OVERDUE_BOLETO_SETTLING: "The boleto may still be confirming (up to 3 business days). If you haven't paid it, use its payment line.",
				ERR_BILLING_OVERDUE_CANCEL_FAILED: "The Pix couldn't be prepared right now. Try again in a few minutes.",
				ERR_BILLING_NOTHING_TO_RETRY: "There's no declined card invoice to try again.",
				ERR_BILLING_NOT_SUSPENDED: "The company isn't suspended.",
				ERR_BILLING_REFUND_REASON: "Tell us why you want the refund (at least 10 characters).",
				ERR_BILLING_REFUND_CHARGE: "Charge not found in this company.",
				ERR_BILLING_REFUND_NOT_PAID: "Only a paid charge, not disputed on the card, can be refunded.",
				ERR_BILLING_REFUND_AMOUNT: "The amount is more than what can still be refunded from this charge.",
				ERR_BILLING_REFUND_OPEN: "There's already a refund request in progress for this charge.",
				ERR_BILLING_REFUND_DECISION: "Choose: refuse, approve as a correction or as an annulment.",
				ERR_BILLING_REFUND_NOT_FOUND: "Refund request not found in this company.",
				ERR_BILLING_REFUND_DECIDED: "This refund request has already been decided.",
				ERR_BILLING_REFUND_FAILED: "Pagar.me didn't process the refund now. The request stays approved: try again.",
				ERR_BILLING_REFUND_NOT_APPROVED: "This refund isn't approved and waiting to be paid back.",
				ERR_COMMERCIAL_INVALID_KIND: "Invalid exception type.",
				ERR_BILLING_REFUND_MISMATCH: "The amount refunded at Pagar.me doesn't match the request. Check it there; then mark it as done or undo the approval.",
				ERR_BILLING_REFUND_ALREADY_SENT: "Pagar.me has already refunded (or is refunding) this charge: use \"Try again\" to check.",
				ERR_ADEQUACY_INVALID: "The choice was incomplete. Open it again and retry.",
				ERR_ADEQUACY_UNKNOWN_ITEM: "Something changed since the screen opened. Open the choice again.",
				ERR_ADEQUACY_SELF: "You cannot remove yourself.",
				ERR_ADEQUACY_NO_ACCOUNT: "At least one account must stay.",
				ERR_ADEQUACY_USER_WITHOUT_ACCOUNT: "Whoever stays needs access to an account that stays.",
				ERR_ADEQUACY_NO_ADMIN: "At least one administrator must stay.",
				ERR_ADEQUACY_INTERNAL_ONLY: "Someone who only uses Internal Chat cannot stay without it: remove the person or keep Internal Chat.",
				ERR_ADEQUACY_SHARED_PERSON: "This person is also in another company: their Internal Chat does not change here.",
				ERR_ADEQUACY_OVER_LIMIT: "The choice is still over the plan limits.",
				ERR_ADEQUACY_CHANGED: "Things changed since the choice opened. Open it again to see what applies now.",
				ERR_HISTORY_INVALID_TOPIC: "Invalid history topic.",
				ERR_HISTORY_INVALID_PAGE: "Invalid history page.",
				ERR_ADEQUACY_NOT_NEEDED: "There is nothing to choose now.",
				ERR_ARCHIVE_NOT_FOUND: "This item is no longer archived.",
				ERR_ARCHIVE_ACCOUNT_INACTIVE: "This item's account is archived: restore the account first.",
				ERR_ARCHIVE_USER_WITHOUT_ACCOUNT: "This person has no access to an account in use: restore their account first.",
				ERR_PLAN_ADEQUACY_REQUIRED: "Restricted access: the administrator must choose, in Billing, what stays on the plan.",
				ERR_WHATSAPP_ARCHIVED: "This number was archived when fitting the plan and does not send messages.",
				ERR_ACCOUNT_ARCHIVED: "This account was archived when fitting the plan. Contact your company administrator.",
				ERR_MEMBER_ARCHIVED: "Your access to this company was archived when fitting the plan. Contact the administrator.",
			},
			internalChat: {
				jobRoleRequired: {
					title: "Select your role",
					intro: "To use Internal Chat, choose the role that best represents your function in the company. It will appear next to your name in conversations.",
					empty: "No roles registered. Ask the administrator to register roles in Settings → Roles.",
					confirm: "Confirm",
					saving: "Saving...",
					toastSaved: "Role defined",
				},
			},
		}
	},
};

export { messages };
